/**
 * @gooner/shared — the single source of truth shared by the server, control panel,
 * and display view. Every wire contract (data model, REST DTOs, WebSocket protocol,
 * source-adapter shape) lives here. If it crosses a process boundary, type it here.
 *
 * Core mental model: each monitor/region runs AUTONOMOUSLY. There is no
 * monitor-to-monitor synchronization. The server only (a) persists "caves" (saved
 * configurations) and (b) relays live config/commands from the control panel to the
 * display devices that joined a monitor's link.
 *
 * Media DOES flow through the server, and only through the server: every playback source
 * resolves to a clip we have already downloaded, transcoded and pinned, served from
 * `/api/media`. Nothing a viewer sees is fetched from RedGifs or Reddit — those are
 * admin-only import sources (see IMPORT_ONLY_KINDS in routes/source.ts).
 */

export type ID = string;

/** A rectangle in normalized monitor coordinates. x,y = top-left; all in [0,1]. */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

// ---------------------------------------------------------------------------
// Sources (pluggable). Add a new feed source by extending SourceConfig with a
// new `kind` and implementing a matching SourceAdapter on the server.
// ---------------------------------------------------------------------------

export type SourceKind =
  | "redgifs"
  | "playlist"
  | "reddit"
  | "clip"
  | "clips"
  | "keywords"
  | "random"
  | "roulette"
  | "colorwave"
  | "build"
  | "goontok"
  | "upload";
// redgifs/reddit are backend-only building blocks now; the cave editor exposes
// the higher-level kinds (clip/playlist/keywords/random) and the server decides
// which raw sources to combine. "upload" tags an owner-private user upload; it is
// NOT a fetchable SourceConfig kind (no adapter) — it only labels a clip's origin.

export const REDGIFS_ORDERS = ["trending", "latest", "top", "best"] as const;
export type RedgifsOrder = (typeof REDGIFS_ORDERS)[number];

export const REDDIT_SORTS = ["hot", "top", "new", "rising"] as const;
export type RedditSort = (typeof REDDIT_SORTS)[number];

/** Time window for `sort: "top"`. */
export const REDDIT_TOP_WINDOWS = ["day", "week", "month", "year", "all"] as const;
export type RedditTopWindow = (typeof REDDIT_TOP_WINDOWS)[number];

/** How multiple selected tags combine. */
export type TagMatch = "any" | "all";

export interface RedgifsSourceConfig {
  kind: "redgifs";
  /** Selected RedGifs tags. Empty => the default trending/top feed. */
  tags: string[];
  /** "any" = mix gifs from any selected tag (default); "all" = only gifs with every tag. */
  match?: TagMatch;
  order?: RedgifsOrder;
  /** Only return clips that have audio. */
  audioOnly?: boolean;
}

/** Play a saved playlist (a user's curated, kept clips) as a region's source. */
export interface PlaylistSourceConfig {
  kind: "playlist";
  /** Which playlist to resolve to clips (shareable by id). */
  playlistId: ID;
}

/**
 * Pull media from one or more subreddits, engagement-filtered to cut spam. The
 * admin curates the available subreddit list + default thresholds (SystemSettings);
 * a region may narrow to a subset. Empty `subreddits` => the admin default list.
 */
export interface RedditSourceConfig {
  kind: "reddit";
  subreddits: string[];
  sort?: RedditSort;
  /** Only used when sort is "top". */
  topWindow?: RedditTopWindow;
  /** Engagement floor. Undefined => fall back to the admin defaults. */
  minScore?: number;
  minComments?: number;
}

/** Pin a single clip to a region and keep relaying it (loops forever). */
export interface ClipSourceConfig {
  kind: "clip";
  /** The locked clip. null = not chosen yet (region shows nothing until picked). */
  clip: MediaItem | null;
}

/**
 * A fixed handful of clips — `clip` but plural: the region cycles through them. The
 * Community Wall's sold squares use this, holding a buyer's 1–5 picks, and the items are
 * re-resolved from the ledger's keys at every materialization — which is why a clip
 * banned AFTER purchase simply drops out of the rotation with no wall-specific code.
 * Deliberately NOT in SOURCE_KINDS: a buyer's placement is not an editor affordance.
 */
export interface ClipsSourceConfig {
  kind: "clips";
  clips: MediaItem[];
}

/**
 * A keyword selection: which clips from OUR catalog a region plays. (It used to fan
 * out across RedGifs and Reddit too; it doesn't — nothing on the user end comes from
 * off our servers.)
 *
 * `groups` is the current shape: OR inside each group, AND across them, so
 * `[["race:latina","race:asian"], ["tits:large"]]` means "(Latina or Asian) and
 * Large". That's what the editor's facet groups produce and what a flat list can't
 * express — `["race:latina","race:asian","tits:large"]` reads the same whether you
 * meant that or "any of these three".
 *
 * `keywords` + `match` is the older shape, still honoured so saved regions keep
 * playing what they always played. Both map onto groups without a special case — see
 * keywordGroups.
 */
export interface KeywordsSourceConfig {
  kind: "keywords";
  /** Groups of keywords: OR within a group, AND across groups. */
  groups?: string[][];
  /** Legacy flat list. Combined per `match`. Ignored when `groups` is set. */
  keywords?: string[];
  /** Legacy: "any" = union (default); "all" = clips carrying every keyword. */
  match?: TagMatch;
}

/**
 * The OR-groups a keyword source resolves to, whichever shape it was saved in.
 *
 * The legacy modes are both expressible, which is why no migration was needed:
 * "any" is one group holding everything (OR), and "all" is one keyword per group
 * (AND). Empty groups and blank keywords are dropped, so a half-edited config can't
 * produce a clause that matches nothing.
 */
export function keywordGroups(cfg: KeywordsSourceConfig): string[][] {
  const clean = (list: readonly string[]) => list.map((k) => k.trim()).filter(Boolean);
  if (cfg.groups?.length) {
    return cfg.groups.map(clean).filter((g) => g.length > 0);
  }
  const flat = clean(cfg.keywords ?? []);
  if (flat.length === 0) return [];
  return cfg.match === "all" ? flat.map((k) => [k]) : [flat];
}

/**
 * A page from ONE keyword the server picks at random per fetch — hidden keywords (the
 * `sub:*` provenance tags) included, which is why the pick is the server's and the tag
 * never rides the response: a public read may not name a hidden tag. Infinity's Keyword
 * Scroll deals each wall from one of these. Not an editor affordance (see SOURCE_KINDS):
 * a region cannot choose it, only a stage can.
 */
export interface RouletteSourceConfig {
  kind: "roulette";
}

/**
 * A page from ONE COLOURED GARMENT the server picks at random per fetch — black bikini,
 * pink panties — and only clips whose focus keeps that garment in frame (COLORWAVE_FOCUS).
 * The pick is drawn from the themes the catalog is deep enough in (colorwaveThemes), and
 * — like roulette — is the server's and never rides the response. Infinity's Colorwave
 * deals each wall from one of these. Not an editor affordance (see SOURCE_KINDS): a
 * region cannot choose it, only a stage can.
 */
export interface ColorwaveSourceConfig {
  kind: "colorwave";
}

/**
 * GoonTok's feed (2026-10-29): the whole library, drawn part by what people did with each
 * clip — views, edges, faps (goontokWeight) — and part at plain random (GOONTOK_RANDOM_SHARE),
 * so the scroll is the crowd's favourites without being only them. A stage's, never a
 * region's, like roulette.
 */
export interface GoontokSourceConfig {
  kind: "goontok";
}

/**
 * Build a Slut (see BUILD_STAGES). One config, two moods: a ROUND — `stage` names it and
 * the server deals four candidates that differ on that stage's trait, naming each in
 * `SourceFetchResponse.picks` — and the SCROLL after the last round, dealt from clips that
 * carry the most of the `profile` the picks built. `profile` is the picks' tags so far
 * (`race:white`, `hair:blonde`, `tits:large`...), client-held for the sitting and re-sent
 * each page because the server keeps nothing. `maxMatch` is the scroll's ceiling on the
 * match tier — the client lowers it when the tier above is spent. `preview` is a free
 * viewer's ONE wall of her (see BUILD_PREVIEW_CLIPS): the route serves a non-Gold asker
 * the scroll only with it set. Not an editor affordance: a stage's, like roulette.
 */
export type BuildStageId = "face" | "tits" | "body" | "hair" | "style";
export interface BuildSourceConfig {
  kind: "build";
  stage: BuildStageId | "scroll";
  profile: string[];
  maxMatch?: number;
  preview?: true;
  /** A round dealt as four COLUMNS (a wide desk window) wants portrait clips — a column
   *  shows a portrait clip whole and crops a landscape one to a sliver. */
  portrait?: true;
}

/** One of a round's four candidates: which served clip (by clipKey), the trait tags a pick
 *  of it contributes to the profile, and the words the tile wears ("White · Blonde"). */
export interface BuildPick {
  clip: string;
  tags: string[];
  label: string;
}

/** Random clips drawn from the whole cached catalog. */
export interface RandomSourceConfig {
  kind: "random";
}

/** Discriminated union of every supported source. Grows over time. */
export type SourceConfig =
  | RedgifsSourceConfig
  | PlaylistSourceConfig
  | RedditSourceConfig
  | ClipSourceConfig
  | ClipsSourceConfig
  | KeywordsSourceConfig
  | RandomSourceConfig
  | RouletteSourceConfig
  | GoontokSourceConfig
  | ColorwaveSourceConfig
  | BuildSourceConfig;

/**
 * The source kinds the cave editor offers per region. RedGifs/Reddit are omitted
 * on purpose — they're backend building blocks that `keywords`/`random` combine.
 */
export const SOURCE_KINDS: ReadonlyArray<{ kind: SourceKind; label: string }> = [
  { kind: "random", label: "Random (catalog)" },
  { kind: "playlist", label: "Collection / Playlist" },
  { kind: "keywords", label: "Keywords" },
  { kind: "clip", label: "Locked clip" },
];

/** Build a fresh Reddit source config. */
export function defaultRedditSource(): RedditSourceConfig {
  return { kind: "reddit", subreddits: [], sort: "hot" };
}

/** Build a fresh keyword source config. */
export function defaultKeywordsSource(): KeywordsSourceConfig {
  return { kind: "keywords", keywords: [], match: "any" };
}

/** A normalized media item. `url`/`posterUrl` are always directly playable in a browser
 *  (already proxied/signed by the server as needed). */
export interface MediaItem {
  id: string;
  kind: "video" | "image";
  url: string;
  posterUrl?: string;
  /** Short (~3s) muted preview clip for thumbnail/hover contexts; falls back to `url` when absent. */
  previewUrl?: string;
  width?: number;
  height?: number;
  durationSec?: number;
  /**
   * What the SOURCE claimed about this clip's audio (RedGifs' `gif.hasAudio`, Reddit's
   * `reddit_video.has_audio`). An ingest-time input, NOT a fact about the file we serve:
   * on prod the claim disagrees with an actual probe for 31.6% of measured clips, in both
   * directions. Read `audible` for anything that decides whether to make noise.
   */
  hasAudio?: boolean;
  /**
   * Whether the file we serve actually carries an audio stream — MEASURED with ffprobe by
   * the producer, never the source's word for it.
   *
   * THREE STATES, and the third one matters: `true` a probe found a track, `false` a probe
   * found none, `undefined` nobody has probed yet. Absence is NOT silence — collapsing the
   * two is precisely what makes `hasAudio` useless — so a consumer picking a panel to make
   * noise wants `audible !== false` (never choose a known-silent clip, still try an
   * unmeasured one), and one that must be certain wants `=== true`.
   */
  audible?: boolean;
  sourceKind: SourceKind;
  /**
   * Source-provided flair/label for the post, when the adapter surfaces one (Reddit's
   * `link_flair_text`). Read at ingest time to drive per-flair tag/skip rules; never
   * persisted. `null` when the post has no flair; `undefined` for sources without flair.
   */
  flair?: string | null;
  /**
   * Tags the SOURCE already put on this item (RedGifs returns a list per clip). Read at
   * ingest time to drive per-tag rules, exactly as `flair` is for Reddit, and never
   * persisted — the catalog's own tags are written separately and deliberately.
   *
   * Not to be confused with a clip's catalog tags. These are the upstream's vocabulary,
   * in the upstream's casing, and are only ever an input to a rule.
   */
  sourceTags?: string[];

  /**
   * Adaptive Framing's shot list — where to point a cropped tile, over time.
   *
   * Absent for any clip the focal sweep has not reached, which is the same thing as
   * "frame it dead centre like we always did". See `solveFocalShots` for how it is
   * derived and `focalCrop` for how a tile spends it.
   */
  focal?: FocalShot[];
}

// ---------------------------------------------------------------------------
// Tag facets — the namespaced axes a clip can be filtered on. A facet is a
// naming convention over the slug (`race:latina`), not a table: the vocabulary
// lives here so the browse sidebar can render a facet's full range even where a
// value has no clips, and so writes can be validated against it.
//
// Closed on purpose. Adding `race:indian` is a code change and a restart — the
// closed set is what keeps a typo like `race:latino` out of the catalog. Display
// names stay admin-editable per keyword, so a value can be RELABELLED without
// touching code (see tagDisplayName).
//
// `sex` and `race` describe EVERY performer (2026-09-13). They used to label the woman
// only — "sex has no male; white man / black man stay flat keywords" — and an interracial
// clip was race:white + sex:female and nothing about him. Now he is sex:male + race:black
// on the same clip, so on a clip with two or more people these two facets hold several
// values. On a solo clip they still hold one: see PER_ACTOR_FACETS.
// ---------------------------------------------------------------------------

/**
 * The producer's MEASURED verdict on a clip's audio, written as a keyword so the catalog
 * can be filtered on it (several MONITOR_TEMPLATES require `audio:audible` on the panel
 * they designate for sound). Exactly one of the pair is ever on a clip.
 *
 * The same verdict is denormalized onto `clips.audio_verified` for playback, which needs
 * a per-item answer without a tag join. `Db.addAutoTag` maintains both from this one pair,
 * so they cannot drift — which is why these are constants and not literals at the call
 * site. Distinct from `MediaItem.hasAudio`, which is only what the source claimed.
 */
export const TAG_AUDIO_AUDIBLE = "audio:audible";
export const TAG_AUDIO_SILENT = "audio:silent";

export const TAG_FACETS = {
  // Who is in it, by name (2026-09-13). An OPEN group with no seed list: every value is a
  // performer the creator-subreddit feed rules tag (`pornstar:autumn-falls`, one rule per
  // r/<performer>), listed by clip count. First in FACET_ORDER — it is the first thing a
  // viewer asks. Multi-valued: a collab clip names two.
  pornstar: [],
  // `lesbian` (2026-09-12) is the first second value — what the design doc said would
  // make this facet worth having. Its only writer is the WD tagger's rules (yuri).
  orientation: ["straight", "lesbian"],
  // What the stored master can actually SERVE, tiered by its short side — the way a
  // video platform's quality menu works. 1440p and 240p were both removed 2026-08-11:
  // see RESOLUTION_TAG_HEIGHTS for why neither can legitimately exist.
  quality: ["1080p", "720p", "480p", "360p"],
  // `arab` (added 2026-08-27) is NOT in the vision race head's closed vocabulary — the
  // head only ever emits the four before it. It exists as a facet value so the RedGifs
  // Arab niche feeds can tag their pulls `race:arab` and land on the Race facet; its
  // only source is those feed rules, not inference — the same standing as focus:feet.
  race: ["white", "latina", "asian", "black", "arab"],
  // `male` (2026-09-13) is the man in the clip — what `white man` / `black man` used to say
  // as flat keywords. The /actor head never emits it (it answers for the woman on solo
  // clips); its writers are feed rules and the WD tagger's rules.
  sex: ["female", "trans", "male"],
  // The vision head's own closed vocabulary (vision/cup/predict_cup.py), which is
  // what makes it a facet rather than a loose namespace: the model can only emit
  // these four, so the sidebar can offer exactly these four.
  tits: ["small", "medium", "large", "huge"],
  // #people in the clip, from the /actor head (vision/actor/people.joblib) — its own
  // closed 1..4 vocabulary (4 means "4+"). sex/race are only tagged when actor_count:1.
  actor_count: ["1", "2", "3", "4"],
  // How much is covered, as four ORDERED levels (vision/actor/nudity.joblib). Ordered, so
  // a UI can range over it; the head only emits these four.
  nudity: ["fully-clothed", "minimal-clothing", "partially-nude", "fully-nude"],
  // Coarse posture of the single actor (vision/actor/body_position.joblib). `multiposition`
  // is deliberately absent: it is a property of the clip's TIMELINE, and a head reading a
  // mean-pooled embedding cannot see it, so it is labelled by hand and never predicted.
  body_position: ["standing", "sitting", "kneeling", "lying", "squatting", "bent-over",
                  "on-all-fours"],
  // What the FRAMING is about (vision/actor/focus.joblib, scene-level — emitted whatever
  // the actor count is). Declared 2026-08-25 when the "random catalog sweep" batch fixed
  // the rare classes: 2,719 training labels, 81% test acc, 0.786 macro-F1, no class
  // below 0.69 (the first attempt had three at 0.00). `multifocus` is deliberately
  // absent: it is labelOnly in labelshop and excluded from training — a mean-pooled
  // embedding cannot see a property of the TIMELINE, same reasoning as body_position's
  // missing `multiposition`.
  // `feet` (added 2026-08-27) is NOT one of the vision head's 7 trained classes — the
  // head never emits it. It exists as a facet value so the RedGifs `feet` niche rule can
  // tag its pulls `focus:feet` and have them render as a real Focus chip; its only source
  // is that rule, not inference.
  focus: ["wholebody", "ass", "sex", "tits", "face", "vagina", "cock", "feet"],
  aspect: ["portrait", "landscape", "square"],
  // How long the STORED clip runs, bucketed. The vocabulary is spelled out here rather
  // than derived from CLIP_LENGTH_BUCKETS so facetTag() keeps its literal types; a
  // compile-time assertion beside that table keeps the two from drifting.
  clip_length: ["micro", "very_short", "short", "medium", "long", "very_long"],
  // Whether the STORED master carries an audio stream — MEASURED (ffprobe), never the
  // source's has_audio claim, which lies both ways (silent-at-source among declared-audio,
  // remuxed sound among the rest). Tagged at ingest by the producer and backfilled by
  // tools/backfill-audio-tags.mjs; a clip is exactly one of these, never both.
  audio: ["audible", "silent"],

  // ---- The keyword GROUPS (2026-09-12) — the follow-up the facet design doc deferred.
  //
  // Everything above is single-valued: a clip has one race, one nudity level, one length.
  // Everything below except `hair` is MULTI-valued — a clip can be attire:fishnets AND
  // attire:lingerie, act:blowjob AND act:facial — and the code that assumes one value per
  // facet (setFacetTag, the fill-only gates, GOONCHA's sibling negatives) keys off
  // SINGLE_VALUE_FACETS below rather than off "is a facet". Values are hyphenated, as
  // slugValue() in labelshop-import spells a multi-word answer, so a labelshop axis with
  // the same key lands on the facet already populated.
  //
  // Writers: the WD tagger's rule table (vision/wd/rules.json) for all of them; feed rules
  // in producer.config.json for the ones that used to be flat keywords (blowjob, thick,
  // cosplay, ...) — LEGACY_FLAT_KEYWORDS is that rename, and the producer refuses the old
  // spelling at load. `pos:` (the held-out position head's namespace) is folded in here
  // too: positions to `position`, the acts it also named to `act`.

  // The performer's hair. Six values, exactly labelshop's `hair` axis (labelshop/src/schema.ts)
  // as slugValue spells them — `dyed/colored` -> `dyed-colored`. Per-actor, single-valued.
  hair: ["blonde", "brunette", "black", "redhead", "dyed-colored", "grey-silver"],
  // What is being done. Multi-valued. `hardcore` is the feed vocabulary's word for
  // penetrative sex and keeps its name; `tit-jiggle` / `titty-drop` are the two gestures
  // the boobbounce/tittydrop subs are about (were pos:tit_jiggle / pos:titty_drop).
  act: ["blowjob", "deepthroat", "handjob", "titfuck", "anal", "facial", "cumshot",
        "creampie", "squirting", "orgasm", "cunnilingus", "masturbation", "kissing",
        "spanking", "footjob", "twerking", "striptease", "dancing", "threesome",
        "group-sex", "tit-jiggle", "titty-drop", "hardcore"],
  // The sex position — distinct from body_position (posture of a single actor). The
  // held-out pos head's vocabulary, hyphenated, less `amazon` (retired 2026-09-16 by the
  // owner: one admin-tagged clip, no source feeds it). Multi-valued: a clip can change position.
  position: ["doggy", "cowgirl", "reverse-cowgirl", "missionary", "prone-bone",
             "facesitting"],
  // What she is wearing. Multi-valued.
  // `panties` and `bra` (2026-09-13) are the parents the WD families roll up to — a
  // `black panties` keyword is also attire:panties. Multi-valued like the rest.
  attire: ["fishnets", "lingerie", "thong", "bikini", "swimsuit", "pantyhose", "stockings",
           "high-heels", "glasses", "latex", "leggings", "maid", "nurse", "cosplay", "collar",
           "panties", "bra"],
  // Where it happens. Multi-valued in principle (a bathroom mirror selfie is two).
  setting: ["bedroom", "bathroom", "shower", "kitchen", "couch", "car", "pool", "beach",
            "outdoors", "gym", "office"],
  // Where the camera is. Named after labelshop's `camera_angle` axis (its `camera` axis is
  // MOVEMENT: static/handheld/multicam) and shares its `pov`/`mirror`/`low`/`high`; `selfie`
  // is ours. `multiangle` is deliberately absent — a timeline property, labelOnly there.
  camera_angle: ["pov", "low", "high", "mirror", "selfie"],
  toy: ["dildo", "vibrator", "plug"],
  // Body type and body mods. Multi-valued (thick AND tattoos). `milf`, `goth`, `alt` stay
  // flat: age and style, not body.
  body: ["thick", "curvy", "skinny", "petite", "fit", "tattoos", "piercings", "hairy"],
  // ---- 2026-09-13, the OPEN groups' own axes. `hair` above is COLOUR, single-valued per
  // person (labelshop corrects it). Length and style are a different question and can
  // coexist with a colour, so they get their own multi-valued group — and eyes and nails
  // theirs. The lists below are the seeds; the WD families mint the rest (see OPEN_FACETS).
  hairstyle: ["long", "short", "wavy", "curly", "straight", "ponytail", "braid", "twintails"],
  eyes: ["brown", "blue", "green", "black", "grey"],
  nails: ["painted"],
  // ---- 2026-09-13, what was left in "Other": the aesthetic, the kind of clip, and the
  // pairing slang. Feed rules write these (they were flat keywords until today); all open, multi.
  aesthetic: ["goth", "alt", "cute"],
  genre: ["hypno", "joi", "sph", "babecock", "goon-caption", "hentai", "animated", "freeuse",
          "hotwife", "breeding", "implied", "couple", "at-work", "rough", "milf", "tiktok", "amateur"],
  // The pairing slang means more than the sex/race facets say (LEGACY_IMPLIED_KEYWORDS
  // still adds those beside it), so it keeps its names — as a group.
  pairing: ["snowbunny", "torta", "interracial"],
  // The cock: the slang the feeds use (bbc, bwc, girlcock — implying his sex/race, or hers)
  // and what the model can see of it. Open, multi-valued.
  cock: ["bbc", "bwc", "girlcock", "big", "small", "veiny", "uncut"],
} as const satisfies Record<string, readonly string[]>;

/**
 * The facets whose value vocabulary is OPEN (2026-09-13): the list in TAG_FACETS is the
 * seed the sidebar always shows, and any other well-formed value a writer produces is a
 * value too — `attire:black-panties`, `act:shirt-lift`, `hairstyle:wavy`. The WD tagger's
 * families mint hundreds of these from the model's colour × garment compounds, and a
 * closed list would either hide them in "Other" (where they were until today) or put
 * three hundred empty chips in every picker. Surfaces list the seed values first, then
 * the observed ones by count. All of these are multi-valued; the single-valued facets
 * (race, nudity, ...) stay closed, because a closed list is what makes a correction a
 * replacement.
 */
export const OPEN_FACETS = [
  "pornstar", "act", "position", "attire", "setting", "camera_angle", "toy", "body", "hairstyle", "eyes", "nails",
  "aesthetic", "genre", "pairing", "cock",
] as const satisfies readonly (keyof typeof TAG_FACETS)[];
export function isOpenFacet(facet: string): boolean {
  return (OPEN_FACETS as readonly string[]).includes(facet);
}
/** The form an open facet's value must take: a hyphenated lowercase slug, at most 40 chars. */
export const OPEN_FACET_VALUE_RE = /^[a-z0-9][a-z0-9-]{0,39}$/;

/** Facets whose values carry an ORDER of their own (levels, buckets, counts) and are shown
 *  in that order. Every other group lists its values by how many clips carry each. */
export const ORDERED_FACETS = ["nudity", "clip_length", "quality", "actor_count", "tits"] as const;

/**
 * The values a group SHOWS, in the order it shows them: for an ordered facet the declared
 * order; for everything else by clip count, highest first, with the declared (seed) values
 * present even at zero and — for an open facet — every observed value after them.
 * `counts` is the facet's row of CatalogFacetCountsResponse (absent while loading).
 */
export function facetValuesToShow(facet: TagFacet, counts: Record<string, number> | undefined): string[] {
  const declared = TAG_FACETS[facet] as readonly string[];
  if ((ORDERED_FACETS as readonly string[]).includes(facet) || !counts) return [...declared];
  const observed = isOpenFacet(facet) ? Object.keys(counts).filter((v) => !declared.includes(v) && (counts[v] ?? 0) > 0) : [];
  return [...declared, ...observed].sort((a, b) => (counts[b] ?? 0) - (counts[a] ?? 0) || declared.indexOf(a) - declared.indexOf(b));
}

/** The facets a clip holds ONE value of. Everything not listed here is multi-valued: a
 *  writer may add a second value beside the first, and nothing should "fill only when
 *  empty" or treat a sibling value as a negative. Keyed off explicitly rather than
 *  derived from position in TAG_FACETS so a reader can see the rule in one place. */
export const SINGLE_VALUE_FACETS = [
  "orientation", "quality", "tits", "actor_count", "nudity", "body_position",
  "focus", "aspect", "clip_length", "audio", "hair",
] as const satisfies readonly (keyof typeof TAG_FACETS)[];
export function isSingleValueFacet(facet: string): boolean {
  return (SINGLE_VALUE_FACETS as readonly string[]).includes(facet);
}

/** The facets that describe a PERSON, one value per person: on a solo clip (actor_count:1,
 *  or no count yet) a clip holds one of each and a correction replaces; on a clip with
 *  two or more people they accumulate — her race and his. `setFacetTag` reads the count
 *  to decide which. Distinct from SINGLE_VALUE_FACETS, which is unconditional. */
export const PER_ACTOR_FACETS = ["sex", "race"] as const satisfies readonly (keyof typeof TAG_FACETS)[];
export function isPerActorFacet(facet: string): boolean {
  return (PER_ACTOR_FACETS as readonly string[]).includes(facet);
}

/**
 * Flat keywords and `pos:` slugs that became facet values on 2026-09-12, and what each
 * became. One table, three readers: the one-shot catalog migration (server/src/migrations/
 * tag-groups.ts) renames every row; the producer refuses a rule that still emits the old
 * spelling (producer/src/config.ts) so the next pull cannot write it back; and the WD
 * tagger's rules check refuses it as a target. Old spelling on the left exactly as it
 * was stored — `pos:` used underscores, the facets use hyphens.
 */
export const LEGACY_FLAT_KEYWORDS: Readonly<Record<string, string>> = {
  // A hand-typed `trans` (three admin rows, 2026-09-13) is the sex facet's value, which the
  // first facet migration already spelled out — the admin editor now maps it on the way in.
  trans: "sex:trans",
  // acts
  blowjob: "act:blowjob", deepthroat: "act:deepthroat", anal: "act:anal", facial: "act:facial",
  cumshot: "act:cumshot", creampie: "act:creampie", squirt: "act:squirting", orgasm: "act:orgasm",
  twerking: "act:twerking", hardcore: "act:hardcore",
  "pos:blowjob": "act:blowjob", "pos:handjob": "act:handjob", "pos:titfuck": "act:titfuck",
  "pos:female_masturbation": "act:masturbation", "pos:striptease": "act:striptease",
  "pos:dancing": "act:dancing", "pos:twerking": "act:twerking",
  "pos:tit_jiggle": "act:tit-jiggle", "pos:titty_drop": "act:titty-drop",
  // positions
  doggy: "position:doggy", riding: "position:cowgirl",
  "pos:doggy": "position:doggy", "pos:cowgirl": "position:cowgirl",
  "pos:reverse_cowgirl": "position:reverse-cowgirl", "pos:missionary": "position:missionary",
  "pos:prone_bone": "position:prone-bone", "pos:facesitting": "position:facesitting",
  // attire
  cosplay: "attire:cosplay", glasses: "attire:glasses",
  // body
  thick: "body:thick", curvy: "body:curvy", skinny: "body:skinny", petite: "body:petite",
  fit: "body:fit",
  // 2026-09-13 (open facets): the flat keywords the first WD rules wrote, now group values
  "sports bra": "attire:sports-bra", jewelry: "attire:jewelry", "nail polish": "nails:painted",
  "tan lines": "body:tan-lines", "close-up": "camera_angle:close-up", "spread legs": "act:spread-legs",
  cum: "act:cum", bondage: "act:bondage", wet: "body:wet", oiled: "body:oiled", redhead: "hair:redhead",
  // style / genre / pairing — what was left in "Other"
  goth: "aesthetic:goth", alt: "aesthetic:alt", cute: "aesthetic:cute", tiktok: "genre:tiktok", amateur: "genre:amateur",
  hypno: "genre:hypno", joi: "genre:joi", sph: "genre:sph", babecock: "genre:babecock", "goon caption": "genre:goon-caption",
  hentai: "genre:hentai", animated: "genre:animated", freeuse: "genre:freeuse", hotwife: "genre:hotwife",
  breeding: "genre:breeding", implied: "genre:implied", couple: "genre:couple", "at work": "genre:at-work",
  rough: "genre:rough", milf: "genre:milf",
  bbc: "cock:bbc", bwc: "cock:bwc", girlcock: "cock:girlcock", snowbunny: "pairing:snowbunny", torta: "pairing:torta",
  // the first WD rules (v1-v2, 2026-09-12) wrote these flat before the groups existed
  tattoo: "body:tattoos", piercing: "body:piercings",
  bedroom: "setting:bedroom", bathroom: "setting:bathroom", shower: "setting:shower", kitchen: "setting:kitchen",
  couch: "setting:couch", car: "setting:car", pool: "setting:pool", beach: "setting:beach", outdoors: "setting:outdoors",
  gym: "setting:gym", office: "setting:office", selfie: "camera_angle:selfie", mirror: "camera_angle:mirror",
  pov: "camera_angle:pov", stockings: "attire:stockings", bikini: "attire:bikini", swimsuit: "attire:swimsuit",
  thong: "attire:thong", lingerie: "attire:lingerie", fishnets: "attire:fishnets", pantyhose: "attire:pantyhose",
  "high heels": "attire:high-heels", collar: "attire:collar", maid: "attire:maid", nurse: "attire:nurse",
  latex: "attire:latex", leggings: "attire:leggings", dildo: "toy:dildo", vibrator: "toy:vibrator",
  hairy: "body:hairy", "group sex": "act:group-sex", threesome: "act:threesome", cunnilingus: "act:cunnilingus",
  kissing: "act:kissing", spanking: "act:spanking", footjob: "act:footjob",
};
/** The groups added on 2026-09-12, in FACET_ORDER — every surface that lists groups
 *  badges these "New!" until its own sunset (see client/src/components/NewBadge.tsx for
 *  why the date is per surface and not here). */
export const NEW_FACETS: readonly TagFacet[] = [
  "pornstar", "act", "position", "hair", "body", "attire", "toy", "setting", "camera_angle", "hairstyle", "eyes", "nails",
  "aesthetic", "genre", "pairing", "cock",
];

/** Retired outright by the same migration: a labelOnly timeline artifact, 2 clips — and
 *  `pos:amazon`, whose position value was retired 2026-09-16 (see TAG_FACETS.position). */
export const LEGACY_DROPPED_KEYWORDS: readonly string[] = ["pos:multifocus", "pos:amazon"];

/**
 * Flat keywords that became SEVERAL facet values on 2026-09-13 and went away: the man in
 * the clip, as sex:male plus his race. Same three readers as LEGACY_FLAT_KEYWORDS.
 */
export const LEGACY_SPLIT_KEYWORDS: Readonly<Record<string, readonly string[]>> = {
  "white man": ["sex:male", "race:white"],
  "black man": ["sex:male", "race:black"],
};

/**
 * Keywords that STAY (they mean more than the facets say — `bbc` is not just a black man,
 * `snowbunny` is a pairing) but IMPLY facet values, which travel with them: the migration
 * adds the values to every clip carrying the keyword, the importer and the admin editor
 * add them on arrival, and a feed rule that names the keyword must list them.
 */
export const LEGACY_IMPLIED_KEYWORDS: Readonly<Record<string, readonly string[]>> = {
  "cock:bbc": ["sex:male", "race:black"],
  "cock:bwc": ["sex:male", "race:white"],
  "cock:girlcock": ["sex:trans"],
  "pairing:snowbunny": ["sex:female", "race:white", "sex:male", "race:black"],
  "pairing:torta": ["sex:female", "race:latina", "sex:male", "race:black"],
};

/** One incoming keyword, in today's spelling: a renamed flat keyword becomes its facet
 *  value, a split keyword becomes its values, an implying keyword keeps itself and gains
 *  its values, anything else passes through. What the importer and the admin editor apply
 *  to every tag on the way in. */
export function expandLegacyTag(tag: string): string[] {
  const t = tag.trim().toLowerCase();
  if (t in LEGACY_SPLIT_KEYWORDS) return [...LEGACY_SPLIT_KEYWORDS[t]];
  // A rename first (bbc -> pairing:bbc), then what the renamed keyword implies.
  const renamed = t in LEGACY_FLAT_KEYWORDS ? LEGACY_FLAT_KEYWORDS[t] : tag;
  const key = renamed.toLowerCase();
  if (key in LEGACY_IMPLIED_KEYWORDS) return [renamed, ...LEGACY_IMPLIED_KEYWORDS[key]];
  return [renamed];
}

/**
 * The position-review tool's labels (the held-out pos head's classes, less the retired amazon, plus the human-
 * only extras) and the facet slug each one is now. The head's vocabulary mixed positions
 * with acts — labelshop/scripts/backfill-admin-tags.ts refused to import it for exactly
 * that reason — so the split lands here: a position is `position:`, an act is `act:`.
 * `multifocus` is gone (a timeline property; see body_position's missing multiposition).
 */
export const POS_REVIEW_TAGS: Readonly<Record<string, string>> = {
  cowgirl: "position:cowgirl", doggy: "position:doggy",
  facesitting: "position:facesitting", missionary: "position:missionary",
  prone_bone: "position:prone-bone", reverse_cowgirl: "position:reverse-cowgirl",
  blowjob: "act:blowjob", cunnilingus: "act:cunnilingus", footjob: "act:footjob",
  handjob: "act:handjob", titfuck: "act:titfuck",
  dancing: "act:dancing", twerking: "act:twerking", tit_jiggle: "act:tit-jiggle",
  titty_drop: "act:titty-drop", female_masturbation: "act:masturbation", striptease: "act:striptease",
};

export type TagFacet = keyof typeof TAG_FACETS;
export const TAG_FACET_NAMES = Object.keys(TAG_FACETS) as TagFacet[];

/** Compose a facet slug. `facetTag("race", "latina")` -> `"race:latina"`. */
export function facetTag<F extends TagFacet>(
  facet: F,
  value: (typeof TAG_FACETS)[F][number],
): string {
  return `${facet}:${value}`;
}

/** The facet a slug belongs to, or null for a flat keyword (or one in a
 *  non-facet namespace like `sub:` / `pos:` / `tits:`). Does NOT check that the
 *  value is in the vocabulary — see isFacetTag for that. */
export function facetOf(tag: string): TagFacet | null {
  const i = tag.indexOf(":");
  if (i < 0) return null;
  const ns = tag.slice(0, i).toLowerCase() as TagFacet;
  return ns in TAG_FACETS ? ns : null;
}

/** True when a slug names a DECLARED value of a declared facet. `race:latina` is
 *  a facet tag; `race:latino` is not, and neither is `sub:latinas`. This is the
 *  check that keeps a mistyped rule out of the catalog. */
export function isFacetTag(tag: string): boolean {
  const facet = facetOf(tag);
  if (!facet) return false;
  const value = tag.slice(facet.length + 1).toLowerCase();
  if ((TAG_FACETS[facet] as readonly string[]).includes(value)) return true;
  // An open facet accepts any well-formed value (see OPEN_FACETS).
  return isOpenFacet(facet) && OPEN_FACET_VALUE_RE.test(value);
}

// ---------------------------------------------------------------------------
// Auto media tags — quality + aspect buckets derived from a clip's intrinsic
// pixel dimensions, applied automatically when a clip is catalogued (server
// db.upsertClip) and backfilled over existing clips. Pure functions so ingest,
// the backfill script, and the client all agree on the buckets.
// ---------------------------------------------------------------------------

/** Resolution tag buckets, highest first. A clip wears the highest bucket its STORED
 *  MASTER's short side can fill — capped at 1080p, floored at 360p.
 *
 *  The promise is the one a video platform's quality menu makes: the number is what you
 *  will actually be served, never what the source happened to be. Pick 1080p on YouTube
 *  and you get a 1080p stream; the menu never offers a tier that was not encoded.
 *
 *  Both ends changed 2026-08-11, and both for the same underlying reason — the tag used
 *  to be derived from `clips.width/height`, which holds SOURCE dimensions on many rows
 *  rather than the master we stored. Measured against every master on disk, that made
 *  45% of quality tags wrong, nearly all of them over-stated (2,308 clips claiming 1080p
 *  were 720p files).
 *
 *    1440p — removed. MASTER_MAX_SHORT_EDGE caps the master at 1080, so no master can
 *            ever fill it. The largest short side across all 5,349 stored masters is
 *            1124; all 38 clips that carried the tag were mismeasured.
 *    240p  — removed. MIN_SOURCE_HEIGHT refuses anything under 360, so a clip that could
 *            legitimately wear it does not belong in the library at all.
 *
 *  Raising MASTER_MAX_SHORT_EDGE is what would make a higher bucket meaningful again —
 *  and it only becomes VISIBLE if the rendition ladder grows a matching rung, since
 *  RENDITION_HEIGHTS tops out at 1080 too. */
export const RESOLUTION_TAG_HEIGHTS = [1080, 720, 480, 360] as const;

/** The resolution tag ("<n>p") for a single pixel dimension — the highest bucket it
 *  fills. Feed it the SHORT side (see autoMediaTags) so portrait and landscape of the
 *  same tier match, matching how "1080p" is conventionally read.
 *
 *  Null both when the dimension is unknown AND when it falls below the floor. It used to
 *  CLAMP to the smallest bucket instead — harmless while that bucket was 240p, but with
 *  the floor at 360p a clamp would label a 240p file "360p", inventing a tier it cannot
 *  serve. That is the exact class of over-statement this facet was just fixed to stop, so
 *  a sub-floor clip gets no quality tag at all: no tag means "below the floor, should not
 *  be here", which is visible, rather than a plausible wrong number, which is not.
 *
 *  e.g. 1124 -> "1080p", 1080 -> "1080p", 650 -> "480p", 359 -> null. */
export function resolutionTag(px: number | null | undefined): string | null {
  if (!px || !Number.isFinite(px) || px <= 0) return null;
  const floor = RESOLUTION_TAG_HEIGHTS[RESOLUTION_TAG_HEIGHTS.length - 1];
  if (px < floor) return null;
  return `${RESOLUTION_TAG_HEIGHTS.find((h) => px >= h)}p`;
}

/** Aspect-ratio buckets a clip can be tagged with. Named "aspect", not
 *  "orientation": `orientation:` is the sexual-orientation facet, and one word
 *  meaning two things in the same file is how you get a wrong filter. */
export const ASPECT_TAGS = TAG_FACETS.aspect;
export type AspectTag = (typeof ASPECT_TAGS)[number];

/** The aspect tag for a frame's dimensions, bucketed to the NEAREST of
 *  landscape / portrait / square. Cutoffs sit at 4:3 and 3:4 — the geometric
 *  midpoints between square and 16:9 / 9:16 — so anything within that band reads
 *  as "square" even when it isn't exactly 1:1. Null if a dimension is missing.
 *  Returns the BARE bucket; autoMediaTags adds the `aspect:` prefix. */
export function aspectTag(
  width: number | null | undefined,
  height: number | null | undefined,
): AspectTag | null {
  if (!width || !height || width <= 0 || height <= 0) return null;
  const r = width / height;
  if (r > 4 / 3) return "landscape";
  if (r < 3 / 4) return "portrait";
  return "square";
}

/** All auto-derived tags for a clip's dimensions (quality + aspect), with unknowns
 *  dropped. Lowercase + ready to INSERT OR IGNORE into clip_tags. Both are facet
 *  slugs — this is the one place the prefixes get composed, so the buckets above
 *  stay usable on their own. */
export function autoMediaTags(
  width: number | null | undefined,
  height: number | null | undefined,
): string[] {
  const tags: string[] = [];
  // Tier by the short side so a 1080x1920 portrait clip reads as "1080p", not "1440p".
  const shortSide =
    width && height ? Math.min(width, height) : (height ?? width ?? null);
  const res = resolutionTag(shortSide);
  if (res) tags.push(`quality:${res}`);
  const aspect = aspectTag(width, height);
  if (aspect) tags.push(`aspect:${aspect}`);
  return tags;
}

// ---------------------------------------------------------------------------
// Keyword provenance — WHO put a keyword on a clip. Recorded per clip↔tag
// association (not per tag): the same keyword can be model-guessed on one clip
// and hand-applied on another.
//
//   ai      a MODEL produced it — the vision sidecar (GoonerAI) writing tits:*
//           from the cup head and pos:* from the position head. The ONLY source
//           that renders a badge and can be reported as wrong, because a model's
//           output is the only thing here nobody has either measured or decided.
//   system  applied by a DETERMINISTIC rule, with no judgement and no model: the
//           daily pull's subreddit and flair autolabels, its sub:* provenance
//           tag, and the quality:* / aspect:* tags measured off the file.
//   admin   a human stands behind it: the admin tag editor, a verified position
//           label, an accepted user suggestion, or an AI tag confirmed in review.
//   manual  a human labelled it in Labelshop and an admin approved that answer.
//           Also a human judgement, but a DIFFERENT one from `admin`: nobody
//           opened this clip in the panel and typed a keyword — somebody was
//           shown the clip alone, asked one closed question, and their answer
//           survived review. Kept apart from `admin` so the two populations stay
//           countable: `admin` is curation, `manual` is a labelling campaign, and
//           a campaign is the thing you re-run, audit or roll back as a unit.
//
// A reviewed AI tag is promoted ai -> admin, which is what retires its badge.
// `manual` never carries a badge either, and like `admin` it is not reportable —
// the report queue exists to catch a model, and a model did not write these.
//
// The line that matters is "did a model decide this". A subreddit rule saying
// r/latinas means race:latina is a lookup table, not an inference — it's system,
// however much it resembles a guess. When a vision head starts predicting race
// and sex, THAT output is ai and arrives badged and reportable on its own merit.
// ---------------------------------------------------------------------------

export const TAG_SOURCES = ["ai", "system", "admin", "manual"] as const;
export type TagSource = (typeof TAG_SOURCES)[number];

/** The source recorded when nothing more specific is known. Mirrors the column
 *  default, so a row written by older code reads back as admin-owned. */
export const DEFAULT_TAG_SOURCE: TagSource = "admin";

export function isTagSource(v: unknown): v is TagSource {
  return typeof v === "string" && (TAG_SOURCES as readonly string[]).includes(v);
}

/** Every tag an ingest rule applies is 'system' — a rule is a lookup table, and a
 *  lookup table is deterministic no matter which facet it writes into. Kept as a
 *  named constant rather than a bare literal so the ingest and the migration state
 *  the same intent, and so there's one place to look when a model eventually starts
 *  writing race:/sex: and needs 'ai' instead. */
export const RULE_TAG_SOURCE: TagSource = "system";

/** What an approved Labelshop answer is stamped with when it lands in the catalog.
 *  Named rather than inlined so the import route, the db method and the check all
 *  cite one constant — and so the campaign's tags can be found in one query. */
export const LABELSHOP_TAG_SOURCE: TagSource = "manual";

/** A clip's keyword as the UI consumes it: the raw slug (still the filter key and
 *  the thing we store), its resolved display name, and who applied it. */
export interface ClipTag {
  /** The stored slug — what /browse?tag= filters on. */
  tag: string;
  /** Formatted for humans: an admin override when set, else derived from the slug. */
  displayName: string;
  source: TagSource;
}

/** Namespace prefixes stripped when deriving a display name. The namespace is an
 *  internal grouping (`position:doggy` is the position facet's "doggy", `race:latina`
 *  is the race facet's "latina"), not something a reader needs to see on a bubble —
 *  the facet's own header already says which axis it is. */
// `pornstar:` (2026-09-13) is an OPEN namespace — one value per performer, written by the
// creator-subreddit feed rules as `pornstar:<slug>` — so it is not a facet, but its values
// read as names: "Autumn Falls", not "Pornstar:autumn-falls".
const TAG_NAMESPACES = ["sub:", "rg:", "wd:", "pornstar:", ...TAG_FACET_NAMES.map((f) => `${f}:`)] as const;

/** Namespaces whose bare value can't stand alone as a label. `tits:medium` reduces
 *  to "Medium", which on its own says nothing — medium *what*? Those get the
 *  namespace back as a trailing word: "Medium Tits". A namespace absent from this
 *  map reads fine bare (`act:tit-jiggle` is "Tit Jiggle"; `sub:*` never reaches a
 *  public surface). `hair:black` would be a bare "Black" on a chip outside its group,
 *  so it gets "Black Hair". */
const TAG_NAMESPACE_SUFFIX: Record<string, string> = {
  "tits:": "Tits", "focus:": "Focused", "hair:": "Hair",
  // The open axes whose bare value is an adjective: "Long" -> "Long Hair", "Brown" -> "Brown Eyes".
  "hairstyle:": "Hair", "eyes:": "Eyes", "nails:": "Nails",
};

/** Split a keyword into its namespace prefix (with colon, lowercased) and the rest.
 *  `pos:doggy` -> `["pos:", "doggy"]`; a bare tag gets an empty namespace. */
function splitTagNamespace(tag: string): [string, string] {
  const t = tag.trim();
  for (const ns of TAG_NAMESPACES) {
    if (t.toLowerCase().startsWith(ns)) return [ns, t.slice(ns.length)];
  }
  return ["", t];
}

/** Strip a keyword's namespace prefix, if it carries one. `pos:doggy` -> `doggy`. */
export function stripTagNamespace(tag: string): string {
  return splitTagNamespace(tag)[1];
}

/** Words title-casing gets wrong: acronyms and brand casing. A facet VALUE cannot carry
 *  an admin override (the sidebar derives its chips), so the words the catalog actually
 *  uses are spelled here; a flat keyword can still be overridden per tag. */
const TAG_WORD_CASING: Readonly<Record<string, string>> = {
  pov: "POV", bbc: "BBC", bwc: "BWC", milf: "MILF", joi: "JOI", sph: "SPH", tiktok: "TikTok",
};

/** Values whose bare word is a noun, not an adjective, and so reads right without the
 *  namespace suffix: "Ponytail", not "Ponytail Hair". */
const NO_SUFFIX_VALUES = new Set(["hairstyle:ponytail", "hairstyle:braid", "hairstyle:twintails", "hairstyle:bun", "nails:painted"]);

/** Derive a display name from a slug: drop the namespace, split on separators, and
 *  title-case each word. Words containing a digit are left alone so resolution tags
 *  survive intact ("1080p" must not become "1080P"). A namespace listed in
 *  TAG_NAMESPACE_SUFFIX comes back as a trailing word, since its values are
 *  meaningless without it.
 *
 *  This is the FALLBACK. Anything it gets wrong (acronyms, brand casing like
 *  "TikTok") is fixed by setting an explicit display name in the admin tag manager,
 *  which always wins — see tagDisplayName. */
export function deriveTagDisplayName(tag: string): string {
  const [ns, rest] = splitTagNamespace(tag);
  const bare = rest.replace(/[_-]+/g, " ").trim();
  if (!bare) return tag;
  const titled = bare
    .split(/\s+/)
    .map((w) => TAG_WORD_CASING[w] ?? (/\d/.test(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");
  // A hairstyle that is a noun already ("Ponytail") needs no "Hair" after it.
  const suffix = NO_SUFFIX_VALUES.has(`${ns}${rest}`) ? undefined : TAG_NAMESPACE_SUFFIX[ns];
  return suffix ? `${titled} ${suffix}` : titled;
}

/** The name to show for a keyword: an admin-set override when present, otherwise
 *  the derived fallback. Every keyword surface goes through this. */
export function tagDisplayName(tag: string, override?: string | null): string {
  const o = override?.trim();
  return o ? o : deriveTagDisplayName(tag);
}

/**
 * How long a still image shows before a region advances.
 *
 * Images are the only thing that needs a duration now. A video advances when it ENDS —
 * which, since a rendition is cut to the clip's length bucket, is exactly the band the
 * region was filtered to. A still has no end, so it gets this.
 *
 * The per-region timer this replaces (2s/3s/5s/10s/random) picked a slice out of whatever
 * happened to be playing, which is the wrong axis to control: it made a 60s clip and a 2s
 * clip look identical on the tile and hid the difference the catalog actually has. Choose
 * the length you want by filtering on it; the clip then plays whole.
 *
 * This is the DEFAULT: a region may set its own photo hold (`Region.photoSeconds`, the
 * wall's Photo time chip). A clip revealed on its poster because it never played keeps
 * this value regardless — that is recovery, not a photo.
 */
export const FULL_IMAGE_SECONDS = 6;

/**
 * Clip length buckets — the `clip_length` facet's vocabulary, and the rendition ladder.
 *
 * One table, two jobs, on purpose. A length bucket is a promise about how long a clip
 * plays, and a rendition rung is how much of it gets encoded; if those were declared
 * separately they would drift, and the drift shows up as a clip filed under "Very Long"
 * that stops after fifteen seconds. Deriving the ladder from the buckets makes that
 * unrepresentable — a clip's rung IS its bucket's ceiling.
 *
 * `max` is INCLUSIVE and the bands are half-open below (`micro` is 0–3s, `very_short` is
 * >3–7s), so every duration lands in exactly one bucket. The top ceiling is
 * MAX_CLIP_SECONDS: ingest trims there, so nothing stored can exceed it.
 *
 * Measured against the catalog this replaces a flat 6s/15s pair. Those were chosen when a
 * rung's length was a property of the TILE (the shortest rung outlasting its timer), which
 * meant a 50s clip and a 2s clip were both cut to the same 15s — the first losing 70% of
 * itself, the second padding nothing. Bucketing per clip keeps the bandwidth win where it
 * pays (a micro clip now ships 3s, not 6s) and stops lying where it did not.
 *
 * Values carry no display label. `clip_length:very_short` derives "Very Short" through
 * tagDisplayName like every other keyword, which also means an admin can rename it
 * without a deploy — a second label here would be a second source of truth for the one
 * thing this table is trying not to have two of.
 */
/**
 * Where this app serves its own media from. Mirrors MEDIA_CACHE_PATH in
 * @gooner/media-contract, which the browser bundle cannot import — one copy here rather
 * than one per use, which is what the display player used to have.
 */
const MEDIA_CACHE_PATH = "/api/media";

/**
 * Rendition ladder: map the source pixel height a surface NEEDS to the closest rung that
 * covers it. A dense grid tile pulls a light rung; a big or fullscreen tile on a 1080p /
 * 1440p / 4K screen asks for a matching high-res one. The server only ever downscales, so
 * a large tile whose master tops out at 720p simply gets that 720p master back — no
 * upscaling, and the picker never needs to know the source height.
 *
 * Takes a DEMAND, not a box: how many source pixels a tile needs depends on the CLIP's
 * shape as well as its own. The display wall works that out in `tileSourceHeight`; the
 * clip viewer measures its own stage.
 *
 * Lives here rather than beside either caller because both the display wall and the clip
 * viewer ask this question, and two copies would be two answers. It cannot live in
 * @gooner/media-contract with the rest of the ladder vocabulary: that package is
 * node-only (see media-contract-boundary.mts) and this runs in the browser.
 */
export function pickRenditionHeight(px: number): number | null {
  if (px <= 0) return null;
  /*
   * Three rungs, matching RENDITION_HEIGHTS in @gooner/media-contract.
   *
   * Each rung covers tiles up to 1.25x its own height, so the worst case is a 25%
   * downscale — video tolerates that far better than text does, and the alternative is
   * pushing mid-size tiles onto a rung with roughly double the bytes. The ratio is
   * deliberately the SAME at both steps; an earlier cut used 500 and 900, which quietly
   * allowed 39% at the bottom and 25% at the top.
   *
   * Every real layout lands cleanly: a 3x4 grid on 1080p is 270px (-> 360), 2x2 is 540
   * (-> 720), fullscreen is 1080 (-> 1080), and a 3x4 on a 4K screen is 540 (-> 720).
   */
  if (px <= 450) return 360;
  if (px <= 900) return 720;
  if (px <= 1350) return 1080; // 1080 * 1.25, the same bound as every other step
  // Full-screen portrait. Above ~1400 physical pixels the tile is taller than a 1080 rung
  // can fill, and a portrait rung at height 1080 is only 608 wide — upscaled 1.78x across
  // a 1080-wide phone. 1920 resolves to the MASTER for both orientations (see
  // RENDITION_HEIGHTS), so this asks for more pixels without asking for another encode.
  return 1920;
}

/**
 * A url this app serves out of its own media cache, as opposed to a clip's pre-cache
 * origin (reddit, imgur) which reaches the player unchanged after an eviction.
 *
 * Tolerates the `?exp=&sig=` the server appends — see media-access.ts. Anchoring this on
 * end-of-string instead silently failed every signed url, so no tile ever asked for a
 * rung and they all pulled master bytes.
 */
const MEDIA_URL_RE = new RegExp(`^${MEDIA_CACHE_PATH}/[a-f0-9]{8,64}(?:\\?|$)`);

/** Whether this app serves the url out of its own media cache. Callers use it to decide
 *  what may be rewritten to a rung, and whether to ask for CORS — an origin url must get
 *  neither. */
export function isOwnMediaUrl(url: string): boolean {
  return MEDIA_URL_RE.test(url);
}

/**
 * Rewrite a cached clip url to a rendition rung; pass anything else through unchanged
 * (origin urls, or a null height meaning "the master, please").
 *
 * Length is not in the url. A rung is cut to the CLIP's own length bucket, which the
 * server resolves — a caller has nothing to say about it. It used to: the url carried
 * `/t/<seconds>` picked from the calling region's timer, so the same clip on a 3s tile
 * and a 10s tile was two different downloads of the same seconds.
 */
export function rungUrl(url: string, height: number | null): string {
  if (height == null) return url;
  if (!MEDIA_URL_RE.test(url)) return url;
  // The rung is a path SEGMENT, so it has to go in before the query string. Appending to
  // the whole url buries `/r/720` inside the signature parameter.
  const [path, query] = url.split("?");
  const rung = `${path}/r/${height}`;
  return query ? `${rung}?${query}` : rung;
}

export const CLIP_LENGTH_BUCKETS = [
  { value: "micro", max: 3 },
  { value: "very_short", max: 7 },
  { value: "short", max: 15 },
  { value: "medium", max: 30 },
  { value: "long", max: 45 },
  { value: "very_long", max: 60 },
] as const satisfies readonly { value: string; max: number }[];

export type ClipLengthBucket = (typeof CLIP_LENGTH_BUCKETS)[number]["value"];

// The facet vocabulary and this table must name the same buckets, in the same order.
// Asserted at compile time in BOTH directions: a one-way check passes happily when one
// side gains a bucket the other never hears about, which is precisely the drift that puts
// an unfilterable clip in the catalog. Adding a bucket now fails the build until both
// lists agree.
type _ClipLengthFacetMatchesBuckets = [
  (typeof TAG_FACETS)["clip_length"][number] extends ClipLengthBucket ? true : never,
  ClipLengthBucket extends (typeof TAG_FACETS)["clip_length"][number] ? true : never,
];
const _clipLengthVocabularyInStep: _ClipLengthFacetMatchesBuckets = [true, true];
void _clipLengthVocabularyInStep;

/**
 * How long a rendition may be, in seconds — the bucket ceilings, ascending.
 *
 * A clip is encoded at ITS bucket's ceiling and no other, so this is the set of legal
 * rung durations across the catalog, not a ladder any single clip walks. Three rungs per
 * clip (one per height), the same count as the pair it replaces.
 */
export const RENDITION_SECONDS = CLIP_LENGTH_BUCKETS.map((b) => b.max) as readonly number[];

/**
 * The bucket a stored duration falls in, or null when the duration is unknown.
 *
 * Measured on the STORED master rather than the source's declared length: the tag promises
 * what a tile will actually play, and ingest has already trimmed to MAX_CLIP_SECONDS. A
 * clip whose source ran 47s but whose stored file is 15s is "Short", because fifteen
 * seconds is what anybody watching it gets.
 *
 * Durations past the top ceiling clamp into the last bucket rather than going untagged —
 * a master that predates the current trim is still a real clip, and "Very Long" is true
 * of it.
 */
export function clipLengthBucket(durationSec: number | null | undefined): ClipLengthBucket | null {
  if (durationSec == null || !Number.isFinite(durationSec) || durationSec <= 0) return null;
  const hit = CLIP_LENGTH_BUCKETS.find((b) => durationSec <= b.max);
  return (hit ?? CLIP_LENGTH_BUCKETS[CLIP_LENGTH_BUCKETS.length - 1]).value;
}

/** The `clip_length:` slug for a stored duration, or null when it is unknown. */
export function clipLengthTag(durationSec: number | null | undefined): string | null {
  const bucket = clipLengthBucket(durationSec);
  return bucket ? `clip_length:${bucket}` : null;
}

/**
 * The rendition length for a clip, in seconds — its bucket's ceiling.
 *
 * Falls back to the top ceiling when the duration is unknown, because an unmeasured clip
 * must not be silently truncated: encoding it whole is wrong only in cost, where cutting
 * it short would be wrong on screen.
 */
export function renditionSecondsFor(durationSec: number | null | undefined): number {
  const bucket = clipLengthBucket(durationSec);
  const last = CLIP_LENGTH_BUCKETS[CLIP_LENGTH_BUCKETS.length - 1];
  return (CLIP_LENGTH_BUCKETS.find((b) => b.value === bucket) ?? last).max;
}

/**
 * The span a SET of length buckets covers, as a badge-sized string.
 *
 * Adjacent buckets merge into one range, because that is what picking them means: micro
 * plus very short is not "two lengths", it is everything up to seven seconds. Buckets with
 * a gap stay separate, since collapsing them would advertise a band the region will never
 * play — picking micro and very long and reading "0–60s" is worse than saying nothing.
 *
 *   micro                    -> "0–3s"
 *   micro + very_short       -> "0–7s"       (contiguous, merged)
 *   micro + very_long        -> "0–3s; 45–60s"
 *   every bucket             -> "0–60s"
 *
 * Accepts slugs or bare values, and ignores anything that is not a bucket, so a caller can
 * hand it a region's whole keyword list. Null when none are selected — the caller then has
 * nothing to say about length and should fall back to whatever it said before.
 */
export function clipLengthSummary(tags: readonly string[]): string | null {
  const picked = new Set<number>();
  for (const t of tags) {
    const value = t.includes(":") ? stripTagNamespace(t) : t;
    const i = CLIP_LENGTH_BUCKETS.findIndex((b) => b.value === value);
    if (i >= 0) picked.add(i);
  }
  if (picked.size === 0) return null;
  const runs: Array<[number, number]> = [];
  for (const i of [...picked].sort((a, b) => a - b)) {
    const last = runs[runs.length - 1];
    if (last && i === last[1] + 1) last[1] = i;
    else runs.push([i, i]);
  }
  return runs
    .map(([a, b]) => {
      // A run's floor is the previous bucket's ceiling — the bands are half-open below,
      // so the first bucket floors at 0 and every other one at where the last left off.
      const lo = a === 0 ? 0 : CLIP_LENGTH_BUCKETS[a - 1].max;
      return `${lo}–${CLIP_LENGTH_BUCKETS[b].max}s`;
    })
    .join("; ");
}

/** The human range for a bucket — "0–3s", ">15–30s". This is the tooltip text, and the
 *  only place the band is put into words, so the chip and the hint cannot disagree. */
export function clipLengthRangeLabel(value: string): string | null {
  const i = CLIP_LENGTH_BUCKETS.findIndex((b) => b.value === value);
  if (i < 0) return null;
  const b = CLIP_LENGTH_BUCKETS[i];
  const prev = i === 0 ? null : CLIP_LENGTH_BUCKETS[i - 1].max;
  return prev == null ? `0–${b.max}s` : `>${prev}–${b.max}s`;
}

// ---------------------------------------------------------------------------
// Regions / Monitors / Caves
// ---------------------------------------------------------------------------

export type MediaFit = "cover" | "contain";

/**
 * How a synced region relates to the one it follows.
 *
 * `normal` is the same picture twice. `flip` mirrors it across a vertical axis, which is
 * what makes a pair of tiles read as one wide symmetrical image rather than as the same
 * clip shown twice.
 */
export type SyncMode = "normal" | "flip";

export interface Region extends Rect {
  id: ID;
  name?: string;
  source: SourceConfig;
  fit: MediaFit;
  /**
   * WHICH panel is audible, and how loud — a property of the LAYOUT, not of the room.
   *
   * Sound is split across two owners on purpose. Whether the room is silent belongs to
   * the SCREEN — the device with the speaker — and stays ephemeral: the screen defaults
   * to muted and its bar (and the remote's `mute` command) toggle it, exactly as before.
   * But WHICH of a wall's panels carries the audio when the room does have sound is a
   * composition choice, the same kind as which panel is big — so it saves into the view.
   * (A per-region `muted` lived here once and was removed for making a layout decide
   * whether a television made noise; the master stayed with the screen this time.)
   *
   * `soundOn` is optional and ABSENT MEANS OFF at the reader — only an explicit `true`
   * plays. Strict at that level on purpose, and the opposite default from
   * `autoOptimize` below: the moment anyone has said ANYTHING about a wall's sound,
   * an untouched tile means silence. But a view where nobody has said anything at all
   * arrives audible, by materialization rather than by reading absence differently:
   * two deliberate stampers write an explicit `true` on every panel — viewFromTemplate
   * for quick setups, and `withSoundDefault` on the screen's doorstep for everything
   * else (collections, views that predate the field) — so unmuting a wall makes noise
   * without hovering sixteen tiles first. Materializing, not defaulting, is what lets
   * the first explicit mute of one tile leave its neighbours audible instead of
   * collapsing them back to absent-is-off.
   *
   * `volume` is 0..1 and absent means HALF — see DEFAULT_SOURCE_VOLUME. Full was the
   * element default and was vetoed by ear: a fresh wall arriving at 1.0 is a blast, and
   * full should be a level somebody ASKS for by dragging a slider there, not the reward
   * for never touching one. It applies only while the region's sound is on and the
   * screen is unmuted; it is kept when soundOn turns off, so toggling a region's sound
   * back on restores its level.
   *
   * A follower has no audio of its own (see syncWith below — it mirrors its leader's
   * decoded output, always muted, or a flanging echo plays twice); sound icons aimed at
   * a follower edit its LEADER, the same routing every playback affordance uses.
   *
   * Read through `regionSoundOn` / `regionVolume`, never the raw fields.
   */
  soundOn?: boolean;
  volume?: number;
  /**
   * Play whatever the region with this id is playing, frame for frame.
   *
   * A follower runs no feed of its own: it does not choose clips, hold a buffer, or keep
   * an advance timer.
   *
   * It does NOT get there by pointing a second element at the same URL and trusting the
   * HTTP cache. That was tried and measured: media elements fetch by Range, and two of
   * them issue two independent streams for one `immutable` URL. The follower takes the
   * leader's decoded output via `captureStream()` instead — one download, one decode,
   * the same frames. See `SyncedRegion` for the fallback where capture is unavailable,
   * which does cost a second fetch.
   *
   * `source` and `timer` are ignored while this is set. They are left ON the region
   * rather than cleared so unsyncing restores what it was playing before, instead of
   * dropping it to a default.
   */
  syncWith?: ID;
  syncMode?: SyncMode;
  /**
   * Let the region fit what it plays to the box it plays in. On unless explicitly off.
   *
   * Two things, both derived from the region's RENDERED size and shape and neither of
   * them a filter the operator wrote:
   *
   *   shape    of the clips already buffered, prefer the ones whose orientation suits
   *            this box — see `aspectPenalty`. A 16:9 clip in a 9:16 tile loses 72% of
   *            its width to the crop, and the subject with it.
   *   quality  ask for the rendition rung that covers the box and no more — see
   *            `tileSourceHeight`. A 270px tile on a phone does not need 1080p to look
   *            the same, and a wall of sixteen of them asking for it is the difference
   *            between a link that keeps up and one that does not.
   *
   * OPTIONAL, and absent means ON. That is what makes this default true without a
   * migration: every stored region predates the field, and reading absence as "on"
   * gives all of them the behaviour they already had (the rung has always been
   * size-matched) plus the shape preference. Only an explicit `false` opts out, and
   * opting out means full masters at whatever shape comes up — which is heavier, not
   * lighter, and is the honest meaning of "don't optimise this for me".
   *
   * An `aspect:*` KEYWORD overrides it. Picking the orientation by tag and ranking by
   * measured orientation are the same act twice over, and the tag wins — see
   * `autoOptimizeActive`, which is what everything except the editor should read.
   */
  autoOptimize?: boolean;

  /**
   * Point the crop at what the eye lands on, instead of the middle of the frame.
   *
   * A region's shape comes from the viewport, so the same clip plays in a 5.5:1 band and
   * a 0.46:1 column at once, and `object-fit: cover` centre-crops for both. At the widest
   * tile that keeps 22% of the source and puts the subject off the tile entirely in 44%
   * of sampled frames. With this on, the tile pans — and zooms, minimally — to hold the
   * clip's focal point near the middle, holding each framing still and gliding only when
   * the subject genuinely moves.
   *
   * OPTIONAL, and absent means ON, for the same reason `autoOptimize` above is: every
   * stored region predates the field, and a default that needed a migration to express
   * would be a default in name only. Only an explicit `false` opts out.
   *
   * Does nothing on a `contain` region (there is no crop to steer) or on a clip the
   * focal sweep has not reached yet — both fall back to plain centre-cover.
   */
  adaptiveFraming?: boolean;

  /**
   * How long a PHOTO holds in this tile before it advances, in seconds.
   *
   * Per region for the same reason sound is: one wall often mixes a video feed and a
   * photo feed, and a single global pace would force one tempo on both. Videos ignore it
   * — they still play to `ended` — and so does a clip shown on its poster because it
   * never became playable; that recovery path keeps FULL_IMAGE_SECONDS.
   *
   * OPTIONAL, and absent means FULL_IMAGE_SECONDS, so every stored region keeps exactly
   * the pace it had (first-load stagger included) without a migration. Stored values
   * snap to PHOTO_SECONDS_STOPS. A follower's is ignored like its source: the leader
   * runs the feed, so the leader's value is the pair's.
   *
   * Read through `regionPhotoSeconds`, never the raw field.
   */
  photoSeconds?: number;
}

/**
 * The Photo time slider's stops, in seconds. A ladder rather than a 1s-step range: the
 * difference between 20 and 21 seconds is nothing anyone sees, while 2→3 is half again.
 * The 2s floor is a load decision as much as a taste one — every flip fetches and decodes
 * the next item, and a sixteen-tile photo wall at 1s is a strobe.
 */
export const PHOTO_SECONDS_STOPS = [2, 3, 4, 5, 6, 8, 10, 15, 20, 30] as const;

/** Snap any number to the nearest Photo time stop (ties go to the shorter one), clamping
 *  past either end. Null for anything that is not a finite number — garbage is an
 *  absence, and the caller decides what absence means. */
export function snapPhotoSeconds(v: unknown): number | null {
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  let best: number = PHOTO_SECONDS_STOPS[0];
  for (const s of PHOTO_SECONDS_STOPS) if (Math.abs(s - v) < Math.abs(best - v)) best = s;
  return best;
}

/** The region's photo hold with absence and garbage read as FULL_IMAGE_SECONDS — see
 *  `Region.photoSeconds`. The one reader, so a tile with no value cannot drift from the
 *  6s every stored wall was authored against. */
export function regionPhotoSeconds(region: Pick<Region, "photoSeconds">): number {
  return snapPhotoSeconds(region.photoSeconds) ?? FULL_IMAGE_SECONDS;
}

/** The STORED switch, with absence read as on — see `Region.adaptiveFraming`. One reader,
 *  for the same reason autoOptimizeOn is one. */
export function adaptiveFramingOn(region: Pick<Region, "adaptiveFraming">): boolean {
  return region.adaptiveFraming !== false;
}

/** The STORED switch, with absence read as on — see `Region.autoOptimize`. One reader, so
 *  "absent is on" cannot be got wrong in one place and right in another.
 *
 *  Not the same question as "is it in force": a region whose keywords settle shape or
 *  quality by hand overrides it. Ask `autoOptimizeActive` for that, and this one only when
 *  you specifically want what the operator chose. */
export function autoOptimizeOn(region: Pick<Region, "autoOptimize">): boolean {
  return region.autoOptimize !== false;
}

/** The STORED sound switch, with absence read as OFF — see `Region.soundOn`. One reader,
 *  so "absent is off" cannot be got wrong in one place and right in another. The screen's
 *  own mute is a separate, senior switch: a region plays sound only when this is true AND
 *  the screen is unmuted. */
export function regionSoundOn(region: Pick<Region, "soundOn">): boolean {
  return region.soundOn === true;
}

/** A stored 0..1 level, with absence and garbage read as FULL and out-of-range hand edits
 *  clamped rather than obeyed. The clamp for every level on a screen — but its
 *  absence-is-full fallback now belongs to the EPHEMERAL levels only (the master fader,
 *  the channel), which are always explicit anyway. A STORED region level answers absence
 *  differently — see regionVolume, the one reader allowed to. */
export function clampVolume(v: unknown): number {
  if (typeof v !== "number" || !Number.isFinite(v)) return 1;
  return Math.min(1, Math.max(0, v));
}

/** What a SOURCE plays at when nobody has chosen its level: half. One constant for every
 *  source on a screen — a region whose view never stored a volume, the radio channel's
 *  own fader at mount — so "loud enough to hear, quiet enough that full is something you
 *  ask for" cannot drift apart per source. See `Region.volume` for the veto of the
 *  element-default full. */
export const DEFAULT_SOURCE_VOLUME = 0.5;

/** The region's volume with absence read as the HALF default and out-of-range hand edits
 *  clamped rather than obeyed — see `Region.volume`. Garbage (a hand-edited string, NaN)
 *  is an absence, not a level, so it lands on the default too rather than on clampVolume's
 *  full. */
export function regionVolume(region: Pick<Region, "volume">): number {
  return typeof region.volume === "number" && Number.isFinite(region.volume)
    ? clampVolume(region.volume)
    : DEFAULT_SOURCE_VOLUME;
}

/** A view NOBODY has voiced a sound opinion on arrives audible: every region stamped an
 *  explicit `soundOn: true`, the same arrival quick setups get from viewFromTemplate.
 *  One opinion anywhere — a single `true` OR `false` — and the view passes through
 *  untouched, because from then on absence is that author's silence. No `volume` is
 *  written (absence already means the half default), followers are stamped like everyone
 *  else
 *  (SyncedRegion is hard-muted regardless), and the inapplicable cases return the SAME
 *  object so a re-arrival cannot churn state. See `Region.soundOn` for why this
 *  materializes rather than defaults. */
export function withSoundDefault(view: View): View {
  if (view.regions.length === 0) return view;
  if (view.regions.some((r) => r.soundOn !== undefined)) return view;
  return { ...view, regions: view.regions.map((r) => ({ ...r, soundOn: true })) };
}

// ---------------------------------------------------------------------------
// The screen's own half of sound, which used to be a boolean.
//
// Sound has two owners: WHICH panel carries it and how loud saves into the view
// (Region.soundOn / Region.volume, absence = silent); WHETHER the room makes noise at
// all is the screen's own ephemeral switch, senior to everything a region says. That
// senior switch was `muted`, and a wall of sixteen panels made it too blunt — unmuting
// meant every soundOn panel spoke at once.
//
// It is now four-valued. The two extra states are the ones a room actually wants:
// exactly one panel audible, and the radio without the clips.
// ---------------------------------------------------------------------------

/** The screen's four sound states. `radio` silences every panel but leaves the channel
 *  playing; `muted` is the only one that silences everything, including the channel. */
// ---------------------------------------------------------------------------
// Radio channels, and the partners who supply them.
//
// CURATED, NOT USER CONTENT. A partner gives us a track; in exchange the channel row
// links to a credit card for them. That trade is the whole reason the feature exists,
// which is why the partner rides INSIDE the channel rather than being a second fetch —
// a channel with no partner is not a thing we serve.
//
// Ids are opaque SLUGS rather than filenames so a file rename cannot orphan the
// analytics, and they are the only part of any of this that reaches PostHog.
// ---------------------------------------------------------------------------

/** What kind of destination a partner link is. A CLOSED vocabulary, because this is what
 *  travels in analytics — the url never does. */
export type PartnerLinkKind = "site" | "store" | "social" | "music" | "support";

export interface PartnerLink {
  label: string;
  url: string;
  kind: PartnerLinkKind;
}

/**
 * A partner's credit card. Shown ONLY on a deliberate click — never as an impression
 * slot, never auto-opened, never on a timer. That is what makes it a credit and not an
 * ad; see AdSlot.tsx, whose rule this is the sanctioned exception to.
 */
export interface PartnerInfo {
  /** Slug, `[a-z0-9-]+`. The one field that reaches analytics. */
  id: string;
  name: string;
  /** One line under the name, in the accent callout. */
  tagline?: string;
  /** Body copy. Plain text — no markup is rendered. */
  blurb: string;
  imageUrl: string;
  links: ReadonlyArray<PartnerLink>;
}

export interface Channel {
  /** Slug, `[a-z0-9-]+`. Stable across a file rename. */
  id: string;
  name: string;
  artist: string;
  artUrl: string;
  /**
   * The looping body, played with `<audio loop>` — so a channel loops because its FILE
   * loops, not because anything schedules it.
   *
   * This replaced a `loopStart`/`loopEnd` pair decoded through Web Audio, which is
   * seamless and cannot carry a real track: `decodeAudioData` has no streaming form, so a
   * twelve-minute channel meant 267 MB of PCM resident in the browser for as long as it
   * played. Cutting the file at the loop point instead costs one extra asset and keeps
   * memory flat. Two expressions of the same fact — a split file AND an offset — would
   * only invite them to disagree, so the offset is gone.
   */
  audioUrl: string;
  /** Played once before the body, for a channel whose opening should not come round again.
   *  Absent means the body is the whole thing, and nothing special-cases it. */
  introUrl?: string;
  partner: PartnerInfo;
}

export interface ChannelsResponse {
  channels: Channel[];
}

export type SoundMode = "muted" | "radio" | "one" | "all";
export const SOUND_MODES: readonly SoundMode[] = ["muted", "radio", "one", "all"];

/**
 * ONE GAIN LAW for every source on a screen: a region's clip, the radio channel, anything
 * added later. The master fader multiplies the source's own stored level.
 *
 * TAKES NO MODE, on purpose, and `sourceGain.length === 2` is asserted. Whether a source
 * is allowed to be heard is a routing question answered by soundsHere (for panels) and
 * radioAudible (for the channel); how loud it is once allowed is this. Folding the mode
 * in here would put the same decision in two places and let them disagree.
 */
export function sourceGain(master: number, level: number): number {
  return clampVolume(master) * clampVolume(level);
}

/** Whether the radio channel may be heard. Every mode but `muted` — the modes describe
 *  what the PANELS do, and the channel is a source the room started by hand, with its own
 *  transport and its own level. `muted` is the one state that means the screen is silent,
 *  so it is the one state that reaches the channel. */
export function radioAudible(mode: SoundMode): boolean {
  return mode !== "muted";
}

/**
 * The AND of sound's two owners, with the screen's half now four-valued.
 *
 * THE LAYOUT'S HALF IS TESTED FIRST AND UNCONDITIONALLY: nothing any mode can say makes a
 * region the view silenced speak. `soloId` names Play-1's chosen panel and is ignored in
 * every other mode, so at most one region can be audible in `one` however it is called.
 */
export function soundsHere(
  mode: SoundMode,
  region: Pick<Region, "id" | "soundOn">,
  soloId: ID | null,
): boolean {
  if (!regionSoundOn(region)) return false;
  if (mode === "muted" || mode === "radio") return false;
  if (mode === "all") return true;
  return region.id === soloId;
}

/**
 * The panels Play 1 may choose between: sound-on LEADERS, in authored order.
 *
 * Two exclusions, both hard. A region the view silenced is never a candidate — that is
 * the same absence-is-off rule regionSoundOn pins. And a FOLLOWER is excluded
 * structurally: SyncedRegion mirrors its leader's decoded frames and carries a bare
 * literal `muted`, so "audible follower" is not a state that exists, and a picker that
 * chose one would silence the wall while believing it had not.
 */
export function audibleCandidates(regions: readonly Region[]): Region[] {
  return regions.filter((r) => regionSoundOn(r) && !syncLeaderOf(r, regions));
}

/**
 * Play 1's choice: which eligible panel carries the sound right now.
 *
 * STICKY FIRST. A pick that still works is never moved off, which is what makes this feel
 * like "the audio panel" rather than a shuffle — and it means an ordinary clip advance
 * costs nothing, because a panel that swaps one video for another is still audible.
 *
 * THEN ROUND-ROBIN FROM THE CURRENT PICK, not from the start: when the audible panel goes
 * quiet, "the next panel with sound" should walk the wall rather than snap back to tile
 * one every time. Falls back to the first candidate when nothing is audible yet — a wall
 * of stills has no right answer, and holding a position means sound arrives the instant
 * that panel's next clip is a video, with no fresh trigger needed.
 */
export function pickSolo(
  order: readonly ID[],
  audible: (id: ID) => boolean,
  current: ID | null,
): ID | null {
  if (order.length === 0) return null;
  if (current !== null && order.includes(current) && audible(current)) return current;
  const from = current === null ? -1 : order.indexOf(current);
  for (let i = 1; i <= order.length; i++) {
    const id = order[(from + i + order.length) % order.length];
    if (id !== undefined && audible(id)) return id;
  }
  return order[0] ?? null;
}

/**
 * The pointer's override of Play 1's standing pick, or null to leave it standing.
 *
 * Hovering a FOLLOWER means its leader — the same routing the wall's skip already uses,
 * because a follower shows its leader's frames and cannot make noise of its own. Hovering
 * a region the view silenced is a NO-OP rather than silence: the eligibility rule is what
 * keeps a pointer sweeping across muted tiles from cutting the sound out repeatedly.
 */
export function hoverSolo(regions: readonly Region[], hoveredId: ID | null): ID | null {
  if (hoveredId === null) return null;
  const hovered = regions.find((r) => r.id === hoveredId);
  if (!hovered) return null;
  const target = syncLeaderOf(hovered, regions) ?? hovered;
  return regionSoundOn(target) ? target.id : null;
}

/** What this screen reports over the socket, which is still a boolean and should stay one:
 *  the remote asks "is it quiet", and every audible mode answers the same. */
export function soundModeMuted(mode: SoundMode): boolean {
  return mode === "muted";
}

/**
 * A boolean from the wire, resolved against the mode this screen was last audible in.
 *
 * A boolean has one inverse; a four-mode enum has three, so the bar's own button and the
 * remote's command both come through here or they will eventually disagree about what
 * "unmute" restores. A screen that has never left muted restores `all` — exactly what the
 * boolean always did — while one somebody set to Play 1 at the glass gets Play 1 back,
 * because a remote must not quietly destroy a local setting.
 */
export function soundModeFromWire(on: boolean, last: SoundMode): SoundMode {
  if (on) return "muted";
  return last === "muted" ? "all" : last;
}

/**
 * The facets that settle, by hand, the thing auto-optimise would settle for you.
 *
 * One: `aspect`. Picking `aspect:portrait` and letting the shape preference rank by
 * measured orientation are the SAME operation done twice — one selects by tag, the other
 * by pixels — so the tag wins and the ranking stands down.
 *
 * `quality` is deliberately NOT here, and the reason is worth keeping because it is the
 * obvious thing to add back. A quality keyword picks WHICH CLIPS play; a rung picks HOW
 * MANY BYTES of the clip that was picked. Those compose — they are not two answers to one
 * question. Read the Latinas quick setup for the intent in the wild: its filter is
 * `actor_count:1` + `race:latina` + `sex:female` + `quality:720p|1080p`, four conditions
 * of the same kind, excluding badly-mastered sources. Treating that fourth one as a
 * delivery instruction turned auto-optimise off across three portrait lanes over a
 * catalog that is 86% portrait — the case it works best on.
 *
 * The counter-argument, for whoever weighs this again: RESOLUTION_TAG_HEIGHTS says the
 * number means what a quality menu means, so serving a 360 rung of a clip somebody
 * filtered to 1080p can be read as breaking that promise. Considered and rejected — the
 * tag's promise is about the tag not over-stating the MASTER, which is a claim about
 * cataloguing, not about what a 270px tile should download.
 *
 * `satisfies` rather than a bare literal so a renamed facet fails the build here instead
 * of quietly matching nothing and turning the override off for everybody.
 */
export const AUTO_OPTIMIZE_FACETS = ["aspect"] as const satisfies readonly TagFacet[];

/**
 * Which of those a region's source picks, in `AUTO_OPTIMIZE_FACETS` order — empty for
 * none, which is the common case.
 *
 * Only a keyword source can pick one. A collection or a locked clip is a set of clips
 * somebody chose, not a statement about shape or quality, so it overrides nothing.
 *
 * Returns the facets rather than a boolean because the UI has to SAY which: "off because
 * you picked something" is an unexplained disabled control, and an unexplained disabled
 * control reads as a bug.
 */
export function autoOptimizeOverrides(source: SourceConfig): TagFacet[] {
  if (source.kind !== "keywords") return [];
  const picked = new Set(keywordGroups(source).flat().map(facetOf));
  return AUTO_OPTIMIZE_FACETS.filter((f) => picked.has(f));
}

/**
 * Whether auto-optimise is actually in force — the switch AND nothing overriding it.
 *
 * The rule is that the hand beats the guess. Naming the orientation in the keywords is
 * you deciding it yourself, and a feature that then quietly ranked by its own measurement
 * would be the app arguing with the filter you just set.
 *
 * The whole switch, not just its shape half, even though `aspect` is the only overriding
 * facet. One switch has one honest rendering: half-on would need a label that is true on
 * Tuesdays. A region that says which shape it wants has said enough about fitting itself
 * to its box, and the operator who wants the rung sized anyway can clear the chip — which
 * costs nothing, because the ranking would then do the same job the chip was doing.
 *
 * The stored switch is left ALONE by an override, so clearing the keywords restores
 * whatever the operator had chosen rather than a default.
 */
export function autoOptimizeActive(region: Pick<Region, "autoOptimize" | "source">): boolean {
  return autoOptimizeOn(region) && autoOptimizeOverrides(region.source).length === 0;
}

/**
 * How badly a clip's shape suits a region's, lower being better. 0 / 1 / 2, not a
 * boolean, because the middle case is real: a square clip in a portrait tile is a
 * compromise, and a landscape one is a different thing entirely.
 *
 *   0  the orientations match, the shape is unknown, or the region has no orientation
 *      to prefer. Unknown dimensions score 0 on purpose — a clip we cannot measure must
 *      not be pushed behind one we can, since that would quietly rank the whole
 *      pre-dimensions catalog last.
 *   1  a square clip in a portrait or landscape tile. Cover crops it ~50% on one axis:
 *      a lot, but the subject stays in frame.
 *   2  the opposite orientation. Cover crops a 16:9 clip to 28% of its width in a 9:16
 *      tile, which is a picture of somebody's midriff.
 *
 * `shape` is the REGION's bucket, from `aspectTag` on its rendered box — so a tile that
 * is roughly square (inside the 4:3 / 3:4 band) scores everything 0 and expresses no
 * preference, which is correct: it crops both orientations about equally.
 */
export function aspectPenalty(
  clip: { width?: number | null; height?: number | null },
  shape: AspectTag | null,
): 0 | 1 | 2 {
  if (!shape || shape === "square") return 0;
  const clipShape = aspectTag(clip.width, clip.height);
  if (clipShape === null || clipShape === shape) return 0;
  return clipShape === "square" ? 1 : 2;
}

/**
 * The source height a tile needs to render a clip at `fit` without upscaling it.
 *
 * The number the rendition ladder should be asked for, and NOT simply the box's height.
 * Under `cover` the browser scales the clip until it covers both axes, so whichever axis
 * demands more wins:
 *
 *   landscape clip, landscape tile   both agree; the answer is the box height
 *   portrait clip, landscape tile    cover scales it by WIDTH. A 1080x1920 clip in a
 *                                    960x540 box renders 960x1707 and crops — so the
 *                                    tile needs 1707 source pixels of height, not 540.
 *                                    Asked for 540 (the box) it gets a 360 rung, 203px
 *                                    wide, blown up 4.7x across the tile.
 *
 * `contain` is the same rule with the comparison flipped: the clip is scaled until it
 * FITS inside, so the smaller demand wins and the tile needs less than its own height.
 *
 * Falls back to the box height when the clip's dimensions are unknown, which is what the
 * picker did for every clip before it could see them.
 *
 * Pure, and in pixels of whatever unit `box` is given in — the caller multiplies by
 * devicePixelRatio, since the ladder is about real pixels and CSS ones are not.
 */
export function tileSourceHeight(
  box: { w: number; h: number },
  clip: { width?: number | null; height?: number | null },
  fit: MediaFit,
): number {
  const { width: cw, height: ch } = clip;
  if (!cw || !ch || cw <= 0 || ch <= 0 || box.w <= 0 || box.h <= 0) return box.h;
  // What each axis alone would demand of the source: covering the height needs the box's
  // height; covering the width needs however tall the clip is at that width.
  const byHeight = box.h;
  const byWidth = box.w * (ch / cw);
  return fit === "contain" ? Math.min(byHeight, byWidth) : Math.max(byHeight, byWidth);
}

// ---------------------------------------------------------------------------
// Wall playback budget (issues #13/#14, 2026-10-28).
//
// Two questions every wall tile asks before it builds a url: how many REAL pixels is it
// (devicePixelRatio, capped — #13), and what is the highest rung this device may play
// right now (a ceiling made of the phone setting, a one-time decode probe and a live
// dropped-frame budget — #14). Pure and DOM-free so tools/checks can pin every step;
// the browser half is client/src/display/playbackBudget.ts.
// ---------------------------------------------------------------------------

/** The rungs, low to high — RENDITION_HEIGHTS in @gooner/media-contract. */
export const RUNGS = [360, 720, 1080, 1920] as const;

export interface DisplaySettings {
  /** DPR a phone tile is measured at. 1.5: a full-height phone tile lands on 1080 not 1920. */
  phoneDprCap: number;
  /** Highest rung a phone tile asks for; 0 = no ceiling. 720 by default. */
  phoneRungCeiling: number;
  /** The live decode budget (probe, dropped-frame monitor, decode slots). */
  decodeBudget: boolean;
}

/** The display block, clamped: a typo in the admin panel must not blank every wall. */
export function displaySettings(s: Partial<SystemSettings> | null | undefined): DisplaySettings {
  const d = { ...DEFAULT_SETTINGS.display, ...(s?.display ?? {}) };
  const cap = Number(d.phoneDprCap);
  const ceiling = Number(d.phoneRungCeiling);
  return {
    phoneDprCap: Number.isFinite(cap) && cap >= 1 && cap <= 3 ? cap : DEFAULT_SETTINGS.display.phoneDprCap,
    phoneRungCeiling:
      ceiling === 0 || (RUNGS as readonly number[]).includes(ceiling) ? ceiling : DEFAULT_SETTINGS.display.phoneRungCeiling,
    decodeBudget: d.decodeBudget !== false,
  };
}

/**
 * A phone, for the purposes of the wall: a coarse pointer AND a small screen. The pointer
 * alone would class every tablet (and touch laptops) as a phone; the short side is what
 * separates a 412-wide phone from an 820-wide iPad. CSS px, from `screen`, not the
 * viewport — a phone in landscape is still a phone.
 */
export const PHONE_SHORT_SIDE_MAX = 600;
export function isPhoneClass(p: { coarse: boolean; shortSide: number }): boolean {
  return p.coarse && p.shortSide > 0 && p.shortSide <= PHONE_SHORT_SIDE_MAX;
}

/**
 * The pixel ratio a tile is measured at. 2 everywhere (what ClipViewer always used — a
 * 3x screen asks by density, not by how big the video looks), and the admin's phone cap
 * on a phone. Desktop and TV at DPR <= 2 are untouched.
 */
export function effectiveDpr(raw: number, phone: boolean, s: DisplaySettings): number {
  const r = Number.isFinite(raw) && raw > 0 ? raw : 1;
  return Math.min(r, phone ? s.phoneDprCap : 2);
}

/** Hold a rung under a ceiling. Null (the master) stays null; a null ceiling is none. */
export function capRung(rung: number | null, ceiling: number | null): number | null {
  if (rung == null || ceiling == null || ceiling <= 0) return rung;
  return Math.min(rung, ceiling);
}

/** The rung below / above, or the same at the ends of the ladder. */
export function stepDownRung(r: number): number {
  const i = (RUNGS as readonly number[]).indexOf(r);
  return i > 0 ? RUNGS[i - 1] : RUNGS[0];
}
export function stepUpRung(r: number): number | null {
  const i = (RUNGS as readonly number[]).indexOf(r);
  return i >= 0 && i < RUNGS.length - 1 ? RUNGS[i + 1] : null;
}

/** Lowest of any number of ceilings; null when none is set. */
export function minCeiling(...cs: Array<number | null | undefined>): number | null {
  const set = cs.filter((c): c is number => typeof c === "number" && c > 0);
  return set.length ? Math.min(...set) : null;
}

/**
 * What the decode probe asks the browser about each rung: VP9 (what every rung is encoded
 * as), 16:9 at that height, the rung's frame rate (24 at <= 720, RENDITION_FPS_CAP), and
 * a bitrate. The encodes are CRF so there is no nominal bitrate; these are the measured
 * order of magnitude (docs/perf/decode-load.md), rounded up — the browser's answer turns
 * on resolution and frame rate far more than on bitrate.
 */
/** The codecs the probe asks about. VP9 is what every rung is encoded as; H.264 is asked
 *  only to MEASURE (the decode_capabilities event): how many devices decode H.264 in
 *  hardware but not VP9 — the data an H.264 ladder would have to be justified by. */
export const PROBE_CODECS = {
  vp9: 'video/webm; codecs="vp09.00.10.08"',
  h264: 'video/mp4; codecs="avc1.640028"',
} as const;
export const PROBE_CONFIGS: ReadonlyArray<{ rung: number; width: number; height: number; framerate: number; bitrate: number }> = [
  { rung: 360, width: 640, height: 360, framerate: 24, bitrate: 800_000 },
  { rung: 720, width: 1280, height: 720, framerate: 24, bitrate: 2_500_000 },
  { rung: 1080, width: 1920, height: 1080, framerate: 30, bitrate: 5_000_000 },
  { rung: 1920, width: 3414, height: 1920, framerate: 30, bitrate: 9_000_000 },
];

/** Dash.js DroppedFramesRule's numbers: a verdict needs 375 frames, and >15% is too many. */
export const BUDGET_MIN_FRAMES = 375;
export const BUDGET_DROP_DOWN = 0.15;
/** Recovery: every window this clean for this long lifts ONE step. */
export const BUDGET_DROP_UP = 0.05;
export const BUDGET_RECOVER_MS = 60_000;
/** After any change, wait this long (and a fresh window) before judging again. */
export const BUDGET_COOLDOWN_MS = 10_000;
/**
 * HYSTERESIS (2026-10-29). The first night on prod, desktop sessions the budget touched
 * changed ~16 times each: step down, a clean minute at the lower load, lift, drop frames
 * again, step down — the clean minute was clean BECAUSE of the step. A step down soon after
 * a lift is a RELAPSE, and every relapse doubles the clean time the next lift needs
 * (60 s, 2, 4, then 8 min at most). Sticky for the session with the rest of the state.
 */
export const BUDGET_RELAPSE_WINDOW_MS = 120_000;
export const BUDGET_RECOVER_MAX_MS = 8 * 60_000;
/**
 * DECODER FAILURES (2026-10-29). A decoder that cannot START ("Failed to init decoder",
 * PIPELINE_ERROR_DECODE, MEDIA_ERR_DECODE) produces no frames, so the dropped-frame monitor
 * never sees it — a Firefox wall of 16 tiles logged fifteen of them overnight and the
 * budget never moved. This many inside the window lowers the tile count directly: running
 * out of decoders is a COUNT problem, which fewer pixels per tile does not fix.
 */
export const BUDGET_DECODE_FAILS = 2;
export const BUDGET_DECODE_FAIL_WINDOW_MS = 30_000;

export interface BudgetState {
  /** Highest rung this session may fetch; null = no ceiling. */
  rungCeiling: number | null;
  /** How many tiles may decode at once; null = no limit. */
  maxDecoding: number | null;
  /** The probe's ceiling, kept apart so recovery never lifts above what the probe said. */
  probeCeiling: number | null;
  /** Step-downs that came soon after a lift; each doubles the next lift's wait. */
  relapses?: number;
}
export type BudgetReason = "probe" | "dropped_frames" | "decode_errors" | "recover";
export interface BudgetChange extends BudgetState {
  reason: BudgetReason;
  droppedRatio: number | null;
  tiles: number;
}

/**
 * The decode budget's state machine. Fed frame-count DELTAS summed over the playing tiles;
 * answers whether to step. Step down fast (one window over the line), up slowly (a minute
 * clean), one step at a time, rung first then tile count on the way down, the reverse on
 * the way up. Never below rung 360 or one decoding tile.
 */
export class DecodeBudget {
  state: BudgetState;
  private win = { dropped: 0, total: 0 };
  private cooldownUntil = 0;
  private cleanSince: number | null = null;
  private lastLiftAt: number | null = null;
  private failures: number[] = [];

  /** How long playback must stay clean before the next lift: doubles per relapse. */
  recoverMs(): number {
    return Math.min(BUDGET_RECOVER_MS * 2 ** (this.state.relapses ?? 0), BUDGET_RECOVER_MAX_MS);
  }

  /**
   * A tile's decoder failed (to start, or mid-stream). Enough of them inside the window
   * and the tile count steps down — the only lever that frees decoders.
   */
  decodeFailure(s: { tiles: number; now: number }): BudgetChange | null {
    this.failures = this.failures.filter((t) => s.now - t < BUDGET_DECODE_FAIL_WINDOW_MS);
    this.failures.push(s.now);
    if (this.failures.length < BUDGET_DECODE_FAILS || s.now < this.cooldownUntil || s.tiles <= 0) return null;
    this.failures = [];
    this.cleanSince = null;
    return this.stepTiles("decode_errors", null, s);
  }

  constructor(init?: Partial<BudgetState>) {
    this.state = { rungCeiling: null, maxDecoding: null, probeCeiling: null, ...(init ?? {}) };
  }

  /** The probe's verdicts per rung: the ceiling is the highest smooth rung (floor 360). */
  applyProbe(results: Array<{ rung: number; smooth: boolean }>, tiles: number): BudgetChange | null {
    const smooth = results.filter((r) => r.smooth).map((r) => r.rung);
    const top = smooth.length ? Math.max(...smooth) : RUNGS[0];
    if (top >= RUNGS[RUNGS.length - 1]) return null; // everything smooth: no ceiling
    this.state.probeCeiling = top;
    this.state.rungCeiling = minCeiling(this.state.rungCeiling, top);
    return { ...this.state, reason: "probe", droppedRatio: null, tiles };
  }

  /**
   * One sample: frames dropped / rendered across the playing tiles since the last sample,
   * how many tiles were decoding, and the highest rung among them (null when unknown).
   */
  observe(s: {
    dropped: number;
    total: number;
    tiles: number;
    highestRung: number | null;
    now: number;
    /** The ceiling already applied from outside the live budget (the phone setting). */
    outerCeiling?: number | null;
  }): BudgetChange | null {
    if (s.total <= 0 || s.tiles <= 0) return null;
    if (s.now < this.cooldownUntil) return null;
    this.win.dropped += Math.max(0, s.dropped);
    this.win.total += s.total;
    if (this.win.total < BUDGET_MIN_FRAMES) return null;
    const ratio = this.win.dropped / this.win.total;
    this.win = { dropped: 0, total: 0 };

    if (ratio > BUDGET_DROP_DOWN) {
      this.cleanSince = null;
      return this.stepDown(ratio, s);
    }
    if (ratio >= BUDGET_DROP_UP) {
      this.cleanSince = null;
      return null;
    }
    this.cleanSince ??= s.now;
    if (s.now - this.cleanSince < this.recoverMs()) return null;
    this.cleanSince = s.now;
    return this.stepUp(ratio, s);
  }

  private changed(reason: BudgetReason, ratio: number | null, s: { tiles: number; now: number }): BudgetChange {
    this.cooldownUntil = s.now + BUDGET_COOLDOWN_MS;
    if (reason === "recover") this.lastLiftAt = s.now;
    else if (this.lastLiftAt != null && s.now - this.lastLiftAt < BUDGET_RELAPSE_WINDOW_MS) {
      // Down again soon after a lift: the lift was premature. Make the next one wait longer.
      this.state.relapses = (this.state.relapses ?? 0) + 1;
      this.lastLiftAt = null;
    }
    return { ...this.state, reason, droppedRatio: ratio == null ? null : Math.round(ratio * 1000) / 1000, tiles: s.tiles };
  }

  /** Fewer tiles decode at once: three quarters, never below one. */
  private stepTiles(reason: BudgetReason, ratio: number | null, s: { tiles: number; now: number }): BudgetChange | null {
    const current = this.state.maxDecoding ?? s.tiles;
    const next = Math.max(1, Math.floor(current * 0.75));
    if (next >= current) return null; // already one tile: nothing left to give
    this.state.maxDecoding = next;
    return this.changed(reason, ratio, s);
  }

  private stepDown(
    ratio: number,
    s: { tiles: number; highestRung: number | null; now: number; outerCeiling?: number | null },
  ): BudgetChange | null {
    // Rungs first: fewer pixels per tile keeps every tile moving. Stepped from what is
    // KNOWN to be playing — the tiles' own rungs, else the ceilings already holding them.
    // Guessing 1920 when nothing reported a rung "lowered" a ceiling to 1080 on a phone
    // already held at 720: a step that changed nothing and cost a cooldown.
    const playing = minCeiling(s.highestRung, this.state.rungCeiling, s.outerCeiling);
    if (playing != null && playing > RUNGS[0]) {
      this.state.rungCeiling = stepDownRung(playing);
      return this.changed("dropped_frames", ratio, s);
    }
    // At 360 and still dropping: fewer tiles decode at once.
    return this.stepTiles("dropped_frames", ratio, s);
  }

  private stepUp(ratio: number, s: { tiles: number; now: number }): BudgetChange | null {
    // The reverse order: tiles back first, then pixels.
    if (this.state.maxDecoding != null) {
      const next = Math.ceil(this.state.maxDecoding / 0.75);
      this.state.maxDecoding = next >= s.tiles ? null : next;
      return this.changed("recover", ratio, s);
    }
    const c = this.state.rungCeiling;
    if (c == null) return null;
    // One rung up, never past what the probe said this device decodes smoothly; reaching
    // the top with no probe ceiling means no ceiling at all.
    const up = stepUpRung(c);
    const probe = this.state.probeCeiling;
    let next: number | null = probe != null ? Math.min(up ?? probe, probe) : up;
    if (next === c) return null;
    if (probe == null && next === RUNGS[RUNGS.length - 1]) next = null;
    this.state.rungCeiling = next;
    return this.changed("recover", ratio, s);
  }
}

/**
 * Decode slots: which tiles may decode while a limit is set (the budget's `maxDecoding`,
 * or the start-up ramp — see DisplayBudget). Tiles that want to play queue; the first
 * `max` hold a grant. Who is next in line, in order:
 *
 *   1. a BARE tile (nothing of its own on screen — a poster, or black): nobody should wait
 *      on a poster while tiles with a frame to hold keep playing;
 *   2. the BIGGEST tile: the share of the screen is the share of attention, so the hero
 *      region of a layout plays before a thumbnail strip.
 *
 * A granted tile yields at its clip's end, or after SLOT_SLICE_MS when someone is waiting,
 * and rejoins at the back — so every tile gets turns, the big ones first.
 */
export const SLOT_SLICE_MS = 8000;
export interface SlotCandidate {
  id: string;
  bare: boolean;
  /** Rendered area, any unit; bigger is first. */
  area: number;
}
export class DecodeSlots {
  private queue: string[] = [];
  private grantedAt = new Map<string, number>();

  /** Recompute grants. `wanting` is every tile that would play if allowed. */
  allocate(wanting: SlotCandidate[], max: number | null, now: number): Set<string> {
    const byId = new Map(wanting.map((w) => [w.id, w]));
    this.queue = this.queue.filter((id) => byId.has(id));
    for (const w of wanting) if (!this.queue.includes(w.id)) this.queue.push(w.id);
    for (const id of [...this.grantedAt.keys()]) if (!byId.has(id)) this.grantedAt.delete(id);
    // Holders keep their place; the waiting line is ordered bare-first, then by size. A
    // yielded tile was moved to the back by `yield`, and stays behind the tiles that have
    // not had a turn yet (the stable sort keeps the rest of the line as it was).
    const granted = this.queue.filter((id) => this.grantedAt.has(id));
    const fresh = this.queue.filter((id) => !this.grantedAt.has(id) && !this.yielded.has(id));
    const again = this.queue.filter((id) => !this.grantedAt.has(id) && this.yielded.has(id));
    const rank = (x: string, y: string) => {
      const a = byId.get(x)!;
      const b = byId.get(y)!;
      return Number(b.bare) - Number(a.bare) || b.area - a.area;
    };
    this.queue = [...granted, ...fresh.sort(rank), ...again];
    if (max == null || max >= this.queue.length) {
      for (const id of this.queue) if (!this.grantedAt.has(id)) this.grantedAt.set(id, now);
      this.yielded.clear();
      return new Set(this.queue);
    }
    // Time-sliced: a grant held past the slice while others wait goes to the back.
    if (this.queue.length > max) {
      for (const id of this.queue.slice(0, max)) {
        const at = this.grantedAt.get(id);
        if (at != null && now - at >= SLOT_SLICE_MS) this.yield(id);
      }
    }
    const out = new Set(this.queue.slice(0, Math.max(1, max)));
    for (const id of [...this.grantedAt.keys()]) if (!out.has(id)) this.grantedAt.delete(id);
    for (const id of out) {
      if (!this.grantedAt.has(id)) this.grantedAt.set(id, now);
      this.yielded.delete(id);
    }
    return out;
  }

  /** Tiles that have had a turn and are waiting for another — behind first-timers. */
  private yielded = new Set<string>();

  /** A tile finished its turn (clip ended, or slice up): to the back of the line. */
  yield(id: string): void {
    const i = this.queue.indexOf(id);
    if (i < 0) return;
    this.queue.splice(i, 1);
    this.queue.push(id);
    this.grantedAt.delete(id);
    this.yielded.add(id);
  }
}

/**
 * Resolve a region's leader, refusing anything that is not a straight one-hop follow.
 *
 * Returns null when the region follows nothing, follows itself, follows a region that is
 * gone, or follows another follower. That last one is the important refusal: a chain
 * would need a topological sort to render and a cycle would hang the renderer outright,
 * and neither buys anything a direct follow does not — every follower of one leader is
 * already in sync with every other.
 */
export function syncLeaderOf(region: Region, all: readonly Region[]): Region | null {
  if (!region.syncWith || region.syncWith === region.id) return null;
  const leader = all.find((r) => r.id === region.syncWith);
  if (!leader || leader.syncWith) return null;
  return leader;
}


// ---------------------------------------------------------------------------
// Adaptive Framing — point a cropped tile at what the eye actually lands on.
//
// A region's shape comes from the viewport, so the same clip plays in a 5.5:1
// panorama band and a 0.46:1 column at once. Plain `object-fit: cover` centre-crops
// regardless: at the widest tile that keeps 22% of the source and throws the subject
// off the tile entirely in 44% of sampled frames.
//
// The vision /focal head samples 8 frames and reports, per frame, where attention
// concentrates. Two stages turn that into a crop, split by what each end knows:
//
//   solveFocalShots()  server, once per clip. Which hotspot wins, where the camera
//                      re-frames, how much zoom the content wants. All of it in
//                      normalized coordinates, so it holds for every tile shape.
//   focalCrop()        client, per tile. Maps a shot onto THIS tile's aspect, which
//                      is the one thing the server cannot know.
//
// The constants are not guesses. They were chosen against rendered previews on
// 2026-08-29 and validated on 35 held-out clips: aiming lands on the true peak
// (median heat-at-point 1.00 vs 0.64 for the model's own soft-argmax), zoom runs
// median 1.25 / p90 1.50 / max 1.72 with nothing reaching the cap, and 57% of clips
// resolve to a single static shot.
// ---------------------------------------------------------------------------

/** One strong hotspot on a frame: centre, strength, mass, and the size of its basin. */
export interface FocalMode {
  x: number;
  y: number;
  /** Peak cell value — how hot. */
  s: number;
  /** Probability mass in the neighbourhood — how much agreement sits here. */
  m: number;
  ew: number;
  eh: number;
}

/** One sampled frame of the focal head's output. Normalized, aspect-independent. */
export interface FocalPoint {
  sec: number;
  x: number;
  y: number;
  peak: number;
  /** Linear extent of the hot region connected to the peak, as a frame fraction. */
  ext: number;
  modes: FocalMode[];
}

/**
 * A held shot: `[t0, t1, anchorX, anchorY, zoom]`.
 *
 * A tuple rather than an object because it rides `MediaItem` on the hot path — a wall
 * can have 16 regions pulling 120 clips each, so ~1,900 of these are in flight at once
 * and the key names would cost more than the numbers.
 */
export type FocalShot = [number, number, number, number, number];

/** Tuning for the shot solve. Defaults are the settled recipe; see the section header. */
export interface FocalSolveOpts {
  /** How far the focal point must move to earn a re-frame, as a frame fraction. */
  deadband?: number;
  /** Keep the previous hotspot while it holds this fraction of the best one's strength. */
  stick?: number;
  /** Extent-driven zoom strength. 0 disables it and leaves zoom to edge constraints. */
  gain?: number;
  /** Extra zoom a held shot earns — nothing is going to walk out of frame. */
  holdBonus?: number;
  /** Floor on the fraction of the source frame left visible. */
  minShown?: number;
  /** The "normal" hot-area size to zoom against. */
  refExtent?: number;
  maxZoom?: number;
}

/**
 * Which focal head produced a stored track.
 *
 * Stored per row so a retrain has a todo list — `WHERE model != FOCAL_MODEL` — instead of
 * the choice between re-running the whole library and trusting stale framing. Bump it
 * when the head changes, not when the recipe does: the recipe re-solves off stored
 * points without touching the GPU.
 */
export const FOCAL_MODEL = "clip-b16-focal-2026-08-27";

/** The WD EVA02 Danbooru tagger the producer runs over five frames of every ingest
 *  (vision/wd/). Unlike the facet heads it leaves no single facet to test for — its
 *  output is dozens of keywords, any of which an admin may later delete — so a clip it
 *  has visited gets a row in `clip_wd` (model, rules version, when). That row is the
 *  idempotence gate for the inline hook, the "missing" test for the nightly sweep, and
 *  what lets the import refuse to resurrect a deleted keyword on a re-send. A model
 *  bump changes the string and thereby re-lists the whole catalog, which is the intent.
 *  WD_MARKER is the WIRE spelling only: the producer's manifest carries the visit as a
 *  `wd:<model>` tag and the importer turns it back into a row — it was a hidden keyword
 *  until 2026-09-13, and a keyword is something every tag surface eventually lists.
 *  tools/autotag-sweep.mjs carries a hand-copied literal (it is dependency-free .mjs
 *  for prod); tools/checks/wd-rules.mts keeps the two equal. */
export const WD_MODEL = "wd-eva02-large-tagger-v3";
export const WD_MARKER = `wd:${WD_MODEL}`;

/** Namespaces that never reach a public surface: provenance (`sub:` subreddit, `rg:`
 *  RedGifs feed) and the WD tagger's visit marker. The producer marks these hidden in
 *  the manifest so prod hides them on arrival rather than sweeping later. */
export const HIDDEN_TAG_NAMESPACES = ["sub:", "rg:", "wd:"] as const;
export function isHiddenNamespaceTag(tag: string): boolean {
  return HIDDEN_TAG_NAMESPACES.some((ns) => tag.startsWith(ns));
}

export const FOCAL_DEFAULTS = {
  deadband: 0.2,
  stick: 0.55,
  gain: 0.5,
  holdBonus: 1.0,
  /** Corpus median hot-basin extent. Measured 0.250 on the tuning set AND on 35 held-out
   *  clips — 0% drift, so it is a property of the model's output, not a fitted constant. */
  refExtent: 0.25,
  maxZoom: 1.6,
  /**
   * Never zoom below showing this much of the SOURCE FRAME.
   *
   * The tile's own shape already crops — a 9:16 clip in a 16:9 tile keeps 30% of the
   * frame before this feature does anything — and zoom multiplies on top of that. Without
   * a floor the two compound: measured across 6,550 (clip, tile, shot) combinations, the
   * median tile showed 23% of its frame and the bottom tenth showed 7%, which reads as a
   * closeup of nothing and makes a wall of them dizzying.
   *
   * The goal is the most area that still puts the hotspot prominently in frame, not the
   * tightest crop the hotspot allows. So the zoom this permits is
   * sqrt(coverArea / minShown) — and where the tile crop is ALREADY below the floor,
   * that is 1.0 and the framing pans without zooming at all.
   *
   * The edge constraint can still zoom past this: keeping the subject on screen wins over
   * showing more of a frame it is not in.
   */
  minShown: 0.4,
  /**
   * The band a shot must keep its focal point inside.
   *
   * Loose on purpose. Every shot in this model is HELD, and demanding the action sit in
   * the central 30% asked for windows up to 2.12x wider than the whole frame — which
   * silently pinned every shot to zoom 1.0 and disabled the zoom rule outright.
   */
  holdSafe: 0.75,
  /** How long a re-frame takes. */
  moveMs: 700,
} as const;

/**
 * The hotspot a frame should be cropped to.
 *
 * NOT the model's own `x, y`. That is a soft-argmax — the mean of the whole heatmap —
 * so on a frame with two subjects it lands in the trough between them: measured at a
 * median 0.09x the peak on multi-hotspot frames, which are 31-35% of all frames. The
 * strongest mode is on something real by construction.
 *
 * `prev` biases toward the previously chosen hotspot so the camera does not flap
 * between two subjects that trade dominance frame to frame.
 */
export function focalHotspot(
  point: FocalPoint,
  prev?: { x: number; y: number },
  stick: number = FOCAL_DEFAULTS.stick,
): { x: number; y: number; ext: number } {
  const modes = point.modes ?? [];
  if (!modes.length) return { x: point.x, y: point.y, ext: point.ext };
  let best = modes[0];
  if (prev) {
    let near = modes[0];
    let bestD = Infinity;
    for (const m of modes) {
      const d = Math.hypot(m.x - prev.x, m.y - prev.y);
      if (d < bestD) {
        bestD = d;
        near = m;
      }
    }
    if (near.s >= stick * best.s) best = near;
  }
  return { x: best.x, y: best.y, ext: point.ext };
}

/**
 * Zoom wanted by the size of the hot region, relative to the corpus norm.
 *
 * A tight basin means the salient thing is small, so crop in. A broad one means it is
 * large or the model is spreading its bet, so stay wide. Relative to `ref` rather than
 * an absolute "the hot area should fill X% of the tile" target: the basin is really a
 * CONFIDENCE footprint, not an object box (measured corr(extent, peak) = -0.78), and an
 * absolute target just pins everything to the clamps.
 *
 * Broader than the norm clamps to 1.0 — zoom-out past the cover window is not a thing,
 * so "stay wide" falls out rather than needing a case.
 */
export function focalExtentZoom(
  extent: number,
  ref: number = FOCAL_DEFAULTS.refExtent,
  gain: number = FOCAL_DEFAULTS.gain,
  maxZoom: number = FOCAL_DEFAULTS.maxZoom,
): number {
  // Guarded, not assumed: a clip predating the extent field would otherwise make this
  // NaN, and `NaN || 1` downstream silently reads as "no zoom" — a whole feature quietly
  // doing nothing while every check still passes.
  if (!gain || !Number.isFinite(extent) || extent <= 0 || !Number.isFinite(ref)) return 1;
  const z = 1 + (ref / extent - 1) * gain;
  return Number.isFinite(z) ? Math.min(maxZoom, Math.max(1, z)) : 1;
}

/**
 * Turn a clip's sampled points into held shots.
 *
 * A locked-off camera that re-frames deliberately reads far better than one that chases
 * every sample — chasing produces a constant micro-drift that looks like a wobble rather
 * than a decision. So a new shot starts only when the hotspot leaves `deadband` of the
 * running anchor, and the anchor is the MEAN of the shot's hotspots so far: a stable
 * target for a held frame rather than a per-sample one.
 *
 * Aspect-independent by construction, which is what lets one solve serve every tile.
 */
export function solveFocalShots(points: FocalPoint[], opts: FocalSolveOpts = {}): FocalShot[] {
  const o = { ...FOCAL_DEFAULTS, ...opts };
  const pts = [...(points ?? [])].sort((a, b) => a.sec - b.sec);
  if (!pts.length) return [];

  const frames: Array<{ from: number; to: number; cx: number; cy: number; zoom: number }> = [];
  const chosen: Array<{ x: number; y: number; ext: number; sec: number }> = [];
  let prev: { x: number; y: number } | undefined;
  for (const p of pts) {
    const h = focalHotspot(p, prev, o.stick);
    chosen.push({ ...h, sec: p.sec });
    prev = h;
  }

  const groups: Array<typeof chosen> = [];
  let group: typeof chosen = [chosen[0]];
  let ax = chosen[0].x;
  let ay = chosen[0].y;
  const flush = () => {
    const cx = group.reduce((a, c) => a + c.x, 0) / group.length;
    const cy = group.reduce((a, c) => a + c.y, 0) / group.length;
    // 40th percentile, not the mean: a mean drags every long shot back toward the corpus
    // norm, so a shot whose hotspots are mostly tight would never read as tight.
    const ext = percentile40(group.map((c) => c.ext));
    // Every shot here is static, so each earns the hold bonus — a lone unpaired sample
    // is not really "held" and gets none.
    const bonus = group.length >= 2 ? o.holdBonus : 1;
    const zoom = Math.min(
      o.maxZoom,
      focalExtentZoom(ext, o.refExtent, o.gain, o.maxZoom) * bonus,
    );
    frames.push({ from: group[0].sec, to: group[group.length - 1].sec, cx, cy, zoom });
  };
  for (let i = 1; i < chosen.length; i++) {
    const c = chosen[i];
    if (Math.hypot(c.x - ax, c.y - ay) > o.deadband) {
      flush();
      groups.push(group);
      group = [c];
      ax = c.x;
      ay = c.y;
    } else {
      group.push(c);
      ax = group.reduce((a, g) => a + g.x, 0) / group.length;
      ay = group.reduce((a, g) => a + g.y, 0) / group.length;
    }
  }
  flush();
  groups.push(group);

  // Shots must TILE the timeline, not just mark their own samples. A shot built from one
  // sample would otherwise span zero seconds, leaving gaps a playhead falls into — so a
  // shot runs from where it was triggered until the next one takes over, and the first
  // one starts at zero because a clip is framed from its first frame, not its first
  // sample (the sampler skips the leading 8%).
  return frames.map((f, i) => [
    round4(i === 0 ? 0 : f.from),
    round4(i === frames.length - 1 ? f.to : frames[i + 1].from),
    round4(f.cx),
    round4(f.cy),
    round4(f.zoom),
  ] as FocalShot);
}

const round4 = (n: number): number => Math.round(n * 1e4) / 1e4;

/**
 * The 40th percentile, linearly interpolated — numpy's `percentile` default.
 *
 * Matched deliberately rather than approximated: the recipe was tuned and validated
 * against numpy, so a nearest-rank shortcut here is a silent behaviour change. It cost
 * up to 0.06 of zoom and 23px of framing before the parity fixture caught it.
 */
function percentile40(values: number[]): number {
  const v = [...values].sort((a, b) => a - b);
  if (v.length === 1) return v[0];
  const idx = 0.4 * (v.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  return v[lo] + (idx - lo) * (v[hi] - v[lo]);
}

/**
 * The crop a tile of this aspect should show for a shot, as a CSS transform.
 *
 * Composes on top of `object-fit: cover`, which already puts the largest tile-shaped
 * rect at the centre of the frame — so scale 1 with no translate IS today's behaviour,
 * and everything here is a departure from it measured in the same units.
 *
 * Returns percentages of the element box, so it survives any tile size without
 * remeasuring: `transform: translate(x%, y%) scale(s)`.
 */
export function focalCrop(
  shot: FocalShot,
  clipW: number,
  clipH: number,
  tileAR: number,
  opts: { safe?: number; maxZoom?: number; minShown?: number } = {},
): { scale: number; xPct: number; yPct: number; posX: number; posY: number } {
  const safe = opts.safe ?? FOCAL_DEFAULTS.holdSafe;
  const maxZoom = opts.maxZoom ?? FOCAL_DEFAULTS.maxZoom;
  // Dead centre, no zoom: byte-for-byte what `object-fit: cover` already does.
  const none = { scale: 1, xPct: 0, yPct: 0, posX: 50, posY: 50 };
  if (!(clipW > 0) || !(clipH > 0) || !(tileAR > 0)) return none;

  const [, , ax, ay, wantZoom] = shot;
  // The cover window: the largest tile-shaped rect inside the frame, centred.
  const coverW = clipW / clipH > tileAR ? clipH * tileAR : clipW;
  const coverH = coverW / tileAR;
  // How much of the frame the tile's own shape already keeps. A 9:16 clip in a 16:9 tile
  // is at 0.30 before this feature does anything, so there is nothing left to spend.
  const minShown = opts.minShown ?? FOCAL_DEFAULTS.minShown;
  const coverFrac = (coverW * coverH) / (clipW * clipH);
  const areaCap = coverFrac > minShown ? Math.sqrt(coverFrac / minShown) : 1;
  const px = ax * clipW;
  const py = ay * clipH;

  // A point near an edge cannot be centred without the window leaving the frame, so the
  // window must SHRINK until the edge is close enough. That is a ceiling on width; the
  // extent zoom is a target within it.
  const m = (1 - safe) / 2;
  const edge = Math.min(px / m, (clipW - px) / m, (py / m) * tileAR, ((clipH - py) / m) * tileAR);
  const upper = Math.min(coverW, Math.max(edge, coverW / maxZoom));
  const target = coverW / Math.min(maxZoom, areaCap, Math.max(1, wantZoom || 1));
  const w = Math.min(upper, Math.max(coverW / maxZoom, target));
  const h = w / tileAR;
  const z = coverW / w;

  // Centre on the anchor, then clamp so the window never runs off the frame.
  const cx = Math.min(Math.max(px, w / 2), clipW - w / 2);
  const cy = Math.min(Math.max(py, h / 2), clipH - h / 2);

  /*
   * TWO mechanisms, because one cannot do it.
   *
   * `object-position` pans the CONTENT inside the element — that is what it is for, and
   * it is the only one that works at zoom 1. A transform moves the ELEMENT, and the
   * element IS the tile: translating it at zoom 1 slides the tile off its own container
   * and shows the background through the gap. (It did.)
   *
   * But object-position can only slide across the source's own overflow, which at zoom z
   * leaves the window centre short of the frame edges by exactly coverW*(1-1/z)/2. That
   * shortfall is precisely the overhang `scale(z)` creates, so a bounded translate covers
   * it — and being bounded by the overhang is what makes exposing background impossible.
   */
  const pan = (c: number, span: number, cover: number): { pos: number; pct: number } => {
    const overflow = span - cover;
    // Where object-position can put the window centre, and how close that gets.
    const pos = overflow > 0 ? Math.min(1, Math.max(0, (c - cover / 2) / overflow)) : 0;
    const residual = c - (pos * overflow + cover / 2);
    // As a fraction of the element box. Negative moves the image left to reveal content
    // to its right. Clamped to the overhang: past it, black.
    const overhang = (z - 1) / 2;
    const raw = (-residual * z) / cover;
    return { pos: overflow > 0 ? pos * 100 : 50, pct: Math.min(overhang, Math.max(-overhang, raw)) * 100 };
  };
  const x = pan(cx, clipW, coverW);
  const y = pan(cy, clipH, coverH);

  return {
    scale: round4(z),
    xPct: round4(x.pct),
    yPct: round4(y.pct),
    posX: round4(x.pos),
    posY: round4(y.pos),
  };
}

/** The shot covering `sec`, or the nearest one at the ends. */
export function focalShotAt(shots: readonly FocalShot[], sec: number): FocalShot | null {
  if (!shots?.length) return null;
  for (const s of shots) if (sec >= s[0] && sec <= s[1]) return s;
  if (sec < shots[0][0]) return shots[0];
  return shots[shots.length - 1];
}

export type DeviceKind = "monitor" | "phone" | "tablet" | "other";

/** Longest icon we'll store, in code points. A ZWJ sequence like 👨‍👩‍👧‍👦 is 7. */
export const MONITOR_ICON_MAX_CODEPOINTS = 8;

/**
 * Normalize a user-supplied monitor icon: trim, drop if empty, and reject
 * anything longer than a single (possibly composed) emoji. Returns undefined
 * to mean "fall back to the device glyph", which is also the stored absence.
 */
export function sanitizeMonitorIcon(raw: string | undefined | null): string | undefined {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return undefined;
  return [...trimmed].length <= MONITOR_ICON_MAX_CODEPOINTS ? trimmed : undefined;
}

// ---------------------------------------------------------------------------
// Layout presets — generate region geometry for a monitor.
//   *-vertical   => N vertical strips (columns), each full height.
//   *-horizontal => N horizontal strips (rows), each full width.
//   grid-RxC     => R rows by C columns.
// ---------------------------------------------------------------------------

export type LayoutPresetId =
  | "single"
  | "2-vertical"
  | "3-vertical"
  | "2-top-1-bottom"
  | "1-left-2-right"
  | "2-left-1-right"
  | "1-top-3-bottom"
  | "3-top-1-bottom"
  | "grid-2x2"
  | "grid-3x3"
  | "grid-2x3"
  | "grid-3x4"
  | "grid-4x4"
  | "hero-rails"
  | "2x2-hero-2x2"
  | "4-vertical"
  | "2-horizontal"
  | "3-horizontal"
  | "grid-2x4"
  | "grid-3x2"
  | "grid-4x3"
  | "hero-left-rail"
  | "filmstrip"
  | "pip-corners"
  | "center-stage"
  | "twin-towers"
  | "hero-6-pack"
  | "l-shape"
  | "big-little-big"
  | "panorama"
  | "hero-wall"
  | "mosaic"
  | "billboard";

// Deliberately shuffled (one-time, 2026-08-27): a simple→complex ordering front-loaded
// every page with the boring splits and hid the walls behind the pager. This order is
// what the palette shows, page by page — each page mixes small splits, heroes and Gold
// walls on purpose. Reorder here to re-curate, nothing sorts it.
export const LAYOUT_PRESETS: ReadonlyArray<{ id: LayoutPresetId; label: string }> = [
  { id: "grid-2x2", label: "Grid 2×2" },
  { id: "filmstrip", label: "Filmstrip" },
  { id: "2-vertical", label: "2 Vertical" },
  { id: "hero-rails", label: "3 / hero / 3" },
  { id: "grid-3x4", label: "Grid 3×4" },
  { id: "panorama", label: "Panorama" },
  { id: "1-left-2-right", label: "1 left / 2 right" },
  { id: "center-stage", label: "Center Stage" },
  { id: "billboard", label: "Billboard" },
  { id: "2x2-hero-2x2", label: "2×2 / hero / 2×2" },
  { id: "3-vertical", label: "3 Vertical" },
  { id: "hero-left-rail", label: "Hero + Rail" },
  { id: "grid-2x4", label: "Grid 2×4" },
  { id: "l-shape", label: "L-Shape" },
  { id: "single", label: "Single" },
  { id: "twin-towers", label: "Twin Towers" },
  { id: "grid-4x4", label: "Grid 4×4" },
  { id: "pip-corners", label: "Hero + PiP" },
  { id: "grid-3x2", label: "Grid 3×2" },
  { id: "2-top-1-bottom", label: "2 top / 1 bottom" },
  { id: "hero-wall", label: "Hero Wall" },
  { id: "4-vertical", label: "4 Vertical" },
  { id: "big-little-big", label: "Big / 2 / Big" },
  { id: "mosaic", label: "Mosaic" },
  { id: "2-horizontal", label: "2 Stacked" },
  { id: "hero-6-pack", label: "Hero + 6 Pack" },
  { id: "grid-2x3", label: "Grid 2×3" },
  { id: "3-top-1-bottom", label: "3 top / 1 bottom" },
  { id: "grid-4x3", label: "Grid 4×3" },
  { id: "3-horizontal", label: "3 Stacked" },
  { id: "1-top-3-bottom", label: "1 top / 3 bottom" },
  { id: "2-left-1-right", label: "2 left / 1 right" },
  { id: "grid-3x3", label: "Grid 3×3" },
];

/** Returns the normalized rects for a preset (geometry only — no source/timer). */
export function presetRects(preset: LayoutPresetId): Rect[] {
  const strips = (n: number, vertical: boolean): Rect[] =>
    Array.from({ length: n }, (_, i) =>
      vertical
        ? { x: i / n, y: 0, w: 1 / n, h: 1 }
        : { x: 0, y: i / n, w: 1, h: 1 / n },
    );
  const grid = (rows: number, cols: number): Rect[] => {
    const out: Rect[] = [];
    for (let r = 0; r < rows; r++)
      for (let c = 0; c < cols; c++)
        out.push({ x: c / cols, y: r / rows, w: 1 / cols, h: 1 / rows });
    return out;
  };
  switch (preset) {
    case "single":
      return [{ x: 0, y: 0, w: 1, h: 1 }];
    case "2-vertical":
      return strips(2, true);
    case "3-vertical":
      return strips(3, true);
    case "2-top-1-bottom":
      return [
        { x: 0, y: 0, w: 1 / 2, h: 1 / 2 },
        { x: 1 / 2, y: 0, w: 1 / 2, h: 1 / 2 },
        { x: 0, y: 1 / 2, w: 1, h: 1 / 2 },
      ];
    case "1-left-2-right":
      return [
        { x: 0, y: 0, w: 1 / 2, h: 1 },
        { x: 1 / 2, y: 0, w: 1 / 2, h: 1 / 2 },
        { x: 1 / 2, y: 1 / 2, w: 1 / 2, h: 1 / 2 },
      ];
    case "2-left-1-right":
      return [
        { x: 0, y: 0, w: 1 / 2, h: 1 / 2 },
        { x: 0, y: 1 / 2, w: 1 / 2, h: 1 / 2 },
        { x: 1 / 2, y: 0, w: 1 / 2, h: 1 },
      ];
    case "1-top-3-bottom":
      return [
        { x: 0, y: 0, w: 1, h: 1 / 2 },
        { x: 0, y: 1 / 2, w: 1 / 3, h: 1 / 2 },
        { x: 1 / 3, y: 1 / 2, w: 1 / 3, h: 1 / 2 },
        { x: 2 / 3, y: 1 / 2, w: 1 / 3, h: 1 / 2 },
      ];
    case "3-top-1-bottom":
      return [
        { x: 0, y: 0, w: 1 / 3, h: 1 / 2 },
        { x: 1 / 3, y: 0, w: 1 / 3, h: 1 / 2 },
        { x: 2 / 3, y: 0, w: 1 / 3, h: 1 / 2 },
        { x: 0, y: 1 / 2, w: 1, h: 1 / 2 },
      ];
    case "grid-2x2":
      return grid(2, 2);
    case "grid-2x3":
      return grid(2, 3);
    case "grid-3x3":
      return grid(3, 3);
    case "grid-3x4":
      return grid(3, 4);
    case "grid-4x4":
      return grid(4, 4);
    case "hero-rails": {
      // A stacked rail of 3, a double-width hero, then another rail of 3.
      // Four columns wide; the hero spans the middle two, full height.
      const rail = (x: number): Rect[] =>
        Array.from({ length: 3 }, (_, i) => ({ x, y: i / 3, w: 1 / 4, h: 1 / 3 }));
      return [
        ...rail(0),
        { x: 1 / 4, y: 0, w: 1 / 2, h: 1 },
        ...rail(3 / 4),
      ];
    }
    case "2x2-hero-2x2": {
      // A 2×2 block each side of one hero. Thirds wide: the hero takes the middle
      // third, each block fills its own third with sixth-width half-height cells.
      const block = (x: number): Rect[] => [
        { x, y: 0, w: 1 / 6, h: 1 / 2 },
        { x: x + 1 / 6, y: 0, w: 1 / 6, h: 1 / 2 },
        { x, y: 1 / 2, w: 1 / 6, h: 1 / 2 },
        { x: x + 1 / 6, y: 1 / 2, w: 1 / 6, h: 1 / 2 },
      ];
      return [...block(0), { x: 1 / 3, y: 0, w: 1 / 3, h: 1 }, ...block(2 / 3)];
    }
    case "4-vertical":
      return strips(4, true);
    case "2-horizontal":
      return strips(2, false);
    case "3-horizontal":
      return strips(3, false);
    case "grid-2x4":
      return grid(2, 4);
    case "grid-3x2":
      return grid(3, 2);
    case "grid-4x3":
      return grid(4, 3);
    case "hero-left-rail":
      // Three-quarter hero, a four-stack rail down the right edge.
      return [
        { x: 0, y: 0, w: 3 / 4, h: 1 },
        ...Array.from({ length: 4 }, (_, i) => ({ x: 3 / 4, y: i / 4, w: 1 / 4, h: 1 / 4 })),
      ];
    case "filmstrip":
      // Hero on top, a strip of four thumbnails along the bottom edge.
      return [
        { x: 0, y: 0, w: 1, h: 3 / 4 },
        ...Array.from({ length: 4 }, (_, i) => ({ x: i / 4, y: 3 / 4, w: 1 / 4, h: 1 / 4 })),
      ];
    case "pip-corners":
      // Full-bleed hero UNDER four picture-in-picture corners — regions may overlap,
      // and this is the layout that uses it. Quarter-size PiPs, inset one twelfth.
      return [
        { x: 0, y: 0, w: 1, h: 1 },
        { x: 1 / 12, y: 1 / 12, w: 1 / 4, h: 1 / 4 },
        { x: 2 / 3, y: 1 / 12, w: 1 / 4, h: 1 / 4 },
        { x: 1 / 12, y: 2 / 3, w: 1 / 4, h: 1 / 4 },
        { x: 2 / 3, y: 2 / 3, w: 1 / 4, h: 1 / 4 },
      ];
    case "center-stage":
      // A half-size stage ringed by eight cells: corners, then edge slabs.
      return [
        { x: 1 / 4, y: 1 / 4, w: 1 / 2, h: 1 / 2 },
        { x: 0, y: 0, w: 1 / 4, h: 1 / 4 },
        { x: 3 / 4, y: 0, w: 1 / 4, h: 1 / 4 },
        { x: 0, y: 3 / 4, w: 1 / 4, h: 1 / 4 },
        { x: 3 / 4, y: 3 / 4, w: 1 / 4, h: 1 / 4 },
        { x: 1 / 4, y: 0, w: 1 / 2, h: 1 / 4 },
        { x: 1 / 4, y: 3 / 4, w: 1 / 2, h: 1 / 4 },
        { x: 0, y: 1 / 4, w: 1 / 4, h: 1 / 2 },
        { x: 3 / 4, y: 1 / 4, w: 1 / 4, h: 1 / 2 },
      ];
    case "twin-towers":
      // Two full-height towers with a three-stack spine between them.
      return [
        { x: 0, y: 0, w: 1 / 3, h: 1 },
        { x: 2 / 3, y: 0, w: 1 / 3, h: 1 },
        ...Array.from({ length: 3 }, (_, i) => ({ x: 1 / 3, y: i / 3, w: 1 / 3, h: 1 / 3 })),
      ];
    case "hero-6-pack":
      // Two-thirds hero, the last third a 2-wide 3-tall pack of cells.
      return [
        { x: 0, y: 0, w: 2 / 3, h: 1 },
        ...[0, 1].flatMap((c) =>
          [0, 1, 2].map((r) => ({ x: 2 / 3 + c / 6, y: r / 3, w: 1 / 6, h: 1 / 3 })),
        ),
      ];
    case "l-shape":
      // A two-thirds square with cells wrapping its right and bottom edges.
      return [
        { x: 0, y: 0, w: 2 / 3, h: 2 / 3 },
        { x: 2 / 3, y: 0, w: 1 / 3, h: 1 / 3 },
        { x: 2 / 3, y: 1 / 3, w: 1 / 3, h: 1 / 3 },
        { x: 0, y: 2 / 3, w: 1 / 3, h: 1 / 3 },
        { x: 1 / 3, y: 2 / 3, w: 1 / 3, h: 1 / 3 },
        { x: 2 / 3, y: 2 / 3, w: 1 / 3, h: 1 / 3 },
      ];
    case "big-little-big":
      // Full-height columns flanking a split middle.
      return [
        { x: 0, y: 0, w: 1 / 3, h: 1 },
        { x: 2 / 3, y: 0, w: 1 / 3, h: 1 },
        { x: 1 / 3, y: 0, w: 1 / 3, h: 1 / 2 },
        { x: 1 / 3, y: 1 / 2, w: 1 / 3, h: 1 / 2 },
      ];
    case "panorama":
      // Wide bands top and bottom, three columns through the middle.
      return [
        { x: 0, y: 0, w: 1, h: 1 / 3 },
        ...Array.from({ length: 3 }, (_, i) => ({ x: i / 3, y: 1 / 3, w: 1 / 3, h: 1 / 3 })),
        { x: 0, y: 2 / 3, w: 1, h: 1 / 3 },
      ];
    case "hero-wall":
      // Half-size hero top-left, quarter cells walling its two open sides.
      return [
        { x: 0, y: 0, w: 1 / 2, h: 1 / 2 },
        ...[0, 1].flatMap((c) =>
          [0, 1].map((r) => ({ x: 1 / 2 + c / 4, y: r / 4, w: 1 / 4, h: 1 / 4 })),
        ),
        ...Array.from({ length: 4 }, (_, i) => ({ x: i / 4, y: 1 / 2, w: 1 / 4, h: 1 / 2 })),
      ];
    case "mosaic":
      // Two heroes over an eight-cell floor.
      return [
        { x: 0, y: 0, w: 1 / 2, h: 1 / 2 },
        { x: 1 / 2, y: 0, w: 1 / 2, h: 1 / 2 },
        ...[0, 1].flatMap((r) =>
          [0, 1, 2, 3].map((c) => ({ x: c / 4, y: 1 / 2 + r / 4, w: 1 / 4, h: 1 / 4 })),
        ),
      ];
    case "billboard":
      // The Community Wall's board: 1 marquee + 4 medium + 6 small, eleven in all.
      //
      //   ┌───────────────┬───────┬───────┐
      //   │               │  med  │  med  │
      //   │    MARQUEE    ├───────┼───────┤
      //   │               │  med  │  med  │
      //   ├─────┬─────┬───┴─┬─────┼─────┬─┤
      //   │  s  │  s  │  s  │  s  │  s  │s│   floor: ONE ROW of six portrait columns,
      //   └─────┴─────┴─────┴─────┴─────┴─┘   full width
      //
      // The floor is portrait on purpose — the operator's call, arrived at after a
      // landscape split and a split left/right floor were both tried and rejected: tall
      // slots read as a strip of posters under the billboard.
      //
      // Twelfths everywhere, because snap-grid.mts requires every preset edge to land on
      // the editor's 12-grid: sixths and quarters are twelfths, eighths are not.
      //
      // THE ORDER IS LOAD-BEARING beyond this palette: WALL_REGIONS derives ids AND price
      // tiers from these indices (0 = marquee, 1-4 medium, 5-10 small), and a wall region
      // id renames a square somebody BOUGHT — so reorder every other preset freely, never
      // this one.
      return [
        { x: 0, y: 0, w: 1 / 2, h: 2 / 3 },
        ...[0, 1, 2, 3].map((i) => ({
          x: 1 / 2 + (i % 2) / 4,
          y: Math.floor(i / 2) / 3,
          w: 1 / 4,
          h: 1 / 3,
        })),
        ...Array.from({ length: 6 }, (_, i) => ({ x: i / 6, y: 2 / 3, w: 1 / 6, h: 1 / 3 })),
      ];
    default: {
      // Unreachable for a well-typed id — assigning to `never` is what keeps the
      // switch exhaustive as presets are added. At runtime a retired id (say a
      // `grid-3x2` from an old link) lands here, and one full-bleed tile beats
      // returning undefined for every caller to trip over.
      const _exhaustive: never = preset;
      void _exhaustive;
      return [{ x: 0, y: 0, w: 1, h: 1 }];
    }
  }
}

// ---------------------------------------------------------------------------
// Factory helpers (shared so client + server build identical objects)
// ---------------------------------------------------------------------------

export function makeId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return "id-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function defaultSource(): SourceConfig {
  // New regions default to random-from-catalog (all local/cached, reliable).
  return { kind: "random" };
}

export function regionFromRect(rect: Rect): Region {
  return {
    id: makeId(),
    ...rect,
    source: defaultSource(),
    fit: "cover",
  };
}

// ---------------------------------------------------------------------------
// Relayout — apply a layout preset while PRESERVING existing region config.
// ---------------------------------------------------------------------------

/**
 * Re-geometry `existing` regions onto a layout preset without wiping their
 * feeds: region *i* keeps its source/timer/fit/name/id and just adopts
 * the new rect. Extra rects get fresh default regions; regions beyond the new
 * layout's tile count are dropped (their config is unavoidably lost) — and any
 * follow whose leader was among the dropped is cleared, because a dangling
 * syncWith renders as unsynced while the editor still claims the link.
 */
export function relayoutRegions(existing: Region[], preset: LayoutPresetId): Region[] {
  const next = presetRects(preset).map((rect, i) => {
    const prev = existing[i];
    return prev ? { ...prev, ...rect } : regionFromRect(rect);
  });
  const kept = new Set(next.map((r) => r.id));
  return next.map((r) =>
    r.syncWith && !kept.has(r.syncWith)
      ? { ...r, syncWith: undefined, syncMode: undefined }
      : r,
  );
}

/**
 * Mirror every region across the diagonal: columns become rows, hero-left becomes
 * hero-top. A transposed partition of the unit square is still a partition, and
 * twelfth-grid rects stay on twelfths — which is why this is the WHOLE of adaptive
 * layout: one reflection, no relayout engine.
 *
 * Region objects keep their identity — same id, source, fit, sync fields — because
 * everything downstream of geometry is id-keyed: `syncLeaderOf`, the display's
 * `key={region.id}` (a new id would remount every tile mid-play), command targeting.
 * Applied at RENDER TIME on a display whose orientation disagrees with the view's
 * authored aspect; never persisted — the authored rects are the only truth.
 */
export function transposeRegions(regions: Region[]): Region[] {
  return regions.map((r) => ({ ...r, x: r.y, y: r.x, w: r.h, h: r.w }));
}

// ---------------------------------------------------------------------------
// Monitor templates — one-tap "quick setups" (a layout + per-region feeds).
// Portable: a feed is either a concrete SourceConfig, or a reference resolved
// on the client against the available collections (with a portable fallback so
// it always produces *something* even on a fresh database).
// ---------------------------------------------------------------------------

export type TemplateFeed =
  /** A concrete, always-portable source (random / keywords / …). */
  | { kind: "source"; source: SourceConfig }
  /** Resolve to a collection matched by (case-insensitive) name, else fallback. */
  | { kind: "collectionByName"; name: string; fallback: SourceConfig }
  /** Resolve to the Nth Editor's-Choice collection, else fallback. */
  | { kind: "editorsChoice"; index: number; fallback: SourceConfig };

export interface ViewTemplate {
  id: string;
  name: string;
  emoji: string;
  layout: LayoutPresetId;
  /** One feed per region, applied by index and cycled if shorter than the layout. */
  feeds: TemplateFeed[];
  /**
   * Which regions play in step, as a group number per region index.
   *
   * The FIRST region in a group leads and the rest follow it, so a group of six costs one
   * download and one decode rather than six. Regions with no entry (or a group of their
   * own) play independently.
   *
   * A group number rather than a leader index because the leader is an implementation
   * detail of the group — "these three are together" is the thing being declared, and
   * naming one of them the leader in the data invites it to disagree with the code that
   * picks one.
   */
  syncGroups?: readonly number[];
  /**
   * Group numbers (from `syncGroups`) whose followers MIRROR the leader instead of
   * showing the identical frame — the leader plays, the follower is flipped across the
   * vertical axis. For symmetric lockups like two towers facing each other.
   */
  syncFlip?: readonly number[];
  /**
   * Monitor shape this setup is built for. Omitted means 16:9, which every built-in now
   * takes: two of them declared 9:16 on the reasoning that portrait lanes want a portrait
   * screen, and that was wrong about where these are used. A quick setup goes on a wall
   * monitor, and a portrait view on a landscape screen pillarboxes to a sliver of its
   * width — the lanes inside it are portrait either way, which was the point.
   */
  aspect?: { w: number; h: number };
  /**
   * Ship the built view with adaptive layout on: a screen whose orientation disagrees
   * with the authored aspect transposes the rects at render time (columns become
   * rows). Set on the templates where that genuinely helps — the column lockups and
   * the hero-rail — and left off where the transpose is a no-op (single, square
   * grids) or reads oddly (the checkerboard).
   */
  adaptiveLayout?: true;
  /**
   * Approved product copy beyond the name, where a setup has it: the gallery tile's one
   * line and the shelf / remote line. Data only today — no surface renders per-setup copy
   * yet — kept on the template so the words ship with the setup they describe.
   */
  copy?: { tile: string; shelf: string };
}

/**
 * The playlist id of a HAND-REVIEWED collection: a fixed list of clip ids, checked by eye,
 * that lives in the repo (server/src/sources/playlist.ts, REVIEWED_COLLECTIONS) rather
 * than as a playlist row. Resolved by the same `playlist` source every collection plays
 * through, beside the other virtual ids (`faps:<uid>`, `uploads:<uid>`). A user playlist
 * can never take one of these ids — playlist ids are minted by the server, never colon'd.
 */
export const REVIEWED_COLLECTION_PREFIX = "reviewed:";
export const FULLY_CLOTHED_COLLECTION_ID = `${REVIEWED_COLLECTION_PREFIX}fully-clothed`;

/** One OR-group of keywords: a feed matching any of them. */
const tKw = (...keywords: string[]): SourceConfig => ({
  kind: "keywords",
  groups: [keywords],
});

const tSrc = (source: SourceConfig): TemplateFeed => ({ kind: "source", source });
// (tCol — the collection-by-name feed helper — lost its last caller when Booty × Boobs
// moved to the focus facet. The `collectionByName` TemplateFeed kind and resolveFeed
// still exist for saved views and future templates; only the sugar went.)

/**
 * Built-in quick-setup templates offered in the simple editor.
 *
 * Every keyword here is a real catalog tag. That needs saying because it wasn't true
 * before: the facet migration renamed `latina` to `race:latina`, and several of these
 * fell back to keywords like `booty`, `tits` and `solo` that the catalog never had —
 * a setup that silently played nothing.
 */
const ALL_MONITOR_TEMPLATES: ReadonlyArray<ViewTemplate> = [
  /*
   * EVERY SETUP HAS AN AUDIBLE PANEL (2026-08-25): the most central (or largest, or on
   * an all-equal grid a designated) panel requires `audio:audible`, so a wall started
   * from any quick setup has sound to unmute. On synced setups the constraint rides the
   * LEADER — a follower's feed is ignored. Pools measured on prod before committing
   * (the thin ones are called out inline); audio-kept.mts asserts the invariant.
   */
  {
    id: "booty-boobs",
    name: "Booty × Boobs",
    emoji: "🍑",
    adaptiveLayout: true,
    layout: "2-vertical",
    // The focus head's first product placement (2026-08-25): the setup finally plays
    // its own name — clips FRAMED on ass left, FRAMED on tits right, instead of a
    // collection lookup and a breast-size union. The tits panel keeps the audible
    // duty (146 clips clear both on prod; ass alone is 519).
    feeds: [
      tSrc({ kind: "keywords", groups: [["focus:ass"]] }),
      tSrc({ kind: "keywords", groups: [["focus:tits"], ["audio:audible"]] }),
    ],
  },
  {
    id: "tit-ladder",
    name: "Tit Ladder",
    emoji: "🪜",
    adaptiveLayout: true,
    layout: "grid-3x4",
    // grid-3x4 is row-major over 4 columns, and feeds cycle, so a 4-feed list lands
    // one size per COLUMN: index c and c+4 and c+8 all take feeds[c]. The medium
    // column — nearest the middle of the ladder — is the audible one. Every column
    // also demands focus:tits (2026-08-26): a ladder of sizes only reads as one when
    // the framing is on what's being sized. Pools before the catalog-wide backfill:
    // small 41 · medium+audible 63 · large 175 · huge 5 — huge is thin until the
    // sweep lands, and a 3-tile column tolerates it.
    feeds: [
      tSrc({ kind: "keywords", groups: [["tits:small"], ["focus:tits"]] }),
      tSrc({ kind: "keywords", groups: [["tits:medium"], ["focus:tits"], ["audio:audible"]] }),
      tSrc({ kind: "keywords", groups: [["tits:large"], ["focus:tits"]] }),
      tSrc({ kind: "keywords", groups: [["tits:huge"], ["focus:tits"]] }),
    ],
  },
  {
    id: "latinas",
    name: "Latinas ×3",
    emoji: "💃",
    adaptiveLayout: true,
    layout: "3-vertical",
    /*
     * One woman, latina, 720p or better — four conditions, all of them required.
     *
     * Groups AND together while the values inside a group OR (see clipsByKeywordGroups),
     * so the quality group is how "720p and above" is said: the facet's vocabulary is a
     * fixed list, and this names the top of it rather than expressing a comparison the
     * tag store cannot make.
     *
     * That list is 1080p/720p/480p/360p as of 2026-08-11, so "the top" is two values, not
     * three. This asked for `quality:1440p` as well, written against a six-value
     * vocabulary that included it — but no master can fill a 1440p bucket (the cap is
     * MASTER_MAX_SHORT_EDGE, and the largest short side on disk is 1124), so the tag was
     * removed along with every clip that wore it. Listing it here cost nothing and
     * matched nothing, which is the failure mode worth naming: a keyword group cannot
     * report that one of its values is dead, it just quietly contributes zero.
     *
     * `actor_count:1` is doing more work than it looks. sex and race are only tagged when
     * the actor head sees exactly one person, so asking for a solo latina woman is asking
     * for the case those labels are meaningful in at all — without it, `sex:female` on a
     * two-person clip means "at least one of them", which is not what three portrait lanes
     * of a single subject are for.
     *
     * Was a `tCol("Latinas", ...)` lookup, preferring a hand-curated collection of that
     * name and falling back to the tag. Now stated directly: the point of the preset is
     * this filter, and routing through a collection would have meant the filter only
     * applied when nobody had made one.
     *
     * Measured on prod before committing to it, because a preset that matches nothing
     * plays nothing and says so quietly: 1,333 clips are race:latina, 736 of those are
     * solo, 723 female, and 696 survive the quality floor.
     *
     * Re-measured after the quality facet was corrected — the first pass read 1,358 /
     * 758 / 740 / 700 against tags that were 45% wrong, so the agreement is worth an
     * explanation rather than a shrug. The correction moved 2,308 clips from 1080p to
     * 720p, and this group accepts both, so almost nothing crossed the floor; the small
     * drop is the 234 sub-360 clips deleted in the same pass, not a re-tagging effect.
     * A preset whose filter straddled the boundary would not have been so lucky.
     */
    // Three lanes, written out so the MIDDLE one — the central panel — can also require
    // audio:audible (514 clips clear all five conditions; measured before committing).
    feeds: [
      tSrc({
        kind: "keywords",
        groups: [
          ["actor_count:1"],
          ["race:latina"],
          ["sex:female"],
          ["quality:720p", "quality:1080p"],
        ],
      }),
      tSrc({
        kind: "keywords",
        groups: [
          ["actor_count:1"],
          ["race:latina"],
          ["sex:female"],
          ["quality:720p", "quality:1080p"],
          ["audio:audible"],
        ],
      }),
      tSrc({
        kind: "keywords",
        groups: [
          ["actor_count:1"],
          ["race:latina"],
          ["sex:female"],
          ["quality:720p", "quality:1080p"],
        ],
      }),
    ],
  },
  {
    id: "around-the-world",
    name: "Around the World",
    emoji: "🌍",
    layout: "grid-2x2",
    // All four tiles are equal, so the audible duty is a designation rather than a
    // geometry: the first tile carries it (1,866 audible white clips).
    feeds: [
      tSrc({ kind: "keywords", groups: [["race:white"], ["audio:audible"]] }),
      tSrc(tKw("race:black")),
      tSrc(tKw("race:asian")),
      tSrc(tKw("race:latina")),
    ],
  },
  {
    id: "checkers",
    name: "Checkers",
    emoji: "🏁",
    layout: "grid-3x4",
    /*
     * A checkerboard of two feeds, and the whole board is two clips.
     *
     * The feed list is written out per-tile rather than relying on the cycling every
     * other template uses. Cycling cannot express a checkerboard on a four-wide grid: a
     * two-feed list repeats with the row, so every row starts on the same feed and the
     * result is vertical stripes. Twelve entries is the honest way to say twelve tiles.
     */
    // The WHITE LEADER (index 0) carries the audible duty: followers' feeds are ignored
    // while synced, so the constraint anywhere else on a white square would do nothing.
    // Six of twelve squares end up audible (1,866 white audible clips).
    feeds: [
      tSrc({ kind: "keywords", groups: [["race:white"], ["audio:audible"]] }), tSrc(tKw("race:black")), tSrc(tKw("race:white")), tSrc(tKw("race:black")),
      tSrc(tKw("race:black")), tSrc(tKw("race:white")), tSrc(tKw("race:black")), tSrc(tKw("race:white")),
      tSrc(tKw("race:white")), tSrc(tKw("race:black")), tSrc(tKw("race:white")), tSrc(tKw("race:black")),
    ],
    // Matching the pattern above: every white square in step with every other, likewise
    // the black. Twelve tiles, two clips, two downloads.
    syncGroups: [0, 1, 0, 1, 1, 0, 1, 0, 0, 1, 0, 1],
  },
  {
    id: "russian-roulette",
    name: "Russian Roulette",
    emoji: "🎰",
    // The point of the setup is that you don't get to pick — and the one panel is also
    // the audible one, so the draw is from the whole AUDIBLE catalog (5,967 clips)
    // rather than `random`. Still no choosing; the chamber just always makes noise.
    layout: "single",
    feeds: [tSrc({ kind: "keywords", groups: [["audio:audible"]] })],
  },
  {
    id: "shemale-delight",
    name: "Shemale Delight",
    emoji: "🍆",
    adaptiveLayout: true,
    layout: "hero-rails",
    // Written out so the HERO (index 3 — rail, hero, rail) can require audio:audible.
    // THIN POOL, deliberately accepted: 42 trans+audible clips on prod at add time —
    // the hero cycles a small deck until the catalog grows, which beats a silent hero.
    feeds: [
      tSrc(tKw("sex:trans")), tSrc(tKw("sex:trans")), tSrc(tKw("sex:trans")),
      tSrc({ kind: "keywords", groups: [["sex:trans"], ["audio:audible"]] }),
      tSrc(tKw("sex:trans")), tSrc(tKw("sex:trans")), tSrc(tKw("sex:trans")),
    ],
  },
  {
    id: "maximum-dopamine",
    name: "Maximum Dopamine",
    emoji: "⚡",
    /*
     * The most tiles turning over the fastest — the two knobs this app has for
     * intensity, both at their stop.
     *
     * Sixteen tiles is the count; `clip_length:micro` is the speed. Length IS the pacing
     * now (a clip plays whole and the next one starts), so filtering to the 0–3s bucket
     * is not a filter on *content* at all — it is how you ask for a tile that never holds
     * still. Nothing else in the gallery uses a length facet as its feed, which is the
     * whole idea: every other built-in says what to play, this one says how hard.
     *
     * Deliberately NOT synced. Checkers groups its tiles to buy twelve of them for two
     * downloads, and that is the right trade for a checkerboard, where the pattern is the
     * point and repetition reads as design. Here repetition is the failure mode — sixteen
     * copies of one clip is one clip on a big screen — so every tile draws on its own and
     * the wall pays sixteen streams for it. That cost is the feature being bought.
     *
     * Its ceiling is the catalog's, not the layout's: micro is the narrowest length
     * bucket by some distance, so this plays a small pool hard, and it gets better the
     * more short clips ingest brings in rather than needing anything changed here.
     */
    layout: "grid-4x4",
    // Sixteen written out so ONE inner tile (index 5 — row 1, col 1, the closest a 4×4
    // has to a center) can require audio:audible. THIN POOL, accepted: 48 micro+audible
    // clips — one fast tile cycling a small deck, the other fifteen unchanged.
    feeds: [
      tSrc(tKw("clip_length:micro")), tSrc(tKw("clip_length:micro")), tSrc(tKw("clip_length:micro")), tSrc(tKw("clip_length:micro")),
      tSrc(tKw("clip_length:micro")),
      tSrc({ kind: "keywords", groups: [["clip_length:micro"], ["audio:audible"]] }),
      tSrc(tKw("clip_length:micro")), tSrc(tKw("clip_length:micro")),
      tSrc(tKw("clip_length:micro")), tSrc(tKw("clip_length:micro")), tSrc(tKw("clip_length:micro")), tSrc(tKw("clip_length:micro")),
      tSrc(tKw("clip_length:micro")), tSrc(tKw("clip_length:micro")), tSrc(tKw("clip_length:micro")), tSrc(tKw("clip_length:micro")),
    ],
  },
  {
    id: "brain-melter",
    name: "Brain Melter",
    emoji: "🌀",
    /*
     * Eight hypno panes framing one woman. The sides are all the same feed and the
     * feeds are written out per-tile anyway — cycling a two-entry list would put
     * hypno in the hero on a nine-tile layout, and the CENTER being plain is the
     * design: the melt is the frame, not the subject. `hypno` is a plain catalog
     * tag (275 prod clips when this was added), not a facet value, so it has no
     * prefix. Deliberately not synced: eight tiles of one looping clip is one
     * pane, and the melt wants motion everywhere.
     */
    layout: "2x2-hero-2x2",
    // The hero is also the audible panel (2,625 female solo audible clips).
    feeds: [
      tSrc(tKw("hypno")), tSrc(tKw("hypno")), tSrc(tKw("hypno")), tSrc(tKw("hypno")),
      tSrc({ kind: "keywords", groups: [["sex:female"], ["actor_count:1"], ["audio:audible"]] }),
      tSrc(tKw("hypno")), tSrc(tKw("hypno")), tSrc(tKw("hypno")), tSrc(tKw("hypno")),
    ],
  },
  {
    id: "safe-for-work",
    name: "Safe for Work",
    emoji: "👔",
    adaptiveLayout: true,
    // Three lanes of the nudity ladder's bottom two rungs — fully-clothed OR
    // minimal-clothing, one OR-group, so the lanes draw from both. The name is the
    // promise the facet keeps: a model verdict about what's COVERED, not about what's
    // happening, which is exactly the wall for a room where the door might open. The
    // middle lane carries the audible duty like every setup's central panel. Pools
    // measured on prod before committing (2026-08-26): the pair 781 · with audible 479
    // (fully-clothed alone was 313/180 — the widening to minimal-clothing was asked
    // for, and more than doubles both).
    layout: "3-vertical",
    feeds: [
      tSrc({ kind: "keywords", groups: [["nudity:fully-clothed", "nudity:minimal-clothing"]] }),
      tSrc({
        kind: "keywords",
        groups: [["nudity:fully-clothed", "nudity:minimal-clothing"], ["audio:audible"]],
      }),
      tSrc({ kind: "keywords", groups: [["nudity:fully-clothed", "nudity:minimal-clothing"]] }),
    ],
  },
  {
    id: "fully-clothed",
    name: "Fully Clothed",
    emoji: "👗",
    copy: {
      tile: "Solo, clothed, no explicit acts. Hand-picked.",
      shelf: "Fully Clothed: a hand-picked solo tease wall, everyone stays dressed.",
    },
    // What safe-for-work promised, kept by an ALLOW-LIST instead of a tag filter (QA
    // 2026-10-26): 102 clips each checked by eye for its whole length — one person, fully
    // clothed, no act. Tags could not do it: 28% of "fully-clothed, solo, no act tag"
    // clips failed the look, and a keyword source cannot exclude. So every tile plays the
    // reviewed collection and nothing else — no keyword tile, which is also why this is
    // the one setup without an audio:audible panel (the list has no audio tag to require).
    // The default 8-tile wall; at ~10 s a clip, 8 tiles show the whole list in ~2.3 min.
    layout: "grid-2x4",
    feeds: [tSrc({ kind: "playlist", playlistId: FULLY_CLOTHED_COLLECTION_ID })],
  },
  {
    id: "penis-envy",
    name: "Penis Envy",
    emoji: "🍆",
    // twin-towers is [left tower, right tower, spine×3]. The two full-height towers play
    // IN STEP (syncGroups 0,0 — one download, both sides show the same cock), while the
    // three middle spine cells each run their own focus:cock feed independently.
    //
    // WRITTEN OUT rather than one feed cycled, so the TOWERS — the full-height pair, and
    // the only thing on this wall anybody would call the main panel — can require
    // audio:audible while the spine stays unconstrained. Cycling a two-feed list would
    // have scattered the constraint across the spine as well and narrowed three cells for
    // no reason. Index 0 is the sync LEADER, which is where the constraint has to sit: on
    // a follower it would be silently ignored, since a follower mirrors its leader's feed.
    layout: "twin-towers",
    feeds: [
      tSrc({ kind: "keywords", groups: [["focus:cock"], ["audio:audible"]] }),
      tSrc({ kind: "keywords", groups: [["focus:cock"]] }),
      tSrc({ kind: "keywords", groups: [["focus:cock"]] }),
      tSrc({ kind: "keywords", groups: [["focus:cock"]] }),
      tSrc({ kind: "keywords", groups: [["focus:cock"]] }),
    ],
    syncGroups: [0, 0, 1, 2, 3],
    // The two towers mirror each other — the right one is the left flipped.
    syncFlip: [0],
  },
];

/**
 * Quick setups withheld from every surface, kept in ALL_MONITOR_TEMPLATES so un-hiding is
 * deleting an id here. Hidden means gone everywhere MONITOR_TEMPLATES reaches: the lobby
 * tiles, the remote's shelf, rooms, and `builtin:<id>` (templateForViewId), so neither
 * `/screen?setup=<id>` nor `?view=builtin:<id>` serves it.
 *
 * `safe-for-work` (2026-10-25, QA 2026-10-23-am #4): its feeds filter on the nudity facet
 * alone, which says what is COVERED, not what is HAPPENING, so clothed blowjobs, clothed
 * penetration and hentai qualify. Hidden until its feeds can exclude acts.
 */
export const HIDDEN_TEMPLATE_IDS: ReadonlySet<string> = new Set(["safe-for-work"]);

export const MONITOR_TEMPLATES: ReadonlyArray<ViewTemplate> = ALL_MONITOR_TEMPLATES.filter(
  (t) => !HIDDEN_TEMPLATE_IDS.has(t.id),
);

// ---------------------------------------------------------------------------
// View ids: three kinds, one place that says which is which.
// ---------------------------------------------------------------------------

/**
 * A built-in quick setup, addressed directly.
 *
 * They are permanent, identical for everybody, and owned by nobody, so a screen can
 * point straight at one instead of at a private snapshot of it. That is the whole
 * difference from a temporary view: there is nothing to expire, nothing to clean up, and
 * any controller can switch any device onto one without first minting anything.
 */
export const BUILTIN_VIEW_PREFIX = "builtin:";
/** An unsaved view living in the server's memory until nothing is watching it. */
export const TEMP_VIEW_PREFIX = "t_";
/**
 * The walls — shared, purchasable screens. Like a built-in a wall is computed on read,
 * owned by nobody and writable by nobody; unlike a built-in its regions come from the
 * placements ledger rather than a template. The PREFIX is the kind; each wall is an id
 * under it, and the ledger scopes a square to its wall by the region id's prefix
 * (`wall:premium:r3`), so a second wall was an id, not a migration — see WALL_DEFS.
 */
export const WALL_VIEW_PREFIX = "wall:";
/** The Community Wall — the front door's default, and the one everybody can buy onto. */
export const WALL_VIEW_ID = "wall:main";
/** The Premium Wall — same board, same commerce, but only GoonLords Premium members claim. */
export const PREMIUM_WALL_VIEW_ID = "wall:premium";

export type WallId = "main" | "premium";

/**
 * What a wall IS, in one row per wall: its view id, what people call it, its glyph, whether
 * claiming is a members' privilege, and which settings block prices it. Everything that
 * used to be a `wall:main` literal reads from here, so adding a wall is adding a row — the
 * region ids, the resolver, the picker, the Discord feed and the admin panel all follow.
 *
 * `premiumOnly` gates CLAIMING, never watching: a wall is a public place, and the members'
 * wall is worth more to members precisely because everybody can see it.
 */
export interface WallDef {
  id: WallId;
  viewId: string;
  name: string;
  icon: string;
  premiumOnly: boolean;
  settingsKey: "wall" | "premiumWall";
}

export const WALL_DEFS: Record<WallId, WallDef> = {
  main: {
    id: "main",
    viewId: WALL_VIEW_ID,
    name: "Community Wall",
    icon: "🧱",
    premiumOnly: false,
    settingsKey: "wall",
  },
  premium: {
    id: "premium",
    viewId: PREMIUM_WALL_VIEW_ID,
    name: "Premium Wall",
    // The Premium mark. On the glass this wall wears the real Premium chip (the yellow
    // badge with the white crown — WallGlyph on the client); the emoji is the string
    // fallback for the places that can only carry text: a View's icon, a Discord title.
    icon: "👑",
    premiumOnly: true,
    settingsKey: "premiumWall",
  },
};

/** The walls in the order a person meets them — the Community Wall first. */
export const WALL_LIST: ReadonlyArray<WallDef> = [WALL_DEFS.main, WALL_DEFS.premium];

/** Which wall a view id names, or null — `wall:nope` is a wall-kind id that is no wall. */
export const wallIdOfView = (viewId: string): WallId | null =>
  WALL_LIST.find((d) => d.viewId === viewId)?.id ?? null;

/** Which wall a region id belongs to, or null. Exact prefix: `wall:Premium:r0` is nobody's. */
export const wallIdOfRegion = (regionId: string): WallId | null =>
  WALL_LIST.find((d) => regionId.startsWith(`${d.viewId}:r`))?.id ?? null;

/**
 * Infinity Walls — the endless one. Not a board and not a layout: a SCROLL of walls, each
 * page a random layout of a few regions, each region one clip on a loop, the next page one
 * swipe down. The pages are built on the client from the random feed, so the view the
 * server resolves for this id is a STUB with no regions (see infinityView) — enough for
 * everything that keys off a resolvable view (the socket room, the Now Showing pill, a
 * remote pointing a device here) and nothing that would pretend the layout is authored.
 *
 * A prefix, like the walls, so a second infinity feed (a tag's, a collection's) is an id
 * under it rather than a new kind.
 */
export const INFINITY_VIEW_PREFIX = "infinity:";

export type InfinityId = "random" | "keyword" | "colorwave" | "solo" | "build" | "battle" | "goontok";

/**
 * The keyword namespace the vision tagger writes garments into, `attire:<colour>-<garment>`
 * (`attire:black-bikini`, `attire:pink-panties`). Colorwave reads it by colour.
 */
export const ATTIRE_TAG_PREFIX = "attire:";

/**
 * The colours Colorwave may deal a wall in — the first word of an attire keyword. A
 * closed list rather than "whatever prefixes exist", because `attire:lace-panties` and
 * `attire:highleg-bikini` share the shape and are not colours.
 */
export const COLORWAVE_COLORS: ReadonlyArray<string> = [
  "black",
  "white",
  "red",
  "pink",
  "blue",
  "green",
  "purple",
  "yellow",
  "orange",
  "grey",
  "brown",
  "gold",
  "silver",
  "aqua",
];

/**
 * Patterns are colours too, for a page: a plaid skirt or a striped shirt reads as a theme
 * the way a red dress does. Listed like the colours, framed in their own swatch.
 */
export const COLORWAVE_PATTERNS: ReadonlyArray<string> = ["plaid", "striped", "denim"];

/**
 * Where a garment is worn, and which FOCUS keeps it in frame. A Colorwave page is one
 * coloured OUTFIT piece — black bikini, red dress, plaid skirt — and only clips whose
 * focus shows it: a top needs the tits or the whole body in frame, a bottom the ass or
 * the whole body. A black bikini on a face-focused clip is a black page nobody can see;
 * the first cut of this source dealt exactly those and the colour barely registered.
 *
 * OUTFITS, NOT UNDERWEAR. Panties, bras, thigh-highs and pantyhose are out of this list
 * on purpose: the tagger writes them on most clips and they are mostly not what you see —
 * a page of "pink panties" was a page of assorted bodies. Underwear comes back only as a
 * matching SET (COLORWAVE_SETS), which is an outfit.
 */
export const COLORWAVE_FOCUS = {
  top: ["focus:tits", "focus:wholebody"],
  bottom: ["focus:ass", "focus:wholebody"],
} as const;
export type ColorwaveWear = keyof typeof COLORWAVE_FOCUS;

/** The garments the vision tagger writes a colour on, each with where it is worn. */
export const COLORWAVE_GARMENTS: Readonly<Record<string, ColorwaveWear>> = {
  shirt: "top",
  bikini: "top",
  dress: "top",
  jacket: "top",
  skirt: "bottom",
  shorts: "bottom",
  pants: "bottom",
};

/**
 * Matching underwear SETS — pink bra with pink panties, black panties with black
 * thigh-highs — the one way underwear makes a page: two pieces in one colour is an
 * outfit. Shown by any body focus, since the set spans the body.
 */
export const COLORWAVE_SETS: ReadonlyArray<readonly [string, string]> = [
  ["bra", "panties"],
  ["panties", "thigh-highs"],
];
export const COLORWAVE_SET_FOCUS: ReadonlyArray<string> = ["focus:tits", "focus:ass", "focus:wholebody"];

/**
 * What each colour looks like on the glass: the glow the stage frames a Colorwave wall
 * in. Saturated mid-tones rather than the literal colour, because the glow sits over
 * dark video — literal black vanishes and literal yellow blinds. Black gets a smoke
 * grey so its tube still reads against dark video; white a warm off-white.
 */
export const COLORWAVE_SWATCH: Readonly<Record<string, string>> = {
  black: "#8b8b96",
  white: "#f4f4f5",
  red: "#ef4444",
  pink: "#ec4899",
  blue: "#3b82f6",
  green: "#22c55e",
  purple: "#a855f7",
  yellow: "#facc15",
  orange: "#f97316",
  grey: "#9ca3af",
  brown: "#a16207",
  gold: "#d4af37",
  silver: "#c0c0c0",
  aqua: "#22d3ee",
  // Hair. Blonde is gold, brunette a warm brown, redhead copper, black the same smoke
  // as black cloth, grey-silver the same as silver.
  blonde: "#e6c25a",
  brunette: "#a0672f",
  redhead: "#e2611b",
  "grey-silver": "#c0c0c0",
  // Patterns. Plaid is tartan red, striped a chalk white, denim indigo.
  plaid: "#b3282d",
  striped: "#d4d4d8",
  denim: "#3b5b92",
};

/**
 * Keywords EVERY Colorwave clip must carry, whatever the theme: one woman in frame. A
 * black bikini in a two-girl clip is one black bikini among two bodies; the page is about
 * the garment, and a single wearer is what keeps it the subject. And one PERSON is not
 * one woman — a brunette face page dealt a solo man before `sex:female` was here (the
 * tagger writes `sex:*` on every one-person clip, so this excludes nothing untagged).
 */
export const COLORWAVE_ALWAYS: ReadonlyArray<string> = ["actor_count:1", "sex:female"];

/**
 * Hair is a colour too. A face-focused clip has no garment to show, so on those pages
 * Colorwave deals by HAIR COLOUR — `hair:blonde`, `hair:redhead` — framed in the hair's
 * swatch. Eye colour was measured and left out: a fifth of face-focused clips carry one,
 * too thin for a single page.
 */
export const HAIR_TAG_PREFIX = "hair:";
export const COLORWAVE_HAIR_FOCUS: ReadonlyArray<string> = ["focus:face"];
/** The hair colours that are colours — `hair:dyed-colored` is a fact about dye, not a hue. */
export const COLORWAVE_HAIR: ReadonlyArray<string> = ["blonde", "brunette", "black", "redhead", "grey-silver"];

/** A Colorwave theme: the keywords a clip must ALL carry (one garment, or a set's two),
 *  the focus values that show them, and the swatch the page is framed in (a
 *  COLORWAVE_SWATCH key). */
export interface ColorwaveTheme {
  tags: ReadonlyArray<string>;
  focus: ReadonlyArray<string>;
  color: string;
}

/**
 * The themes a catalog can deal, from a tally of attire keywords by how many clips carry
 * each at the given wear's focus: keep the keywords that are a listed colour on a garment
 * worn there, deep enough for a wall. Pure — the tally is the server's, the rule is here
 * where the check can read it.
 */
export function colorwaveThemes(
  counts: ReadonlyArray<{ tag: string; n: number }>,
  wear: ColorwaveWear,
  min: number,
): ColorwaveTheme[] {
  const out: ColorwaveTheme[] = [];
  for (const { tag, n } of counts) {
    if (n < min || !tag.startsWith(ATTIRE_TAG_PREFIX)) continue;
    const name = tag.slice(ATTIRE_TAG_PREFIX.length);
    const dash = name.indexOf("-");
    if (dash < 0) continue;
    const color = name.slice(0, dash);
    const garment = name.slice(dash + 1);
    if (!(COLORWAVE_COLORS.includes(color) || COLORWAVE_PATTERNS.includes(color)) || COLORWAVE_GARMENTS[garment] !== wear) continue;
    out.push({ tags: [tag], focus: COLORWAVE_FOCUS[wear], color });
  }
  return out;
}

/** The keyword a set piece is written as: `attire:<colour>-<garment>`. */
export const attireTag = (color: string, garment: string): string => `${ATTIRE_TAG_PREFIX}${color}-${garment}`;

/** The matching-set themes a catalog can deal, from a tally of (colour, set) pairs by
 *  how many clips wear both pieces at a body focus: the listed colours, deep enough. */
export function colorwaveSetThemes(
  counts: ReadonlyArray<{ color: string; set: readonly [string, string]; n: number }>,
  min: number,
): ColorwaveTheme[] {
  const out: ColorwaveTheme[] = [];
  for (const { color, set, n } of counts) {
    if (n < min || !COLORWAVE_COLORS.includes(color)) continue;
    if (!COLORWAVE_SETS.some((s) => s[0] === set[0] && s[1] === set[1])) continue;
    out.push({ tags: [attireTag(color, set[0]), attireTag(color, set[1])], focus: COLORWAVE_SET_FOCUS, color });
  }
  return out;
}

/** The hair-colour themes a catalog can deal, from a tally of `hair:*` keywords on
 *  face-focused clips: the listed colours, deep enough for a wall. */
export function colorwaveHairThemes(
  counts: ReadonlyArray<{ tag: string; n: number }>,
  min: number,
): ColorwaveTheme[] {
  const out: ColorwaveTheme[] = [];
  for (const { tag, n } of counts) {
    if (n < min || !tag.startsWith(HAIR_TAG_PREFIX)) continue;
    const color = tag.slice(HAIR_TAG_PREFIX.length);
    if (!COLORWAVE_HAIR.includes(color)) continue;
    out.push({ tags: [tag], focus: COLORWAVE_HAIR_FOCUS, color });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Build a Slut — five picking rounds, then a scroll of the woman the picks describe.
// ---------------------------------------------------------------------------

/**
 * A round: the word on the glass, the facets a pick contributes from (the clip's OWN tags
 * under these facets become the profile), the values a round is allowed to offer as its
 * four candidates (`values` — a facet's whole vocabulary where absent), and the focus that
 * keeps the trait in frame. Order is the order played. Ass and genitals are not rounds:
 * the catalog has no shape facet for either (measured 2026-09-18), so a pick there would
 * change nothing.
 */
export interface BuildStage {
  id: BuildStageId;
  word: string;
  facets: ReadonlyArray<TagFacet>;
  values?: Readonly<Partial<Record<TagFacet, ReadonlyArray<string>>>>;
  focus: ReadonlyArray<string>;
  /** Offer EVERY allowed value, one each, in vocabulary order — a scale, not a draw. Tits:
   *  small, medium, large, huge, always, left to right (owner, 2026-09-19). */
  fixed?: true;
}

export const BUILD_STAGES: ReadonlyArray<BuildStage> = [
  {
    id: "face",
    word: "Face",
    facets: ["race", "hair"],
    values: { hair: ["blonde", "brunette", "black", "redhead", "dyed-colored"] },
    // Faces, framed as faces (owner, 2026-09-19): a whole-body shot is a body round.
    focus: ["focus:face"],
  },
  // Each round framed on its trait (owner, 2026-09-19): tits as tits, hair on a face, a
  // body whole, style whole.
  { id: "tits", word: "Tits", facets: ["tits"], focus: ["focus:tits"], fixed: true },
  {
    id: "body",
    word: "Body",
    facets: ["body"],
    // Shape only: tattoos, piercings and hairy are style, and Style is its own round.
    values: { body: ["thick", "skinny", "petite", "curvy", "fit"] },
    focus: ["focus:wholebody"],
  },
  {
    id: "hair",
    word: "Hair",
    facets: ["hairstyle"],
    values: { hairstyle: ["long", "short", "braid", "ponytail", "twintails"] },
    focus: ["focus:face"],
  },
  { id: "style", word: "Style", facets: ["aesthetic", "attire"], focus: ["focus:wholebody"] },
];

export const buildStageOf = (id: string): BuildStage | null => BUILD_STAGES.find((s) => s.id === id) ?? null;

/** Every clip in every round and on the scroll: one woman — the same rule as Colorwave. */
export const BUILD_ALWAYS: ReadonlyArray<string> = COLORWAVE_ALWAYS;

/** The facets a profile may carry — every stage's, and nothing a client could smuggle. */
export const BUILD_PROFILE_FACETS: ReadonlyArray<TagFacet> = ["race", "hair", "tits", "body", "hairstyle", "aesthetic", "attire"];

/** A profile is at most this many tags: five rounds' worth with room for a two-tag face
 *  and a set of attire; it only ever binds into an IN (...), and this bounds it. */
export const BUILD_PROFILE_MAX = 16;

/** How many clips a free viewer's one wall of her holds — enough for any preset. */
export const BUILD_PREVIEW_CLIPS = 6;

/** The profile a client sent, cleaned: facet tags on the listed facets, deduped, capped. */
export function cleanBuildProfile(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  const out: string[] = [];
  for (const v of input) {
    if (typeof v !== "string" || !isFacetTag(v)) continue;
    const f = facetOf(v);
    if (!f || !BUILD_PROFILE_FACETS.includes(f) || out.includes(v)) continue;
    out.push(v);
    if (out.length >= BUILD_PROFILE_MAX) break;
  }
  return out;
}

/** The order the rounds' constraints are DROPPED when a later round would otherwise have
 *  fewer than four candidates: the least visual first, the face last. */
export const BUILD_RELAX_ORDER: ReadonlyArray<TagFacet> = ["attire", "aesthetic", "hairstyle", "body", "tits", "hair", "race"];

/**
 * The next thing the stage asks for, from what has been picked: round N+1 while rounds
 * remain, the scroll after the last. The profile is every pick's tags, flattened and
 * deduped; `maxMatch` rides only on the scroll.
 */
export function buildSourceFor(
  picks: ReadonlyArray<BuildPick>,
  maxMatch: number | null = null,
  preview = false,
  portrait = false,
): BuildSourceConfig {
  const profile = cleanBuildProfile(picks.flatMap((p) => p.tags));
  const next = BUILD_STAGES[picks.length];
  if (next) return { kind: "build", stage: next.id, profile, ...(portrait ? { portrait: true as const } : {}) };
  return {
    kind: "build",
    stage: "scroll",
    profile,
    ...(maxMatch !== null ? { maxMatch } : {}),
    ...(preview ? { preview: true as const } : {}),
  };
}

/**
 * `k` DISTINCT rows drawn without replacement, each draw weighted by `weight(n)`. The
 * default √n is the whole point: at n = 2456 vs 80 (small vs huge tits) a linear weight
 * would show the rare value once in thirty rounds and √ once in six — common still
 * dominates, rare still turns up. Rows with no clips are never drawn. Pure; `rng` is
 * injected so the check can pin the order.
 */
export function weightedDistinct<T extends { n: number }>(
  rows: ReadonlyArray<T>,
  k: number,
  rng: () => number,
  weight: (n: number) => number = Math.sqrt,
): T[] {
  const pool = rows.filter((r) => r.n > 0);
  const out: T[] = [];
  while (out.length < k && pool.length > 0) {
    const total = pool.reduce((acc, r) => acc + weight(r.n), 0);
    let at = rng() * total;
    let idx = pool.length - 1;
    for (let i = 0; i < pool.length; i++) {
      at -= weight(pool[i]!.n);
      if (at < 0) { idx = i; break; }
    }
    out.push(pool.splice(idx, 1)[0]!);
  }
  return out;
}

/**
 * The Style round's candidates are THEMES, not tags: every attire keyword ending in a
 * garment folds into that garment's family ("Bikini" is attire:bikini, attire:red-bikini,
 * attire:micro-bikini...), each aesthetic stands alone, and a candidate carries the OR of
 * its tags. One family is one look; twenty colours of bikini are not twenty candidates.
 */
export const BUILD_STYLE_FAMILIES: ReadonlyArray<{ label: string; garments: ReadonlyArray<string> }> = [
  { label: "Bikini", garments: ["bikini", "swimsuit"] },
  { label: "Lingerie", garments: ["lingerie", "bra", "panties", "thong"] },
  { label: "Dress", garments: ["dress"] },
  { label: "Stockings", garments: ["stockings", "thigh-highs", "fishnets", "pantyhose"] },
  { label: "Skirt", garments: ["skirt"] },
  { label: "Shirt", garments: ["shirt", "top"] },
  { label: "Cosplay", garments: ["cosplay", "maid", "nurse"] },
];

export interface BuildStyleTheme {
  label: string;
  tags: string[];
  n: number;
}

export function buildStyleThemes(counts: ReadonlyArray<{ tag: string; n: number }>): BuildStyleTheme[] {
  const families = new Map<string, BuildStyleTheme>();
  const out: BuildStyleTheme[] = [];
  for (const { tag, n } of counts) {
    if (tag.startsWith("aesthetic:")) {
      out.push({ label: facetValueLabel("aesthetic", tag.slice("aesthetic:".length)), tags: [tag], n });
      continue;
    }
    if (!tag.startsWith(ATTIRE_TAG_PREFIX)) continue;
    const name = tag.slice(ATTIRE_TAG_PREFIX.length);
    const fam = BUILD_STYLE_FAMILIES.find((f) => f.garments.some((g) => name === g || name.endsWith(`-${g}`)));
    if (!fam) continue;
    const t = families.get(fam.label) ?? { label: fam.label, tags: [], n: 0 };
    t.tags.push(tag);
    t.n += n;
    families.set(fam.label, t);
  }
  return [...out, ...families.values()];
}

// ---------------------------------------------------------------------------
// Goonette of the Hill — a round of four, the pick stays, three challengers arrive.
// ---------------------------------------------------------------------------

/** A round is this many tiles: the pick from last round in its place, plus challengers. */
export const BATTLE_SIZE = 4;
/** No pick until the round has been on the glass this long with every tile playing — a
 *  preference is worth recording only once the four were actually watched. Three seconds
 *  (owner, 2026-09-19; five felt like a wait). */
export const BATTLE_READY_MS = 3000;

/** What a pick reports: the clip that won, the clips it beat, the round number. Clip
 *  keys (clipKey), which the server resolves; nothing else about the viewer rides here —
 *  identity is the request's (bearer or anon id), never a body field. */
export interface BattlePickRequest {
  winner: string;
  losers: string[];
  round: number;
  /** Which of the round's slots she stayed in, so a saved game can put her back there. */
  slot?: number;
}

/** A signed-in viewer's game, so it picks up where it stopped: the round reached, the
 *  reigning clip (served), which slot she sits in, and how many rounds she has held. */
export interface BattleStateResponse {
  round: number;
  champion: MediaItem | null;
  slot: number;
  streak: number;
}

/**
 * What a scroll IS, one row per scroll — the walls' WALL_DEFS, for the endless ones:
 * its view id, what people call it, its glyph, and which FEED deals its pages.
 *
 * `feed` names the theme; `source` is what the stage asks the server for, once per page,
 * and null means the page is dealt from the random pool alone. Every themed scroll falls
 * back to that pool when its page comes back too thin, so the scroll never stalls:
 *   random (Random)        — every page from the whole library.
 *   roulette (Enhanced)    — the server picks one keyword per page, hidden ones included.
 *   colorwave (Colorwave)  — the server picks one COLOUR per page and deals every
 *                            garment in it (black bikini, black panties, black shirt...).
 *   solo (Solo Girls)      — every page from clips tagged one person, female.
 *   build (Build a Slut)   — five picking rounds (BUILD_STAGES), then a scroll of the woman
 *                            the picks describe; `source` is round one's config and the
 *                            stage derives the rest (buildSourceFor).
 *   battle (Goonette of the Hill) — every page a round of four; the pick stays in its place
 *                            and three challengers arrive (BATTLE_*). Each pick is a
 *                            preference recorded (POST /api/battles/pick).
 * Adding a scroll is adding a row: the resolver, the picker, the guide's link and the
 * stage all read from here.
 */
export interface InfinityDef {
  id: InfinityId;
  viewId: string;
  name: string;
  icon: string;
  feed: "random" | "roulette" | "colorwave" | "solo" | "build" | "battle" | "goontok";
  /** Asked once per page for a themed scroll; null deals from the random pool. */
  source: SourceConfig | null;
  /**
   * Until when this scroll wears its NEW! badge in the picker (ms since epoch). A badge
   * with no sunset is a badge nobody sees after the first week; each row retires its own
   * a month after ITS launch, so a new scroll is new while the old ones are not.
   */
  newUntil: number;
}

export const INFINITY_DEFS: Record<InfinityId, InfinityDef> = {
  random: {
    id: "random",
    viewId: "infinity:random",
    name: "Random",
    icon: "🎲",
    feed: "random",
    source: null,
    // Launched 2026-09-09, with Enhanced.
    newUntil: Date.UTC(2026, 9, 9),
  },
  keyword: {
    id: "keyword",
    viewId: "infinity:keyword",
    name: "Enhanced",
    // The AI sparkle: on React surfaces the real IconSparkle (InfinityGlyph), the emoji
    // for the text-only places — the stub view's icon, the pill.
    icon: "✨",
    feed: "roulette",
    source: { kind: "roulette" },
    newUntil: Date.UTC(2026, 9, 9),
  },
  colorwave: {
    id: "colorwave",
    viewId: "infinity:colorwave",
    name: "Colorwave",
    // The rainbow for text-only places; InfinityGlyph draws a colour wheel.
    icon: "🌈",
    feed: "colorwave",
    source: { kind: "colorwave" },
    // Launched 2026-09-18, with Solo Girls.
    newUntil: Date.UTC(2026, 9, 18),
  },
  solo: {
    id: "solo",
    viewId: "infinity:solo",
    name: "Solo Girls",
    icon: "💃",
    feed: "solo",
    // The two facets AND-ed: `sex:*` is only ever tagged on one-person clips, so this
    // is exactly "a woman alone" in the catalog's own vocabulary.
    source: { kind: "keywords", groups: [["actor_count:1"], ["sex:female"]] },
    newUntil: Date.UTC(2026, 9, 18),
  },
  build: {
    id: "build",
    viewId: "infinity:build",
    name: "Build a Slut",
    icon: "🧬",
    feed: "build",
    // Round one. The stage asks the next rounds and the scroll itself (buildSourceFor).
    source: { kind: "build", stage: "face", profile: [] },
    // Launched 2026-09-19.
    newUntil: Date.UTC(2026, 9, 19),
  },
  battle: {
    id: "battle",
    viewId: "infinity:battle",
    name: "Goonette of the Hill",
    icon: "👑",
    feed: "battle",
    // One woman per tile, the whole library of them: the challengers each round.
    source: { kind: "keywords", groups: [["actor_count:1"], ["sex:female"]] },
    newUntil: Date.UTC(2026, 9, 19),
  },
  goontok: {
    id: "goontok",
    viewId: "infinity:goontok",
    name: "GoonTok",
    icon: "📱",
    feed: "goontok",
    // One clip a page (GOONTOK_PRESET), from the crowd-weighted draw.
    source: { kind: "goontok" },
    // Launched 2026-10-29: new for a month.
    newUntil: Date.UTC(2026, 10, 29),
  },
};

/** A GoonTok page is ONE clip, the whole glass — the scroll's whole idea. */
export const GOONTOK_PRESET: LayoutPresetId = "single";

/** How much of a clip `object-fit: cover` cuts away to fill a page of another shape —
 *  0 for a match, 0.5 for a square clip on a 1:2 phone. */
export const goontokCrop = (clipAspect: number, pageAspect: number): number =>
  1 - Math.min(clipAspect / pageAspect, pageAspect / clipAspect);

/** Past this much cut, a GoonTok clip is letterboxed instead: a portrait clip on a phone
 *  (~12%) fills the glass; a square one there (~50%) or a portrait one on a desk (~67%)
 *  shows whole. */
export const GOONTOK_MAX_CROP = 0.3;

/** A GoonTok clip's fit on a page of this shape: fill it, unless that would cut more than
 *  GOONTOK_MAX_CROP — then whole, letterboxed. Decided at RENDER (2026-10-29), so a phone
 *  turned sideways re-judges the clip on the glass instead of keeping the dealt-for fit. */
export function goontokFit(clip: Pick<MediaItem, "width" | "height"> | null | undefined, pageAspect: number): MediaFit {
  const w = clip?.width ?? 0;
  const h = clip?.height ?? 0;
  return w > 0 && h > 0 && pageAspect > 0 && goontokCrop(w / h, pageAspect) > GOONTOK_MAX_CROP ? "contain" : "cover";
}

/**
 * How many of a GoonTok fetch are drawn at plain random rather than by weight. A third:
 * enough that a clip nobody has seen yet still turns up every few swipes (and so can earn
 * the views and faps that weight it next time), few enough that the scroll reads as the
 * good stuff.
 */
export const GOONTOK_RANDOM_SHARE = 1 / 3;

/** What a clip's crowd numbers count for, against a base of 1. Edges and faps are someone
 *  reacting to the clip; a view is only that it played, so it counts for half as much. */
export const GOONTOK_WEIGHTS = { views: 1, edges: 2, faps: 2 } as const;

export interface GoontokStats {
  /** Plays × the clip's length: seconds of play. A play of a 2s loop is not a play of a
   *  minute-long clip, and every loop is a play, so raw plays hand the weight to whatever
   *  is shortest (median on prod: 1,371 under 5s, 69 at 30–60s). */
  views: number;
  edges: number;
  faps: number;
}

/**
 * A clip's draw weight: 1, plus each crowd number on a LOG scale against the catalog's
 * own high-water mark (`scale`, a 99th percentile), capped at its full share — then
 * SQUARED. Logs because the counts are heavy-tailed (a handful of clips have thousands of
 * views) and a linear weight would deal those few forever. Squared because nearly every
 * clip has SOME views, so the plain sum sat at a median of 2.3 against a ceiling of 6 and
 * barely tilted anything: on the prod catalog (2026-10-29) the top tenth by weight was 16%
 * of a deal plain, 24% squared. So the busiest clip is at most 6² = 36× as likely as an
 * untouched one, ~7× the median: weighted PARTIALLY, as asked, not ranked.
 */
export function goontokWeight(s: GoontokStats, scale: GoontokStats): number {
  const part = (n: number, top: number) => (top > 0 ? Math.min(1, Math.log1p(Math.max(0, n)) / Math.log1p(top)) : 0);
  const sum =
    1 +
    GOONTOK_WEIGHTS.views * part(s.views, scale.views) +
    GOONTOK_WEIGHTS.edges * part(s.edges, scale.edges) +
    GOONTOK_WEIGHTS.faps * part(s.faps, scale.faps);
  return sum * sum;
}

/** The 99th percentile of each crowd number — the `scale` goontokWeight reads against, so
 *  one runaway clip does not flatten everyone else's weight to nothing. */
export function goontokScale(rows: ReadonlyArray<GoontokStats>): GoontokStats {
  const p99 = (pick: (r: GoontokStats) => number) => {
    if (rows.length === 0) return 0;
    const v = rows.map(pick).sort((a, b) => a - b);
    return v[Math.min(v.length - 1, Math.floor(v.length * 0.99))]!;
  };
  return { views: p99((r) => r.views), edges: p99((r) => r.edges), faps: p99((r) => r.faps) };
}

/**
 * One GoonTok fetch: `n` distinct ids, each slot a coin flip between a WEIGHTED draw
 * (Efraimidis–Spirakis: key = u^(1/w), highest keys win — a weighted sample without
 * replacement in one pass) and a UNIFORM one. Pure, so the check can count what a
 * thousand draws favour.
 */
export function goontokDeal<T extends GoontokStats & { id: string }>(
  rows: ReadonlyArray<T>,
  n: number,
  rng: () => number,
  randomShare = GOONTOK_RANDOM_SHARE,
): string[] {
  const scale = goontokScale(rows);
  const weighted = rows
    .map((r) => ({ id: r.id, key: Math.pow(Math.max(rng(), 1e-12), 1 / goontokWeight(r, scale)) }))
    .sort((a, b) => b.key - a.key)
    .map((r) => r.id);
  const out: string[] = [];
  const taken = new Set<string>();
  let wi = 0;
  for (let guard = 0; out.length < Math.min(n, rows.length) && guard < n * 20; guard++) {
    let id: string | undefined;
    if (rng() < randomShare) id = rows[Math.floor(rng() * rows.length) % rows.length]!.id;
    else while (wi < weighted.length && taken.has((id = weighted[wi++]!)));
    if (id === undefined || taken.has(id)) continue;
    taken.add(id);
    out.push(id);
  }
  return out;
}

/** The scrolls in the order a person meets them. */
export const INFINITY_LIST: ReadonlyArray<InfinityDef> = [
  INFINITY_DEFS.random,
  INFINITY_DEFS.keyword,
  INFINITY_DEFS.colorwave,
  INFINITY_DEFS.solo,
  INFINITY_DEFS.build,
  INFINITY_DEFS.battle,
  INFINITY_DEFS.goontok,
];

/** The section they sit under in the picker and the name the How-To card wears. */
export const INFINITY_SECTION = "Infinite Scroll";

/** The default scroll — what a bare infinity door opens. */
export const INFINITY_VIEW_ID = INFINITY_DEFS.random.viewId;

/**
 * What a bare `/screen` plays, and what a device nobody has pointed anywhere shows: one of
 * THREE front doors, drawn at random — Build a Slut, Colorwave, or the Community Wall
 * (owner, 2026-09-18; it was Build a Slut alone since 2026-09-19, and the Community Wall
 * before that). One list and one draw, read by the client's door (PendingScreen) and the
 * server's default (devices), so the two can only ever disagree on WHICH of these three —
 * never on whether something plays — and a screen that heals on the server lands on a door
 * the client would have opened too.
 */
export const SCREEN_FRONT_DOORS: ReadonlyArray<string> = [
  INFINITY_DEFS.build.viewId,
  INFINITY_DEFS.colorwave.viewId,
  WALL_VIEW_ID,
];

/** One of the front doors, uniformly. Call it ONCE per screen, not per render. */
export const randomScreenFrontDoor = (): string =>
  SCREEN_FRONT_DOORS[Math.floor(Math.random() * SCREEN_FRONT_DOORS.length)]!;

/**
 * Auto-scroll's wait between walls, in seconds; 0 is off (2026-09-14). A WHITELIST, the
 * CLAIM_HOLD_CHOICES_MS shape: a television picks from chips, a slider is a cursor's job,
 * and a number that is not on the list reads as "off" rather than as the nearest one — a
 * stray value must never make a wall move before anyone asked. Off by default for the same
 * reason: a wall that moves by itself before you asked is a wall that moves while you are
 * looking at it.
 *
 * 1 s (2026-10-28, the owner's ask): a rapid-fire flip through walls. The next page is
 * already mounted (InfinityStage's MOUNT_RADIUS) and the decode budget parks what scrolls
 * away, so a wall a second is a slideshow of first frames on a weak device, not a stall.
 */
export const INFINITY_AUTO_SCROLL_CHOICES_S = [0, 1, 5, 10, 20, 30, 60] as const;
export type InfinityAutoScrollSec = (typeof INFINITY_AUTO_SCROLL_CHOICES_S)[number];

export function parseInfinityAutoScrollSec(value: unknown): InfinityAutoScrollSec {
  return typeof value === "number" && (INFINITY_AUTO_SCROLL_CHOICES_S as readonly number[]).includes(value)
    ? (value as InfinityAutoScrollSec)
    : 0;
}

/** Which scroll a view id names, or null — `infinity:nope` is an infinity-kind id that
 *  is no scroll. */
export const infinityIdOfView = (viewId: string): InfinityId | null =>
  INFINITY_LIST.find((d) => d.viewId === viewId)?.id ?? null;

/** A GOON ROOM's view id (2026-09-19): `room:<roomId>`. The room's OWNER decides what
 *  it plays; the id resolves (server-side, from the room hub's snapshot) so a screen can
 *  join its ws room, the bar can name it, and — later — a remote can point a TV at it. */
export const ROOM_VIEW_PREFIX = "room:";
/** The room behind a `room:` id, or null for any other kind. */
export const roomIdOfView = (viewId: string): string | null =>
  viewId.startsWith(ROOM_VIEW_PREFIX) ? viewId.slice(ROOM_VIEW_PREFIX.length) || null : null;
export const roomViewId = (roomId: string): string => `${ROOM_VIEW_PREFIX}${roomId}`;
/** What a MEMBER is told the host is showing: a scroll's id (public, and the stage the
 *  member draws itself from the host's pages), else the room's own id. Never a `t_` or a
 *  saved id — holding a temp id is a write claim (routes/views.ts), and a member holds
 *  nothing of the host's. */
export const memberShowing = (roomId: string, showing: string | null): string | null =>
  showing === null ? null : viewIdKind(showing) === "infinity" ? showing : roomViewId(roomId);

export type ViewIdKind = "builtin" | "temp" | "wall" | "infinity" | "room" | "saved";

/**
 * What kind of thing an id refers to.
 *
 * Exported and used everywhere rather than re-tested per call site: the views route and
 * the devices route each used to parse this for themselves, which is how a device ends up
 * holding an id the views route will not serve.
 */
export function viewIdKind(id: string): ViewIdKind {
  if (id.startsWith(BUILTIN_VIEW_PREFIX)) return "builtin";
  if (id.startsWith(TEMP_VIEW_PREFIX)) return "temp";
  if (id.startsWith(WALL_VIEW_PREFIX)) return "wall";
  if (id.startsWith(INFINITY_VIEW_PREFIX)) return "infinity";
  if (id.startsWith(ROOM_VIEW_PREFIX)) return "room";
  return "saved";
}

/** A built-in is never editable — they are everyone's. The wall is everyone's too: its
 *  squares change hands by purchase, never by edit. Infinity has nothing to edit: its
 *  pages are dealt, not authored. */
export const isViewWritable = (id: string) => {
  const kind = viewIdKind(id);
  // A room is not edited either: its owner PICKS what it plays (a snapshot of some
  // other view), and the pencil would mint a private copy of a moment.
  return kind !== "builtin" && kind !== "wall" && kind !== "infinity" && kind !== "room";
};

// ---------------------------------------------------------------------------
// Who owns a thing: an account, or a signed-out browser.
// ---------------------------------------------------------------------------

/**
 * A signed-out browser's owner id. A signed-in user's is a bare id, so the prefix is the
 * whole of the distinction — written once here because the rule "saving needs an account"
 * is now enforced in several places and a second copy of this string is a second answer.
 */
export const ANON_OWNER_PREFIX = "anon:";

/**
 * Does this owner have an account?
 *
 * Keeping a view is the one thing that requires one. Everything else — building a view,
 * previewing it, putting a built-in or a temporary view on a screen — works signed out,
 * because a temporary view has no owner to check against and holding its id is the whole
 * claim. What an account buys is permanence, which is exactly the thing a browser-local
 * identity cannot honestly promise: clear the storage and the "owner" is gone with it,
 * leaving rows nobody can ever reach again.
 */
export const isAccountOwner = (owner: string | null | undefined): boolean =>
  !!owner && !owner.startsWith(ANON_OWNER_PREFIX);

/**
 * How many screens one operator may have at once.
 *
 * Signed out gets fewer, and not as a nag to sign up: an anon identity lives in one
 * browser's storage, so its screens are unreachable the moment that storage is cleared.
 * Every one of them is a row that can be stranded, and a smaller number bounds how much
 * gets stranded. An account can always come back for its screens, so it is trusted with
 * more.
 *
 * A ceiling rather than a quota — deleting a screen frees the slot immediately, because
 * what is being bounded is how many exist at once, not how many you have ever made.
 */
export const MAX_SCREENS_ANON = 5;

/**
 * The pairing code a waiting screen displays and an operator types back.
 *
 * Both halves live here because both sides need the same answer and they are on opposite
 * ends of a wire: the server mints codes with these (`pending-screens.ts`), and the
 * operator's input box validates against them (`AddScreenCard`). They used to be declared
 * twice, so lengthening a code or widening the alphabet would have left the input box
 * silently truncating and stripping perfectly valid codes.
 *
 * The charset omits `0/O` and `1/I/L`: a code is read across a room off a TV, and those
 * are the pairs that get misread.
 */
export const PAIRING_CODE_CHARS = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
export const PAIRING_CODE_LEN = 4;
export const MAX_SCREENS_ACCOUNT = 8;

/**
 * How long a claim holds a screen against the next person, in ms.
 *
 * A party screen changes hands by being read off the glass, which means the next person to
 * look up can take it out from under whoever just sat down. The hold is the answer: for
 * this long after a claim, that screen is spoken for.
 *
 * Here rather than in the dialog for the reason PAIRING_CODE_CHARS records above — both
 * ends of a wire need the same answer. The screen's dialog offers exactly these, and the
 * server accepts exactly these.
 *
 * Off is FIRST and is the default. A screen nobody has configured must behave the way it
 * does today, and a hold is a lock on somebody else's television.
 */
export const CLAIM_HOLD_CHOICES_MS = [0, 5 * 60 * 1000, 10 * 60 * 1000] as const;

/**
 * The hold this value asks for, or 0.
 *
 * A WHITELIST, not a clamp, and that is the whole point of it. `POST /screens/pending`
 * takes no identity — a screen has none — so the number arrives from whoever chose to send
 * it. Clamping to a ceiling would still let a stranger who guessed a screenId ask for the
 * ceiling; only accepting the three answers the product offers means the worst case is a
 * ten-minute hold somebody could have set from the dialog anyway.
 */
/**
 * A remaining duration as `M:SS`, rounded UP.
 *
 * Shared because the server writes this number into the refusal sentence and the screen
 * counts the same number down on its badge, and two spellings of "2:50" on the two
 * surfaces of one wait is exactly the kind of disagreement nobody notices until a user
 * reports that the TV and the phone disagree.
 *
 * ROUNDED UP so the last second reads `0:01` and not `0:00`. A countdown that sits on zero
 * while still refusing you is the one number here that would read as broken.
 */
export function holdCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

export function parseClaimHoldMs(value: unknown): number {
  return typeof value === "number" &&
    (CLAIM_HOLD_CHOICES_MS as readonly number[]).includes(value)
    ? value
    : 0;
}

/** The screen ceiling for this owner. */
export const maxScreensFor = (owner: string | null | undefined): number =>
  isAccountOwner(owner) ? MAX_SCREENS_ACCOUNT : MAX_SCREENS_ANON;

// ---------------------------------------------------------------------------
// GoonLords Gold — the tier ceilings.
//
// Two tiers, resolved per owner: GOLD (paid, `gold_until` in the future) and CAPPED
// (everyone else while the caps are armed — no grandfathering, by decision: the caps
// bind everyone the day they go on, and what protects existing users is the SEMANTICS,
// not an exemption. Every count check is `< max` (keep what you have, can't add) and
// regions get a growth rule (an over-cap view stays editable, only growing refuses),
// so arming the caps strands nothing — it just makes MORE a Gold thing.)
// The helpers take plain args because shared code can read neither settings nor the DB:
// the server resolves them in `entitlementsFor` (index.ts), the client in useGoldGate
// (lib/gold.ts), and both sides therefore refuse — or don't — with the same arithmetic.
//
// `capsEnabled=false` makes every helper return today's numbers, which is the property
// that lets the caps SHIP DARK: the code is live, the behavior is byte-identical, and
// the admin toggle is the launch.
// ---------------------------------------------------------------------------

/**
 * THE BASIC LINE, SET WHERE THE HEAVY USERS ACTUALLY ARE (2026-09-10).
 *
 * The first cut (3 · 5 · 10 · 9) sat where almost nobody lived: in thirty days 9% of
 * signed-in free users ever met a cap, 80% of the HEAVIEST free users never did, and
 * the cap dialogs produced two Subscribe clicks and no payments. Measured on prod:
 * 51 free users hold ≥3 saved views, 28 hold ≥3 collections, 77 built a ≥9-region wall,
 * 72 ran ≥2 screens — each 3–9% of the free base, and every one of them in the top
 * decile by watch time. That decile is who the paywall is for; the line moves to them.
 * A metered paywall wants 5–10% of engaged users to meet it (the publishers' number),
 * and these four together land there. Bumped CAPS_VERSION so the funnel can tell the
 * two eras apart.
 *
 * Two screens: the second display is the single behaviour that most separates heavy
 * free users from light ones (11×). A phone as a REMOTE is not a screen and never
 * counted. Gold stays "as many as you own".
 */
export const MAX_SCREENS_FREE = 2;
export const MAX_VIEWS_FREE = 3;
export const MAX_COLLECTIONS_FREE = 3;
/**
 * Eight: the free default play layout (grid-2x4) stays Basic's, and the line starts at
 * the square grid — 3×3 is the smallest Gold preset. It was nine, moved there so that
 * 3×3 would be free (the most-built big wall); the data since says the 9-region wall
 * IS the wall the heavy users build (77 owners, all in the top decile), which is exactly
 * where a paywall should stand. Nothing existing is taken: the growth rule keeps every
 * saved 9-region wall editable, it just cannot grow.
 */
export const MAX_REGIONS_FREE = 8;
/**
 * Clips one collection may hold. New with the 2026-09-10 line: nothing bounded it, the
 * measured p90 sat at ~100 anyway (browse pages and clones land in hundreds), and a
 * collection is the one thing on the box that grows a row per act. Basic 100, Gold the
 * hard 500 — a ceiling for everyone, because a 10,000-clip playlist resolves on every
 * play. The virtual Faps collection is not a playlist row and is untouched.
 */
export const MAX_CLIPS_PER_COLLECTION_FREE = 100;
export const MAX_CLIPS_PER_COLLECTION_HARD = 500;
/**
 * Which edition of the Basic line is in force, sent on every gate event so the funnel
 * can split "before" from "after" by a property instead of a deploy timestamp. Bump it
 * whenever a MAX_*_FREE above moves.
 */
export const CAPS_VERSION = 2;
/**
 * The hard region ceiling for EVERYONE, Gold included — sixteen decoders is where a
 * view stops being a wall and starts being a stress test. maximum-dopamine is exactly
 * 16, so every comparison against this is `>`, never `>=`.
 */
export const MAX_REGIONS_HARD = 16;

export interface TierArgs {
  /** The owner id — `anon:<id>` or a users.id. */
  owner: string | null | undefined;
  /** users.gold_until — accounts only; anon owners can never be Gold. */
  goldUntil?: number | null;
  /** settings.gold.capsEnabled. */
  capsEnabled: boolean;
  now: number;
}

export const isGoldTier = (a: TierArgs): boolean =>
  isAccountOwner(a.owner) && (a.goldUntil ?? 0) > a.now;

/** Capped = the caps are armed and the owner isn't Gold. Everyone, account and anon
 *  alike — no grandfathering (see the header above for why nothing strands). */
const isCapped = (a: TierArgs): boolean => a.capsEnabled && !isGoldTier(a);

export const maxScreensForTier = (a: TierArgs): number =>
  isGoldTier(a) ? Number.POSITIVE_INFINITY : isCapped(a) ? MAX_SCREENS_FREE : maxScreensFor(a.owner);
export const maxViewsForTier = (a: TierArgs): number =>
  isGoldTier(a) ? Number.POSITIVE_INFINITY : isCapped(a) ? MAX_VIEWS_FREE : Number.POSITIVE_INFINITY;
export const maxCollectionsForTier = (a: TierArgs): number =>
  isGoldTier(a) ? Number.POSITIVE_INFINITY : isCapped(a) ? MAX_COLLECTIONS_FREE : Number.POSITIVE_INFINITY;
export const maxRegionsForTier = (a: TierArgs): number =>
  isCapped(a) ? MAX_REGIONS_FREE : MAX_REGIONS_HARD;
/** Caps off ⇒ unbounded, as it was before the ceiling existed (the dark-ship property);
 *  Gold ⇒ the hard 500; capped ⇒ 100. */
export const maxClipsPerCollectionForTier = (a: TierArgs): number =>
  isGoldTier(a) ? MAX_CLIPS_PER_COLLECTION_HARD : isCapped(a) ? MAX_CLIPS_PER_COLLECTION_FREE : Number.POSITIVE_INFINITY;
/** The daily wall allowance in wall-minutes: a setting rather than a constant, because
 *  it is a meter to be tuned monthly (5–10% of engaged free users should meet it);
 *  Infinity for Gold and while the caps are dark. */
export const wallMinutesPerDayForTier = (a: TierArgs, wallMinutesFree: number): number =>
  isCapped(a) && wallMinutesFree > 0 ? wallMinutesFree : Number.POSITIVE_INFINITY;

/**
 * The spent screen's wall: the `n` largest regions (ties by authored order) re-laid on
 * the preset grid for that count, identities kept. Render-only — it takes the regions
 * a screen would DRAW and hands back what it draws instead; the saved view is never
 * touched, exactly as the adaptive transpose is never written back. Keeping identities
 * means the surviving players do not remount: the tile that was playing keeps playing,
 * only smaller neighbours drop away. Returns the input untouched when it already fits.
 */
export function degradeRegions(regions: Region[], n: number): Region[] {
  if (!(n >= 1) || regions.length <= n) return regions;
  const keep = regions
    .map((r, i) => ({ r, i, area: r.w * r.h }))
    .sort((a, b) => b.area - a.area || a.i - b.i)
    .slice(0, Math.floor(n))
    .sort((a, b) => a.i - b.i)
    .map((x) => x.r);
  const grid: Partial<Record<number, LayoutPresetId>> = { 2: "2-horizontal", 3: "3-horizontal", 4: "grid-2x2", 6: "grid-3x2", 8: "grid-2x4", 9: "grid-3x3" };
  const preset = grid[keep.length];
  if (!preset) return keep;
  const rects = presetRects(preset);
  return keep.map((r, i) => ({ ...r, x: rects[i]!.x, y: rects[i]!.y, w: rects[i]!.w, h: rects[i]!.h }));
}

/** The vault's two ceilings, by tier. On `isGoldTier` directly rather than `isCapped`:
 *  the vault has its own switch (settings.vault.enabled), and Premium's bigger vault is
 *  a perk that holds whether or not the capacity caps are armed. Anonymous owners have
 *  no vault at all — an upload needs an account to belong to. */
export type VaultSettings = SystemSettings["vault"];
export const vaultBaseBytesForTier = (a: TierArgs, v: VaultSettings): number =>
  !isAccountOwner(a.owner) ? 0 : isGoldTier(a) ? v.baseBytesPremium : v.baseBytesFree;
export const vaultMaxBytesForTier = (a: TierArgs, v: VaultSettings): number =>
  !isAccountOwner(a.owner) ? 0 : isGoldTier(a) ? v.maxBytesPremium : v.maxBytesFree;

/** The four ceilings + the tier bit, resolved for one owner — what the server's
 *  entitlementsFor hands each route, so a handler never rebuilds TierArgs itself. */
export interface Entitlements {
  gold: boolean;
  maxScreens: number;
  maxViews: number;
  maxCollections: number;
  maxRegions: number;
  maxClipsPerCollection: number;
  /** Wall-minutes a UTC day, Infinity for Gold and while the caps are dark. */
  wallMinutesPerDay: number;
}

export const builtinViewId = (templateId: string) => `${BUILTIN_VIEW_PREFIX}${templateId}`;

/** The template behind a `builtin:` id, or null if the id names no built-in there is. */
export function templateForViewId(id: string): ViewTemplate | null {
  if (viewIdKind(id) !== "builtin") return null;
  const tid = id.slice(BUILTIN_VIEW_PREFIX.length);
  return MONITOR_TEMPLATES.find((t) => t.id === tid) ?? null;
}

/**
 * Retired `?setup=` ids that still have links out there, and the setup they now open.
 * Silent: the link plays the replacement with no notice. Only `?setup=` — a `builtin:`
 * view id is not aliased (templateForViewId), and a hidden setup stays out of every list.
 */
export const SETUP_ALIASES: Readonly<Record<string, string>> = {
  "safe-for-work": "fully-clothed",
};

/** The offered setup a `/screen?setup=<id>` link opens, following SETUP_ALIASES, or null. */
export function templateForSetup(id: string | null): ViewTemplate | null {
  if (!id) return null;
  const target = Object.hasOwn(SETUP_ALIASES, id) ? SETUP_ALIASES[id] : id;
  return MONITOR_TEMPLATES.find((t) => t.id === target) ?? null;
}

/**
 * Materialise a built-in as a View, with STABLE ids.
 *
 * `viewFromTemplate` mints a fresh `makeId()` per region, which is right for a copy
 * someone is about to own and edit. It is wrong here: a built-in is resolved afresh on
 * every read, so random region ids would mean a screen polling its view sees a different
 * set of regions each time and remounts every tile. Deriving them from the template id
 * and index makes re-resolving the same built-in return the same view.
 *
 * The timestamps are frozen at 0 for the same reason — a built-in is not "edited", and a
 * resolve-time `Date.now()` would make it perpetually the newest thing in any list.
 */
export function builtinView(
  template: ViewTemplate,
  resolveFeed: (feed: TemplateFeed) => SourceConfig,
): View {
  const base = viewFromTemplate(template, resolveFeed, 0);
  // Stable ids: a built-in is the same view for everybody, so its regions cannot be a
  // fresh UUID per resolution or nothing could refer to one across two requests.
  const renamed = base.regions.map((r, i) => ({ ...r, id: `${template.id}:r${i}` }));
  return {
    ...base,
    id: builtinViewId(template.id),
    // Re-pointed AFTER renaming, not carried over. `viewFromTemplate` wires followers to
    // the UUIDs it minted, and renaming the regions underneath left every `syncWith`
    // pointing at an id that no longer existed — so Checkers resolved zero followers and
    // played twelve independent clips, silently, because a dangling follow is defined to
    // resolve to "not synced" rather than to fail.
    regions: applySyncGroups(renamed, template.syncGroups, template.syncFlip),
    createdAt: 0,
    updatedAt: 0,
  };
}

/**
 * A collection a feed can point at — Editor's Choice or one of the user's own.
 *
 * In shared rather than in the client because the SERVER now resolves built-in feeds too:
 * a `builtin:` id has to become a real view with real sources before a screen can play it,
 * and that must be the same resolution the client would have done or the two disagree
 * about what a quick setup is.
 */
export interface CollectionOption {
  id: string;
  name: string;
  clipCount: number;
  editorsChoice?: boolean;
  own?: boolean;
}

/** Resolve one template feed to a concrete source against the available collections. */
export function resolveFeed(feed: TemplateFeed, collections: CollectionOption[]): SourceConfig {
  if (feed.kind === "source") return feed.source;
  if (feed.kind === "collectionByName") {
    const match = collections.find((c) => c.name.toLowerCase() === feed.name.toLowerCase());
    return match ? { kind: "playlist", playlistId: match.id } : feed.fallback;
  }
  // editorsChoice: the Nth Editor's-Choice collection, else the portable fallback.
  const ec = collections.filter((c) => c.editorsChoice);
  const pick = ec[feed.index];
  return pick ? { kind: "playlist", playlistId: pick.id } : feed.fallback;
}

/**
 * Materialise a template into a view.
 *
 * Feeds are applied per tile and cycled when the template lists fewer than the layout
 * has — which is what puts one breast size per COLUMN in the Tit Ladder, since
 * grid-3x4 is row-major over four columns and tiles c, c+4 and c+8 all take feeds[c].
 *
 * `resolveFeed` turns a TemplateFeed into a concrete source. The client passes one that
 * can look up collections by name; a caller with no collection list passes one that
 * only handles `source` feeds and falls back.
 */
/**
 * Point each region at the leader of its sync group, in place.
 *
 * Lives here, and is called by BOTH template→regions paths, because there are two of
 * them: the editor builds regions one way and the server builds a built-in view another.
 * A copy of this in one of them would mean Checkers played in step in the editor's
 * preview and twelve independent clips on the actual wall — the kind of split that only
 * shows up on the screen nobody is testing against.
 *
 * A second pass rather than part of the map: a follower needs its leader's id, and the
 * ids do not exist until the regions do.
 */
export function applySyncGroups(
  regions: Region[],
  groups: readonly number[] | undefined,
  /** Group numbers whose followers MIRROR the leader (syncMode "flip") rather than
   *  showing the identical frame — e.g. two towers flanking a spine as mirror images. */
  flip?: readonly number[],
): Region[] {
  if (!groups) return regions;
  const flipSet = new Set(flip ?? []);
  const leaderOf = new Map<number, string>();
  groups.forEach((group, i) => {
    if (regions[i] && !leaderOf.has(group)) leaderOf.set(group, regions[i].id);
  });
  groups.forEach((group, i) => {
    const region = regions[i];
    const leaderId = leaderOf.get(group);
    // A group of one leads itself; a region synced to itself is refused downstream, so
    // skip rather than emit something that has to be ignored later.
    if (!region || !leaderId || leaderId === region.id) return;
    region.syncWith = leaderId;
    region.syncMode = flipSet.has(group) ? "flip" : "normal";
  });
  return regions;
}

export function viewFromTemplate(
  template: ViewTemplate,
  resolveFeed: (feed: TemplateFeed) => SourceConfig,
  now: number,
): Omit<View, "ownerId"> {
  const rects = presetRects(template.layout);
  return {
    id: makeId(),
    name: template.name,
    icon: template.emoji,
    // Templates that don't care about shape leave the monitor's own aspect alone in
    // the editor; a view always has one, so it needs a concrete default here.
    aspect: template.aspect ?? { w: 16, h: 9 },
    ...(template.adaptiveLayout ? { adaptiveLayout: true } : {}),
    regions: applySyncGroups(
      rects.map((rect, i) => {
        const base = regionFromRect(rect);
        return {
          ...base,
          source: resolveFeed(template.feeds[i % template.feeds.length]),
          // A quick setup arrives AUDIBLE: every panel soundOn at the half default
          // (absence = DEFAULT_SOURCE_VOLUME), so unmuting the screen makes noise
          // without blasting. The
          // strict default-off is for regions a person authored; a template's wall
          // has nobody to hover each tile first. The screen's master mute is still
          // what keeps the room quiet until asked. Stamped here — the one funnel
          // every builtin resolution walks — so a fork of a quick setup keeps its
          // sound instead of falling silent on copy.
          soundOn: true,
        };
      }),
      template.syncGroups,
      template.syncFlip,
    ),
    createdAt: now,
    updatedAt: now,
  };
}

// ---------------------------------------------------------------------------
// Views — a named screen configuration you can put on a device.
//
// This replaced three overlapping shapes: SavedLayout (a monitor without the
// device fields), Monitor (a view with them), and MonitorTemplate (the recipe
// form). A view is the noun; a device is where you point it.
// ---------------------------------------------------------------------------

/**
 * A view: a named screen configuration you can put on a device.
 *
 * This is the one shape that used to be three — SavedLayout (a monitor without the
 * device fields), Monitor (a view with them), and MonitorTemplate (the recipe form).
 * A view is the noun; a device is where you point it.
 */
export interface View {
  id: ID;
  name: string;
  /** Emoji shown on the view card; falls back to a default glyph when absent. */
  icon?: string;
  aspect: { w: number; h: number };
  /**
   * When true, a SCREEN whose orientation disagrees with `aspect` renders
   * `transposeRegions(regions)` instead — columns become rows on a portrait phone.
   * Render-time only: nothing ever persists transposed rects, the editor always
   * shows the authored layout, and previews stay authored-shape. Absent = off.
   */
  adaptiveLayout?: boolean;
  regions: Region[];
  createdAt: number;
  updatedAt: number;
  /** A user id, or `anon:<id>` for a signed-out browser. Server-managed. */
  ownerId?: ID;
}

export interface ViewsResponse {
  views: View[];
}

// ---------------------------------------------------------------------------
// Devices — a screen's persistent identity, separate from what it's showing.
// ---------------------------------------------------------------------------

/**
 * What a device is currently showing. A view id either way — whether it has a row or
 * only lives in memory is the server's business, not the screen's.
 */
export interface ViewRef {
  viewId: ID;
}

/**
 * A screen under an operator's control.
 *
 * Identity travels in the tab's own URL (`/view/d/<id>`) rather than localStorage,
 * which is per-origin: two monitors driven from one computer share storage and would
 * otherwise be one device.
 */
export interface Device {
  id: ID;
  name: string;
  kind: DeviceKind;
  /** The operator who controls it — a user id or `anon:<id>`. */
  ownerId: ID;
  /** Null before it has been given anything to show. */
  showing: ViewRef | null;
  lastSeen: number;
  /**
   * True when the device redeemed a join code, false when the controller opened it
   * locally. This is what decides its lifetime: a claimed device survives closing the
   * site so re-joining restores it, an unclaimed one is deleted when its tab goes.
   */
  claimed: boolean;
}

/**
 * A device as `GET /devices` lists it: the row plus the screen code its tab is wearing
 * right now. The code is JOINED from the in-memory pending-screens registry, not stored —
 * it exists only while a live screen tab announces it, so it is null for a device whose
 * tab is closed or lapsed. The remote's rail leads with it because it is the one identity
 * a person in the room can match against the glass; "Screen 2" names nothing.
 */
export interface DeviceListing extends Device {
  code: string | null;
}

export interface DevicesResponse {
  devices: DeviceListing[];
}

/** How long a view's name may be. Clamped server-side on save and enforced by the name
 *  input, so the two cannot disagree about when a title gets cut. (Was PRESET_NAME_MAX,
 *  from when the editor called a saved view a preset — it only ever measured view names.) */
export const VIEW_NAME_MAX = 80;

// ---------------------------------------------------------------------------
// REST API DTOs  (all under /api)
//   POST   /api/source/fetch SourceFetchRequest -> SourceFetchResponse
//   GET    /api/source/redgifs/media?u=...  -> streamed media (Range supported)
// ---------------------------------------------------------------------------

export interface SourceFetchRequest {
  source: SourceConfig;
  cursor?: string;
  /**
   * The screen asking, when a screen is asking (2026-09-09). The only source that reads
   * it is the vault's `uploads:<uid>`: a display carries no bearer, so the device is how
   * the server learns whose screen this is — and it counts only when the device belongs
   * to an account AND the view it is showing actually carries that source. A bare id is
   * no capability: holding a device UUID must never list anyone's vault.
   */
  deviceId?: ID;
}

export interface SourceFetchResponse {
  items: MediaItem[];
  nextCursor?: string;
  /** The colour a Colorwave page was dealt in — a COLORWAVE_COLORS name — so the stage
   *  can frame the wall in it. Only the colorwave source sets it; roulette's keyword
   *  stays the server's (hidden tags), a colour is nobody's secret. */
  color?: string;
  /** A Build a Slut round's candidates, one per item, keyed by clipKey (the route filters
   *  banned items after the adapter, so an index would drift). Only the build source sets it. */
  picks?: BuildPick[];
}

/** A RedGifs tag with its approximate gif count (when known). */
export interface RedgifsTag {
  tag: string;
  gifs?: number;
}

export interface ApiError {
  error: string;
  /**
   * Seconds to wait before retrying, on a refusal that clears on its own.
   *
   * 429 (a source cooldown) and 423 (a screen held by whoever claimed it). NOT on 409:
   * a full screen fleet does not free itself, it needs the operator to delete one, and a
   * countdown there would be a promise nothing keeps.
   */
  retryAfterSec?: number;
  /**
   * Set on a refusal that GoonLords Premium would lift — a Basic-tier capacity cap (a 409:
   * the same call succeeds once something is deleted) or a members-only surface such as
   * the Premium Wall (a 403: the same call succeeds once the caller is a member). The
   * client forks on this flag to open the upsell modal instead of a plain error — a typed
   * signal, because string-matching the sentence is how copy edits break UIs.
   */
  goldUpsell?: true;
  /**
   * Set on an upload refused because the caller's vault is full (a 409: the same call
   * succeeds once something is deleted or a step is bought). A typed flag with no
   * payload — the client already holds GET /vault and picks the door itself: expand,
   * delete, or (a Basic account at Basic's ceiling) Premium, which arrives as goldUpsell
   * on POST /vault/expand rather than here.
   */
  vaultFull?: true;
}

// ---------------------------------------------------------------------------
// WebSocket protocol  (single endpoint: /ws)
//
//   Display device  -> hello{role:'display', viewId, deviceId?}
//   Server          -> config{view}              (current config for that view)
//   Control panel   -> hello{role:'control', token?, anonId?}
//   Control panel   -> update{view}              (live edit; server relays to displays)
//   Control panel   -> command{deviceId, cmd}    (remote control; server relays)
//   Server          -> error{message} on bad input
//
// A display's room key is the VIEW it shows, because several screens can show one view
// and an edit is addressed to the view. A command's key is the DEVICE, because "flash so
// I can tell which screen you are" is meaningless broadcast to every screen at once.
//
// The control hello carries identity MATERIAL (the same bearer token and anon id the REST
// calls send), never a resolved owner id: the server resolves it with the same function
// registerViewRoutes uses, so a socket cannot assert who it is.
// ---------------------------------------------------------------------------

export type WsClientRole = "display" | "control";

export type DisplayCommand =
  | { kind: "reload" }
  /**
   * Point this screen at a different view, IN PLACE.
   *
   * The nudge a retarget actually needs. It used to be `reload`, for a real reason — a
   * display socket joins its view's room at connect time and the server has no message
   * that moves a socket between rooms, so the write alone left the screen playing the old
   * view. Reloading the page rebuilt the socket against the new assignment and worked.
   *
   * It also took a sledgehammer to the page to change one string, and the collateral was
   * FULLSCREEN: a reload always leaves it, and re-entering needs a fresh user gesture, so
   * changing what a fullscreen television was playing un-fullscreened it every time. The
   * screen re-joins the room by itself when its view id changes; that is all a retarget
   * ever needed, and it carries the id so the screen does not have to go and ask.
   *
   * `reload` stays in this union: it is still right for a genuine restart, it is just not
   * what pointing a screen somewhere means.
   */
  | { kind: "showing"; viewId: ID }
  | { kind: "pause" }
  | { kind: "resume" }
  /**
   * Sound on this screen. `on: true` mutes. Sound is a property of the screen — the device
   * with the speaker — so the remote sets it here rather than in the view, and the screen
   * reports what it actually managed back on its heartbeat (a browser can refuse to unmute
   * a page nobody has touched; see DevicePresence.muted).
   */
  | { kind: "mute"; on: boolean }
  // There is no "next" any more. It was the remote's Skip button — removed with it: a
  // region advances on its own clock, and a hover-advance on the screen itself covers
  // the "not this clip" itch without a command on the wire.
  // The identify overlay, so the user can tell which screen this is. STATEFUL now: the
  // remote holds it up for as long as its row is selected. The contract is pulse/hold —
  // a bare {identify} lights the overlay for a hold window and every re-send extends it;
  // {on:false} drops it at once. The remote pulses while selected, so a remote that dies
  // mid-selection leaves the TV lit for at most one hold window, never forever.
  | { kind: "identify"; on?: boolean };

/**
 * A live "goon session" for an operator. One at a time, shared by every screen that
 * operator has joined (synced over the WebSocket). Works signed out, against the
 * `anon:<id>` identity. `views` counts clip-plays across all their screens while it runs.
 */
export interface GoonSession {
  id: ID;
  ownerId: ID;
  startedAt: number; // epoch ms
  endedAt?: number; // set on the summary returned when a session is stopped // optional goal // optional goal
  edges: number;
  views: number;
  /** Estimated bytes of media played during the session ("GB consumed"). */
  bytes: number;
  /** Epoch-ms timestamp of each edge, for the summary's edge timeline. */
  edgeTimes: number[];
  /** Most-played clip of the session — only set on the stop summary. */
  favoriteClip?: {
    key: string;
    /** A real poster image, when the clip has one. NEVER the video url — see below. */
    posterUrl?: string;
    /** The clip itself, for the ~8% of the catalog with no poster: a muted
     *  `preload="metadata"` video shows its first frame, which is what a poster is. */
    url?: string;
    views: number;
  };
}

export interface StartGoonSessionRequest {
}

/**
 * The width at which a screen stops being hand-held and gets the roomy bar.
 *
 * It is Tailwind's `lg` in a number, and it has to exist as one because two behaviours
 * cannot be expressed in a class: whether the pairing CODE CARD and the wall's INTRO
 * DIALOG open by themselves. Both are television affordances — a code for pairing a
 * second device, a card of rules over a wall you can still see around — and on a
 * hand-held both land on top of the thing they are about.
 *
 * Keep it equal to the `lg:` breakpoint — the width below which the bar is a phone's
 * (the site bar's own nav and ☰ still collapse there, even now that the screen's
 * cluster is one row at every width). A gate that disagreed with the bar would auto-open
 * a dialog on exactly the widths that had collapsed to make room.
 */
export const BAR_CUTOFF_PX = 1024;

export interface GoonSessionResponse {
  session: GoonSession | null;
}

/**
 * POST /api/session/stop — the session, plus what it paid in Faps when Faps is on and the
 * owner is an account. `earned` is THIS session's share of the settle: unsettled sessions
 * settle oldest-first against the day's cap, so an older one can eat the cap first and
 * this one lands 0 with `capped` — it pays tomorrow. Absent when nothing was settled for
 * it (an idle timer is not watching) or Faps is off. Only the caller sees this; the ws
 * fan-out and the summary route carry no Faps.
 */
// GoonSessionStopResponse used to carry `faps?: {earned, capped, balance}` — what THIS
// session paid in watch-time Faps. Removed 2026-09-09: watch-time earning no longer
// ties to a session's lifecycle at all (it settles off cumulative watch_events on the
// wallet's own read), so a session stopping has nothing of its own left to report.
export type GoonSessionStopResponse = GoonSessionResponse;

// ---------------------------------------------------------------------------
// The session summary — everything the post-session card and recap draw.
//
//   GET /api/session/summary?id=<id>  ->  GoonSessionSummaryResponse
//
// A SEPARATE READ FROM `POST /session/stop`, deliberately. Stop's response is on the hot
// path: it feeds the ws hub and the PostHog `session_ended` event, and every one of the
// aggregates below would be a join it does not need. Addressing the summary by id also
// means a card can be re-opened, and makes a session-history surface free later.
//
// EVERY FIELD DEGRADES. A session can be anonymous (`anon:<id>`), can have played nothing,
// and — for sessions that predate first_viewed_at — can have no exact climb. The card is
// the only thing that decides what to hide; the server reports what it has and says so
// with `ranked`, `approximateClimb` and nullable highlights rather than inventing values.
// ---------------------------------------------------------------------------

/** The earned ladder for a single session. Distinct from ADDICTION_TIERS, which ranks a
 *  whole ACCOUNT — this ranks one sitting. Ordered low → high; `min` is on the 0–100
 *  session score. Lives in shared so the card and the server name a tier identically. */
export const SESSION_TIERS: ReadonlyArray<{ name: string; min: number }> = [
  { name: "Barely Warmed Up", min: 0 },
  { name: "Casual Gooner", min: 20 },
  { name: "Getting There", min: 40 },
  { name: "Deep In It", min: 60 },
  { name: "Seasoned Gooner", min: 75 },
  { name: "Certified Goon Lord", min: 90 },
];

/** The tier name for a session score. */
export function sessionTitleFor(score: number): string {
  let name = SESSION_TIERS[0].name;
  for (const t of SESSION_TIERS) if (score >= t.min) name = t.name;
  return name;
}

/**
 * Below this many COMPLETED sessions platform-wide, a percentile is noise — with six
 * sessions on record "out-gooned 83%" means "beat five", which is a lie told in the
 * grammar of a statistic. The summary sets `ranked: false` under the floor and the card
 * shows the tier alone.
 */
export const SESSION_RANK_FLOOR = 25;

/** A clip that earned a slot on the card, with the metric that earned it. */
export interface SessionHighlightClip {
  clip: MediaItem;
  /** Plays during this session. */
  views: number;
  /** Edges credited to this clip during this session. */
  edges: number;
  /** Seconds on the glass this session. 0 on a session from before these were kept. */
  seconds?: number;
  /** Fap taps on it during this session (signed-in only). */
  taps?: number;
}

/** One slice of the session's taste breakdown — a facet value and its share of clips. */
export interface SessionFacetSlice {
  /** The raw tag, e.g. `race:latina`. */
  tag: string;
  /** Display label via `tagLabel`. */
  label: string;
  /** Clips in this session carrying it. */
  clips: number;
  /** Share of the session's tagged clips, 0–1. */
  share: number;
}

/** One step of the cumulative-unique-clips curve: at `at`, `unique` clips had been seen. */
export interface SessionClimbPoint {
  at: number;
  unique: number;
}

/** Where one session's number falls among all completed sessions. */
export interface SessionRank {
  /** % of completed sessions this one strictly beats. */
  pct: number;
  /** 1-based rank (1 = best). */
  rank: number;
}

export interface GoonSessionSummary {
  session: GoonSession;
  /** Distinct clips played. `session.views` counts plays, so views ≥ uniqueClips. */
  uniqueClips: number;
  /** Σ (clip duration × plays) — screen time, ESTIMATED. Nothing records real dwell on a
   *  wall surface: watch_events is written only by Goon Fuel and carries no session id. */
  estimatedWatchSec: number;
  /** 0–100, a blend of this session's percentiles. See `SESSION_RANK_FLOOR`. */
  score: number;
  title: string;
  /** False when the platform has too few completed sessions to rank against; the card
   *  then shows the tier without the "out-gooned X%" hook. */
  ranked: boolean;
  totalSessions: number;
  duration: SessionRank;
  edges: SessionRank;
  clips: SessionRank;
  /** Rank among THIS owner's own sessions by duration, and how many they have. */
  ownRank: number;
  ownTotal: number;
  /** Which numbers are this owner's personal best, e.g. ["duration", "edges"]. */
  personalBests: string[];
  /** The climb, oldest → newest. Empty when nothing played. */
  climb: SessionClimbPoint[];
  /** True when the climb was rebuilt from `last_viewed_at` because the session predates
   *  `first_viewed_at` — the shape is right, the early steps sit too late. */
  approximateClimb: boolean;
  /** Edge shape, all derived from `session.edgeTimes` (ms). Null when there were none. */
  msToFirstEdge: number | null;
  longestEdgeGapMs: number | null;
  shortestEdgeGapMs: number | null;
  /** Start of the densest 5-minute window, as ms from session start, and its edge count. */
  peakWindowStartMs: number | null;
  peakWindowEdges: number;
  mostSeen: SessionHighlightClip | null;
  mostEdged: SessionHighlightClip | null;
  /** Null when signed out — faps need an account. */
  mostFapped: SessionHighlightClip | null;
  /** Clips fapped and loop-presses during the session (signed-in only; 0 otherwise). */
  faps: number;
  loops: number;
  /** Taste, richest facet first. */
  facets: SessionFacetSlice[];
  /** Clips played with sound vs without (by the measured `audio:` facet). */
  audible: number;
  silent: number;
  /** Shape mix of what played. */
  portrait: number;
  landscape: number;
  /** Whether the owner is a real account — the card hides fap-derived slots when false. */
  signedIn: boolean;
}

export interface GoonSessionSummaryResponse {
  summary: GoonSessionSummary | null;
}

/**
 * One row of the profile's session history — enough to read the list and to act on a
 * row (the card is fetched by id through /session/summary; delete is by id too). Only
 * ENDED sessions appear: the running one is on every screen's bar already, and deleting
 * it from a list would leave that bar showing a ghost.
 */
export interface GoonSessionHistoryRow {
  id: string;
  startedAt: number;
  endedAt: number;
  durationSec: number;
  edges: number;
  /** Clips viewed during the session (the session's own tally, not the lifetime one). */
  views: number;
}

export interface GoonSessionHistoryResponse {
  sessions: GoonSessionHistoryRow[];
}

/** A "fap" is a user's like/save of a clip; it collects into their Faps. */
export interface FapStatusResponse {
  /** Whether the signed-in user has fapped this clip (false when anonymous). */
  fapped: boolean;
  /** Total faps across all users. */
  count: number;
  /** On the TOGGLE's reply only, when the toggle landed ON: the same fields
   *  FapClickResponse carries, so a tile that toggles earns like a tile that taps. */
  fapsEarned?: 0 | 1;
  fapsAmount?: number;
  fapsBalance?: number;
  capped?: true;
}


export type WsMessage =
  /* A screen announces which view it plays and which device it is, INDEPENDENTLY. Both
     are optional in the type and at least one is required by the server, because a paired
     screen that has not been given anything yet has only the device half — and it still
     has to connect, since being connected is how it reports that it is switched on.
     `viewId: ID` here was the contract half of a deadlock: a screen needed a view to
     appear online, and the only control that could give it one was disabled until it
     appeared online. */
  | { t: "hello"; role: "display"; viewId?: ID; deviceId?: ID }
  | { t: "hello"; role: "control"; token?: string; anonId?: string }
  /*
   * GOON ROOMS (2026-09-19). A THIRD socket role: a room member. Identity-carrying like
   * the control hello (a token the server resolves; never an asserted username), and
   * separate from the display socket on purpose — a display hello names no one, and a
   * room's roster is exactly "the sockets that proved who they are". The room's VIEW
   * (what plays) still rides the display socket as `config`, fed by the host's
   * `room_view`; a scroll rides here as `room_page`. These carry the people and the mirror.
   * Every server-origin frame below stamps identity (`from`, `username`) server-side.
   */
  | { t: "hello"; role: "room"; roomId: ID; token?: string }
  | {
      t: "room_state";
      roomId: ID;
      name: string;
      public: boolean;
      you: string;
      role: RoomRole;
      status: RoomStatus;
      members: RoomMember[];
      /** Everyone watching — the faces above (one per account) PLUS the signed-out
       *  viewers, who watch and count but have no face, no chat and no crown. */
      watching: number;
      messages: RoomChatMessage[];
      /** The room's recent ACTIVITY lines (see RoomActivity), for a late joiner. */
      activity: RoomActivity[];
      /** THE SYNC (MR B): what each of the host's regions is playing, keyed by region id,
       *  and whether the host has the wall paused. Empty until the host's tiles reveal. */
      paused: boolean;
      regions: Record<string, RoomRegionState>;
      /** THE MIRROR: what the host is showing (masked for a member — memberShowing) and,
       *  while that is a scroll, the host's pages and the page on their glass. */
      showing: string | null;
      stage: RoomStageState | null;
      /** A house room, and whether its chat is off. Absent on a member room. */
      house?: true;
      chatOff?: true;
    }
  | { t: "room_roster"; members: RoomMember[]; watching: number }
  /*
   * THE SYNC. The host's tile revealed a clip (RegionPlayer publishes it to the local
   * SyncBus the moment it is on screen; the room stage forwards that here). The server
   * stamps `startedAt` on its own clock and a `seq`, and hands the result to every
   * member as `room_playing`; a member's DrivenRegion seeks to (serverNow − startedAt).
   * Approximate by design — RTT plus first-frame decode, re-aligned on every clip.
   */
  | { t: "room_reveal"; regionId: string; item: MediaItem; src: string }
  | { t: "room_playing"; regionId: string; state: RoomRegionState }
  // owner -> server -> every member: the host paused (or resumed) the wall.
  | { t: "room_pause"; paused: boolean }
  /*
   * THE MIRROR: a room is the host's screen. The host's DisplayApp runs its real view
   * exactly as /screen does and reports what it has: its current view (layout + sources,
   * a quick setup's session-local edits included) becomes the room's snapshot, pushed to
   * every member's display socket as `config` under the room's own id; a scroll is
   * mirrored page by page, with the `no` of the page on the host's glass. Never written
   * to the row: the row's view_id is the SHOWING, which `room_showing` carries on a re-point.
   */
  | { t: "room_view"; view: View }
  /*
   * ACTIVITY (2026-09-19): what people are DOING in the room, said in grey among the chat
   * — "x started a goon session", "x edged ×3", "x fapped 5 clips", the host "changed the
   * view to …", paused, removed somebody. A member tells the hub the acts only its own
   * screen knows (a session, an edge); the hub stamps the actor itself, makes the lines
   * for the acts it sees (a crown, a re-point, a pause, a kick), COALESCES a spam of one
   * kind from one person into one line with a count, and hands every line to everyone.
   */
  | { t: "room_act"; kind: "session_start" | "session_end" | "edge"; minutes?: number }
  | { t: "room_activity"; activity: RoomActivity }
  | { t: "room_page"; page: RoomPage }
  | { t: "room_active"; active: number }
  | { t: "room_showing"; viewId: string | null }
  // member -> server: a line of chat, or an image the room's own upload door issued.
  | { t: "room_chat"; body?: string; imageKey?: string }
  | { t: "room_message"; message: RoomChatMessage }
  // member -> server: my camera is on/off (the server enforces the cap, ROOM_CAM_CAP).
  | { t: "room_cam"; on: boolean }
  // server -> one member: your camera was switched off (the owner, or the cap).
  | { t: "room_cam_off" }
  // WebRTC signalling between two members, relayed by pid. `from` is stamped.
  | { t: "room_rtc"; to: string; from?: string; sdp?: RoomRtcSdp; ice?: RoomRtcIce }
  // owner -> server: act on a member, by username (every socket of theirs).
  | { t: "room_mod"; action: RoomModAction; username: string }
  // server -> one member, then the socket closes: you are out.
  | { t: "room_kicked"; reason: "kicked" | "banned" | "ended" }
  // A fap by anyone: the crown BURST for everyone else — never a fap on their clip.
  | { t: "room_fap"; regionId: string; username?: string }
  // server -> every member: the owner left (grace expired) or closed it.
  | { t: "room_closed" }
  | { t: "room_refused"; reason: RoomRefusal; message: string }
  | { t: "config"; view: View }
  | { t: "update"; view: View }
  | { t: "command"; deviceId: ID; cmd: DisplayCommand }
  // The operator's live goon session (or null when none is running). Broadcast to every
  // display whose device belongs to that owner, so their nav-bar timers stay in lockstep.
  | { t: "session"; ownerId: ID; session: GoonSession | null }
  // Live presence per DEVICE for one owner: whether a screen is on it right now plus the
  // best latency that screen reported. Pushed to that owner's controls on connect and
  // whenever a display joins/leaves/reports latency.
  | { t: "presence"; ownerId: ID; devices: Record<ID, DevicePresence> }
  // Display -> server: this screen's measured round-trip latency, and whether it is muted.
  // The server already knows which device the socket is, so the message carries no id.
  // `muted` rides the heartbeat rather than a message of its own because it is the same
  // kind of fact — a live property of the screen, surfaced on its presence for the remote.
  | { t: "ping"; ms: number; muted?: boolean; sent?: number }
  // The heartbeat's echo. `sent` is the client's own clock coming back, so the screen
  // derives its round-trip from ONE message a beat — the HTTP `/api/ping` that used to
  // measure it was a second heartbeat riding beside this one (2026-09-10).
  | { t: "pong"; sent: number }
  | { t: "error"; message: string };

/** Live presence for one device: is a screen on it, how far away is it, is it muted. */
export interface DevicePresence {
  online: boolean;
  ping?: number;
  /** What the screen last reported about its own sound. Absent until it has said. */
  muted?: boolean;
}

// ---------------------------------------------------------------------------
// System settings + admin  (single-password admin panel at /admin)
//
//   GET    /api/settings            -> SystemSettings          (public, read-only)
//   POST   /api/admin/login  {password}  -> AdminLoginResponse
//   POST   /api/admin/logout             -> { ok: true }        (auth)
//   GET    /api/admin/session            -> { ok: true }        (auth)
//   GET    /api/admin/settings           -> AdminSettingsResponse (auth)
//   PUT    /api/admin/settings  Partial<SystemSettings> -> AdminSettingsResponse (auth)
//
// Auth is a bearer token returned by /login and sent as `authorization: Bearer`.
// ---------------------------------------------------------------------------

/** Server-wide behavior, editable from the admin panel. All fields are public-safe. */
export interface SystemSettings {
  /** The client birthday/age gate. */
  ageGate: { enabled: boolean; minAge: number };
  /** The floating "goon fuel" player on the control screens. */
  goonFuel: { enabled: boolean; source?: SourceConfig };
  /** Third-party display advertising (JuicyAds). */
  ads: AdSettings;
  /** Which labelshop axes ship to the catalog as `manual` tags. See LabelshopSettings. */
  labelshop: LabelshopSettings;
  /** GOONCHA: the image-grid robot check gating register and login. The toggle is the
   *  kill switch — off, auth takes no token and the client shows no checkbox. */
  gooncha: { enabled: boolean };
  /**
   * Wall playback on weak devices (issues #13/#14, 2026-10-28). `phoneDprCap` and
   * `phoneRungCeiling` cap what a PHONE tile asks for (see isPhoneClass): a DPR-3 phone
   * multiplied tiles up to the 1920 rung, which it cannot decode four of. `decodeBudget`
   * is the kill switch for the live budget (probe + dropped-frame monitor + decode slots,
   * client/src/display/playbackBudget.ts). Read through displaySettings(), which clamps.
   */
  display: DisplaySettings;
  /**
   * GoonLords Gold — the paid tier. TWO switches, deliberately: `enabled` is the SELLING
   * switch (the Gold dialog's CTA, the menu rows, the upsell prompts), `capsEnabled` arms
   * the Basic-tier capacity caps for EVERYONE who isn't Gold (no grandfathering — the
   * `< max` semantics and the region growth rule are what keep existing over-cap
   * holdings usable) — so the supporter era can sell with no caps at all, and the caps
   * can ship dark and be flipped later without a deploy.
   *
   * THESE PRICES ARE NOW THE AUTHORITY, which they were not before. They used to be
   * display copy beside a static hosted checkout link that set its own amount, so the two
   * could disagree and the dialog could quote a figure the till did not charge. We create
   * the payment intent ourselves now and send the amount, so what is written here is what
   * gets charged — in dollars, because that is what the dialog renders; cents happen once,
   * at the API boundary (see goldTierCents).
   *
   * Everything here is public-safe — GET /api/settings serves the whole object — which is
   * why every PayzMoon secret lives in env and never in this block.
   */
  gold: {
    enabled: boolean;
    capsEnabled: boolean;
    price7d: number;
    price30d: number;
    price90d: number;
    price365d: number;
    /**
     * A static hosted checkout link, kept ONLY as the fallback for a box with no API keys:
     * with keys we mint a per-user intent instead, which is the whole reason the buyer no
     * longer has to type their username at somebody else's checkout and hope. Blank and
     * keyless = "Gold is almost here" in the dialog.
     */
    paymentUrl: string;
    /**
     * THE DAILY WALL ALLOWANCE (2026-09-10), in WALL-MINUTES: minutes watched × regions
     * playing. Basic gets this many a UTC day; when it is spent the screen shrinks to
     * `degradedRegions` tiles with sound off until midnight UTC — never a stop. Gold has
     * no allowance. Wall-minutes rather than wall-clock so fewer tiles last longer
     * ("sixteen tiles for an hour, eight for two, four for four" at 960), and rather
     * than bytes because bytes depend on the rung a device pulls and reward loops.
     * Watching the Community Wall or the Premium Wall never counts (the owner's rule).
     * Measured on prod before the line was drawn: free peak-day p50 81 · p90 560 ·
     * p95 1,074 wall-minutes; Gold p50 1,437 — 960 touches 5% of active free users.
     */
    wallMinutesFree: number;
    /** How many tiles a spent screen keeps. Four: a 2×2 is still a wall. */
    degradedRegions: number;
  };
  /**
   * The Community Wall — squares on the shared home screen, sold as one-off placements.
   *
   * `enabled` is the SELLING switch only: the wall itself always renders (open squares
   * play random catalog clips), the way gold's caps and selling are separate acts. Prices
   * are dollars and are the authority, exactly like gold's — cents happen once, at
   * wallTierCents. Public-safe like everything in this object: the board displays every
   * number here on the glass anyway.
   *
   * `uploadsEnabled` lives on THIS block alone and governs uploads for every wall: an
   * upload is a person's media, attached to whichever square they buy, so one switch is
   * the honest shape — a per-wall copy would be a knob that does nothing.
   */
  wall: WallSettings & {
    /**
     * Whether a buyer may bring their OWN media to a square, as opposed to picking from
     * the catalog. A second switch rather than a mode of the first, because the two
     * carry different risk entirely: selling squares moves money, accepting uploads
     * publishes a stranger's pixels on everyone's screen. Ships OFF, the same dark-launch
     * pattern Gold and the wall itself used.
     */
    uploadsEnabled: boolean;
    /**
     * Whether an upload waits for a moderator before anyone else sees it (2026-09-13;
     * owner: "remove the review process, make it a toggle"). OFF — the default — an
     * upload placed on a square or posted on the Feed is LIVE on arrival; the Wall
     * queue still lists it, so a moderator can reject it after the fact (same sweep:
     * clip banned, placements revoked, post gone, account ended). ON restores the
     * pending-until-approved gate.
     */
    reviewUploads: boolean;
  };
  /**
   * FAPS — the platform currency. One FAP is worth one cent on the Community Wall
   * (FAPS_PER_DOLLAR), a peg chosen so a placement's `price_paid_cents` keeps its
   * numeric meaning when the wall is sold in FAPS: the takeover decay and every wall
   * invariant are unit-agnostic, only the labels change.
   *
   * `enabled` is the master switch and ships OFF (the gold/wall dark-launch pattern).
   * With it on: the wall sells in FAPS and ignores its dollar ladder and free claims;
   * the crown pays; the chest opens; Patreon members draw a stipend. Every number here
   * is the faucet's rate card, public-safe like everything in this object — the
   * wallet dialog quotes them, and a rate the site advertises is not a secret.
   */
  faps: {
    enabled: boolean;
    /** Every account's opening balance, credited once on its first wallet read — so
     *  accounts older than the switch get it too, without a backfill. */
    welcomeGrant: number;
    /** Every crown tap pays this — but only once per DISTINCT clip per UTC day; a
     *  re-crown of a clip already paid today lands nothing (see FapsService.earnFap).
     *  The cap below is measured in clips, not Faps. */
    fapReward: number;
    /** Distinct clips a day may pay for. A clip-COUNT cap, not a Faps-amount cap — a
     *  reward change alone never moves how many clips someone may fap in a day. */
    fapDailyCap: number;
    /** The crown for a Premium member: its own rate and its own clip-count cap, so the
     *  tier's perks list can say "N Faps a crown, up to M clips a day" and mean it. */
    fapRewardPremium: number;
    fapDailyCapPremium: number;
    /** Cumulative minutes of playback (any surface, watch_events) a UTC day needs before
     *  the flat watchReward below pays — once, that day; no session, no per-minute rate. */
    watchThresholdMinutes: number;
    /** The flat, once-a-day payout for crossing watchThresholdMinutes. */
    watchReward: number;
    /** A Premium member's watch-time day pays this × watchReward — the same threshold,
     *  more Faps at the end of it. The crown's per-tier shape (fapRewardPremium) on the
     *  second faucet; 1 would make Premium identical to Basic here. */
    watchMultiplierPremium: number;
    /** The daily chest is a 3×3 of clips with a prize under each. Every tile is an
     *  independent draw: `big` with chestBigPct odds, `large` with chestLargePct, and
     *  `small` the rest of the time — drawn at the deal, before the pick, so the pick
     *  is honest. Simple on purpose: three prizes, two percentages. */
    chestPrizeSmall: number;
    chestPrizeLarge: number;
    chestPrizeBig: number;
    /** Percent of tiles that hide the large prize (0–100). */
    chestLargePct: number;
    /** Percent of tiles that hide the big prize (0–100). */
    chestBigPct: number;
    /** A Premium member's chest pays this × every prize — applied AFTER the draw, so the
     *  odds are everybody's and only the amounts differ. */
    chestMultiplierPremium: number;
    /** A Patreon member's monthly stipend is their tier in cents × this. 5 = 500 FAPS
     *  per dollar of membership, so a $10 tier draws 5,000 a month. */
    stipendPerEntitledCent: number;
    /**
     * The wall's ladder IN FAPS, whole numbers, floor 1 — its own ladder rather than the
     * dollar one × 100 because a currency needs no processor floor: a 25-FAP square is
     * a product decision, a $0.25 square is a card the processor cannot mint.
     */
    wallPriceMarquee: number;
    wallPriceMedium: number;
    wallPriceSmall: number;
    /** Where one-off FAPS packs are sold (the Patreon shop). Packs arrive as redeem
     *  codes because Patreon's API reports memberships, never shop purchases. Blank
     *  hides the door. */
    shopUrl: string;
    /**
     * Daily quests — 2 or 3 random tasks a UTC day, drawn deterministically per account
     * (pickDailyQuests, seeded on userId+day) from QUEST_IDS. Completing EVERY assigned
     * quest that day pays questBundleReward once, under `quest:<day>` — nothing pays
     * per-quest. A Premium member's bundle pays questMultiplierPremium × that.
     */
    questCount: number;
    questBundleReward: number;
    questMultiplierPremium: number;
    /** How many distinct clips the `fap_clips` quest asks for. */
    questTargetFapClips: number;
    /** How many Infinite Scroll pages the `scroll_pages` quest asks for. */
    questTargetScrollPages: number;
    /** How many edges the `edge` quest asks for. `wall_bid` and `create_view` have no
     *  target setting — one placement, one saved view, is the whole ask. */
    questTargetEdges: number;
    /** How many DISTINCT clips watched today the `watch_clips` quest asks for — read
     *  straight from watch_events, so it is a real measurement, not a counter someone
     *  could re-bump by watching the same clip twice. */
    questTargetWatchClips: number;
    /** Minutes on today's ONE rotating watch target (a wall or a Quick Setup —
     *  pickWatchTarget) the `watch_view` quest asks for. */
    questTargetWatchViewMinutes: number;
    /** How many Goonette of the Hill rounds the `battle_rounds` quest asks for. */
    questTargetBattleRounds: number;
    /** Minutes in a Goon Room (any room, own or someone else's) the `room_minutes`
     *  quest asks for — accrued by the server from room presence. */
    questTargetRoomMinutes: number;
  };
  /**
   * The Premium Wall — the same board and the same commerce, priced on its own, and
   * claimable only by GoonLords Premium members (the gate is the wall's, not a setting:
   * see WALL_DEFS). Ships dark like everything else that sells.
   */
  premiumWall: WallSettings;
  /**
   * THE VAULT — a user's own uploads, private by construction (2026-09-09). They live in
   * one virtual collection ("Your Uploads", VAULT_COLLECTION_ID), play only on the
   * owner's own screens, and are never re-encoded or tagged: no rung, no preview, never
   * off the box — the one derived file is a first-frame poster cut at intake
   * (2026-09-12). Every number here is bytes or Faps; all are public-safe (the panel
   * quotes them). Prod's disk is the scarce thing, so the defaults are small and space
   * is dear in crowns: 256 MB free, 👑1,000 a step.
   */
  vault: {
    /** The master switch. Ships OFF like everything that costs disk. */
    enabled: boolean;
    /** What an account starts with, by tier — Premium's bigger vault is a perk. */
    baseBytesFree: number;
    baseBytesPremium: number;
    /** The ceiling a tier can reach by buying steps. Basic at its ceiling is the one
     *  vault refusal Premium lifts (goldUpsell), because Premium's ceiling is higher. */
    maxBytesFree: number;
    maxBytesPremium: number;
    /** One purchase: this many bytes for this many Faps. Idempotent per step — see
     *  POST /vault/expand. */
    stepBytes: number;
    stepPriceFaps: number;
    /** Per file. Capped by the admin validator at the app-wide 150 MiB multipart limit,
     *  which is a register-time option and cannot follow a live setting. */
    maxFileBytes: number;
    maxSeconds: number;
    /** The global budget across every vault: the 507 that actually protects the disk.
     *  An upload served as its master is one byte on disk, so this is a real number. */
    diskBudgetBytes: number;
  };
  /**
   * THE FEED (2026-09-13) — the first social surface: members post one clip each with
   * an optional line of text, crown it, comment, reply. One switch, ships OFF like
   * everything that publishes a stranger's words: every /feed route answers 503 and the
   * menu entry stays hidden until an operator flips it. Uploads posted here ride the
   * wall's review queue, so `wall.uploadsEnabled` governs those too.
   */
  feed: {
    enabled: boolean;
  };
  /**
   * GOON ROOMS (2026-09-19) — shared screens with a chat. One switch, ships OFF like
   * the Feed: every /rooms door answers 503 and the menu row stays hidden until an
   * operator flips it.
   */
  rooms: {
    enabled: boolean;
    /** The server-run HOUSE ROOMS (HOUSE_ROOMS, server/src/house-rooms.ts), listed under
     *  "Always on". Follows `enabled`; off closes them for whoever is in them. */
    houseRooms: boolean;
    /** Chat in the house rooms. A member room has a host who can kick and ban, and a house
     *  room has nobody, so this was a moderation call (issue #18). Shipped off; on since
     *  2026-11-03 by the owner's call, with the switch kept to close it. */
    houseChat: boolean;
  };
}

/**
 * One wall's commerce knobs. The Community Wall and the Premium Wall each carry a block;
 * `wallSettingsFor` picks the right one off a wall id, so a route never reads
 * `settings.wall` for a square that is not on that wall.
 */
export interface WallSettings {
  enabled: boolean;
  /**
   * Free at the floor: when a square's take-price sits at its tier base — open and
   * revoked squares from the start, held ones once the takeover decay flattens — it
   * can be claimed for nothing, one square per account per rolling day PER WALL. The
   * claim skips the processor entirely (PayzMoon cannot mint under PAYZMOON_MIN_CENTS)
   * and decays like a floor-price sale, so a fresh freebie still costs the full premium
   * to steal. Ships OFF, the dark-launch pattern.
   */
  freeClaims: boolean;
  priceMarquee: number;
  priceMedium: number;
  priceSmall: number;
  /** A takeover starts at this × what the holder paid… */
  takeoverMultiplier: number;
  /** …and decays in two legs of this many days EACH: down to what the holder paid,
   *  then on down to the square's base price. See wallTakeoverCents. */
  decayDays: number;
  /**
   * BIDDING SINK (Faps only). When a held square is taken over, the displaced holder is
   * paid this percent of the winning price back as Faps — the loss recycled, so being
   * sniped stings less and the Faps keep circulating. 0 = no cut (a pure loss, the
   * original model). Ships at 0, the dark-launch pattern; an operator flips it on.
   * Ignored off Faps (the cash legs mint no Faps).
   */
  displacedCutPct: number;
  /**
   * BIDDING SINK (Faps only). When on, a holder can DEFEND their own square: pay its
   * current takeover price to re-stamp it (same content, the decay clock reset), so
   * snipers pay the full premium again. A pure sink. Ships OFF, the dark-launch pattern.
   */
  defendEnabled: boolean;
}

/** The commerce block behind a wall. */
export const wallSettingsFor = (s: SystemSettings, wallId: WallId): WallSettings =>
  s[WALL_DEFS[wallId].settingsKey];

/**
 * Labelshop → catalog. An approved label on an axis listed here becomes a `manual` tag on
 * the clip (automatically — the panel pulls every few minutes); an axis NOT listed stays in
 * labelshop and the truth journal only.
 *
 * The list is the operator's statement of which axes are LIVE on the site. Not derived from
 * which heads are deployed: a head can be deployed and still not be finished (body_position
 * ships a head and is deliberately off here), and the person who decides an axis is ready
 * is the person approving its labels. Keys are labelshop axis keys (`people`, `sex`,
 * `ethnicity`, `nudity`, `body_position`, `focus`, `hair`, custom ones); what each writes on
 * the catalog side is LABELSHOP_AXES in server/src/labelshop-import.ts.
 */
export interface LabelshopSettings {
  importAxes: string[];
}

/**
 * Display advertising. Zone ids live here rather than in env or the bundle so a
 * creative that shouldn't be on the site can be killed from the admin panel without
 * a rebuild or a redeploy.
 *
 * Ships disabled. Slots also self-suppress in safe mode — see AdSlot on the client:
 * the network renders inside an <iframe>, which the safe-mode blur rule in index.css
 * does not match, so hiding it is the component's job, not CSS's.
 */
export interface AdSettings {
  /** Master switch. Even when on, a slot with no zone id renders nothing. */
  enabled: boolean;
  /** Per-slot JuicyAds zone ids. A missing or blank id disables just that slot. */
  zones: AdZones;
}

/**
 * The ad slots the client knows how to render, keyed by placement.
 *
 * Sizes are dictated by the containers, which are narrower than the obvious choices:
 * the /browse tag sidebar is 224px and the screen rail is 288px, so neither fits a
 * 300px-wide unit. Measured, not assumed — see the components.
 */
export interface AdZones {
  /** In-feed unit spliced into the /browse clip grid. 300x250. */
  browseInFeed?: string;
  /** Banner in the site footer, below the page content. 300x100. */
  footer?: string;
  /** Medium rectangle between the community shelf and Editor's Choice on /collections.
   *  300x250. Blank uses the Browse in-feed zone. */
  collectionsInFeed?: string;
  /** Medium rectangle after the third post on /feed, once per page. 300x250. Blank uses
   *  the Browse in-feed zone. */
  feedInFeed?: string;
  /** Medium rectangle at the end of /rooms, after "Open a room". 300x250. Blank uses the
   *  Browse in-feed zone. */
  roomsList?: string;
  /** Medium rectangle after the 15th clip on a public collection (/browse/:id). 300x250.
   *  Blank uses the Browse in-feed zone. */
  collectionPage?: string;
  /** Skyscraper under the keyword groups in the /browse sidebar. 160x600, desktop only. */
  browseSidebar?: string;
  /** Medium rectangle in the /views right rail, under the phone-QR card. 300x250,
   *  desktop only. Goon Fuel's fixed player is allowed to cover it on short
   *  viewports — the owner's call (2026-08-26); on tall monitors they don't meet. */
  viewsRail?: string;
}

/** Every slot key, in the order the admin panel lists them. Typed against AdZones so
 *  adding a zone to the interface without listing it here fails the build rather than
 *  silently producing a slot the admin panel cannot configure. */
export const AD_SLOTS = [
  "browseInFeed",
  "footer",
  "browseSidebar",
  "viewsRail",
  "collectionsInFeed",
  "feedInFeed",
  "roomsList",
  "collectionPage",
] as const satisfies ReadonlyArray<keyof AdZones>;

export type AdSlotKey = (typeof AD_SLOTS)[number];








/**
 * What Gold is sold in. FOUR ONE-OFF PASSES, not a subscription.
 *
 * PayzMoon has no recurring billing — its API is payment intents and nothing else — so
 * there is no card on file, nothing to cancel, and no renewal to promise. What there is
 * instead is stacking: `grantGold` extends from `MAX(now, gold_until)`, so buying while
 * you still have time left adds to it rather than wasting it. That is the fact the dialog
 * has to lead with, because "will this bill me again" and "am I throwing away my
 * remaining days" are the only two questions anybody actually has.
 *
 * `days` is the product; the price lives in SystemSettings so it can move without a
 * deploy. They are separate on purpose: repricing is an operator act, but changing what a
 * tier IS would change what past receipts in gold_grants meant.
 *
 * Ids are day-based ("7d"…) since the 2026-08 pass rename: the labels read straight off
 * them, and the 7-day pass has no period name to borrow. Nothing persists a tier id —
 * receipts store days — so the retired period ids ("month"/"quarter"/"year") simply
 * stopped validating; their day counts carried over unchanged.
 */
export type GoldTier = "7d" | "30d" | "90d" | "365d";

export const GOLD_TIERS: ReadonlyArray<{
  tier: GoldTier;
  days: number;
  label: string;
  /** Which SystemSettings.gold field prices this tier. */
  priceKey: "price7d" | "price30d" | "price90d" | "price365d";
}> = [
  { tier: "7d", days: 7, label: "7-day pass", priceKey: "price7d" },
  { tier: "30d", days: 30, label: "30-day pass", priceKey: "price30d" },
  { tier: "90d", days: 90, label: "90-day pass", priceKey: "price90d" },
  { tier: "365d", days: 365, label: "365-day pass", priceKey: "price365d" },
];

/** The tier a string names, or null — one reader, so an unknown tier from a request body
 *  cannot become a silent default that grants the wrong number of days. */
export function goldTier(v: unknown): (typeof GOLD_TIERS)[number] | null {
  return GOLD_TIERS.find((t) => t.tier === v) ?? null;
}

/**
 * The smallest amount the payment processor will mint a checkout for.
 *
 * MEASURED, NEVER TAKEN FROM DOCS. It was 50 here once, read off PayzMoon's
 * documentation, and that number let an operator price a wall square at $0.50 and ship a
 * board where every Buy button failed at the processor. A floor that is wrong in the
 * permissive direction is not a floor.
 *
 * 100 is HighRiskPay's, and it is a CARD minimum rather than an API floor — a different
 * kind of limit than PayzMoon's, and it fails differently. Their API mints a 50¢ invoice
 * quite happily; what a sub-minimum price actually buys you is a hosted checkout with no
 * card button on it, payable only in crypto. Nothing errors. The buyer just cannot pay the
 * way they came to pay.
 *
 * $1 is the bottom of the range their pricing page gives ("$1–$15, varies by on-ramp"), so
 * it is the floor below which we KNOW a card cannot be offered, not the floor above which
 * we know it can. If a real card payment is ever refused above this, the number to raise
 * is this one — and it should be raised by measurement, the way 60 was.
 *
 * It moves in lockstep with the wall's prices: a tier priced under it makes wallTierCents
 * return null, which answers 503 to every wall checkout. Prod's ladder went to 3/2/1
 * before this landed for exactly that reason.
 *
 * (PayzMoon's own floor was 60, measured 2026-08-29: 50¢ and 55¢ answered HTTP 500, 59¢
 * and above answered 200. Kept here because it is the precedent for measuring rather than
 * trusting a docs number — the 50 that came from their documentation is what let a $0.50
 * square ship onto a board where every Buy button failed.)
 */
export const MIN_CHARGE_CENTS = 100;

/** @deprecated Use MIN_CHARGE_CENTS. Kept so nothing that imported the old name breaks
 *  mid-migration; the value is identical. */
export const PAYZMOON_MIN_CENTS = MIN_CHARGE_CENTS;

/**
 * A tier's price in CENTS, which is the only unit PayzMoon accepts.
 *
 * The one place dollars become cents, so the number the dialog renders and the number the
 * card is charged cannot drift. Rounded rather than truncated — a price of 9.99 stored as
 * a float must not charge 998 — and floored at MIN_CHARGE_CENTS so a misconfigured
 * price is refused here rather than by their API mid-checkout.
 */
export function goldTierCents(
  gold: Pick<SystemSettings["gold"], "price7d" | "price30d" | "price90d" | "price365d">,
  tier: GoldTier,
): number | null {
  const row = goldTier(tier);
  if (!row) return null;
  const dollars = gold[row.priceKey];
  if (typeof dollars !== "number" || !Number.isFinite(dollars)) return null;
  const cents = Math.round(dollars * 100);
  return cents >= MIN_CHARGE_CENTS ? cents : null;
}

// ---------------------------------------------------------------------------
// Community Wall — the shared, purchasable home screen.
//
// One board, everyone sees the same owners and clips (playback clocks stay local).
// Squares are sold pay-to-place: no refunds, no escrow, a displaced holder keeps only
// history. The board's geometry and every price knob live HERE so client badges, the
// checkout amount, and the checks all read one authority.
// ---------------------------------------------------------------------------

export type WallTier = "marquee" | "medium" | "small";

/**
 * The board: 1 marquee + 4 medium + 6 small on 16:9 — see presetRects("billboard") for
 * the picture. A marquee is six smalls and a medium is one and a half, so price-by-size
 * reads off the board, and the marquee is deliberately MORE screen per dollar than its
 * price ratio; the flagship should feel like one.
 *
 * DERIVED from the "billboard" layout preset since 2026-08-29, which is also in the
 * palette as a Gold layout (13 regions prices it past Basic's 8 by arithmetic — no
 * explicit gate). One spelling of the shape; the preset's case comment carries the
 * reorder warning, because these INDICES are the squares' identities and price tiers.
 * The wall is still not a MONITOR_TEMPLATE — that list leaks into the random quick
 * setup and the picker's quick-setups shelf, and the wall is a place, not a setup.
 *
 * `regionId`s are the FULL stable ids (`wall:main:r0`…): the ledger, the API and the
 * resolved view's regions all spell a square the same way, for the same reason
 * `builtinView` derives `${templateId}:r${i}` — a re-resolve must return the same
 * regions or every watching screen remounts its tiles.
 *
 * EVERY WALL IS THIS BOARD. The Premium Wall's squares are `wall:premium:r0`… over the
 * same rects and the same tiers-by-index; what differs is who may claim and what it costs,
 * neither of which is geometry. The wall id inside the region id is what scopes a ledger
 * row to its board — there is no wall column, and there does not need to be one.
 */
export interface WallRegionDef {
  regionId: string;
  tier: WallTier;
  rect: Rect;
}

const boardFor = (def: WallDef): ReadonlyArray<WallRegionDef> =>
  presetRects("billboard").map((rect, i) => ({
    regionId: `${def.viewId}:r${i}`,
    tier: i === 0 ? ("marquee" as const) : i <= 4 ? ("medium" as const) : ("small" as const),
    rect,
  }));

const REGIONS_BY_WALL: Record<WallId, ReadonlyArray<WallRegionDef>> = {
  main: boardFor(WALL_DEFS.main),
  premium: boardFor(WALL_DEFS.premium),
};

/** One wall's squares, in board order. */
export const wallRegions = (wallId: WallId): ReadonlyArray<WallRegionDef> => REGIONS_BY_WALL[wallId];

/** The Community Wall's squares — the name the ledger, the checks and the feed grew up with. */
export const WALL_REGIONS: ReadonlyArray<WallRegionDef> = REGIONS_BY_WALL.main;

/** The board entry behind a region id, or null — requests name squares by id, and an
 *  unknown id must fail loudly rather than price as something. The wall is read off the
 *  id first, so `wall:main:r99` and `wall:nope:r0` are both nothing. */
export function wallRegion(regionId: string): WallRegionDef | null {
  const wallId = wallIdOfRegion(regionId);
  if (!wallId) return null;
  return REGIONS_BY_WALL[wallId].find((r) => r.regionId === regionId) ?? null;
}

/** A square's number on its own board, 1-based, for a person — or null for no square. */
export function squareNumber(regionId: string): number | null {
  const wallId = wallIdOfRegion(regionId);
  if (!wallId) return null;
  const i = REGIONS_BY_WALL[wallId].findIndex((r) => r.regionId === regionId);
  return i < 0 ? null : i + 1;
}

/**
 * How long starting a checkout holds a square against other buyers. Long enough to type
 * a card number at PayzMoon, short enough that an abandoned checkout doesn't park a
 * square. Sitting past it risks the double-sale the webhook resolves as a normal
 * displacement (last fulfillment wins, no refund).
 */
export const WALL_RESERVATION_TTL_MS = 10 * 60 * 1000;

/**
 * A square's BASE price in cents — the floor takeover prices decay to, and what an open
 * square sells for. Same contract as goldTierCents: the one dollars→cents conversion,
 * rounded not truncated, refused below MIN_CHARGE_CENTS so a misconfigured price
 * fails here rather than mid-checkout — which is exactly how a $0.50 square shipped a
 * board whose every Buy button died at the processor (2026-08-29).
 */
export function wallTierCents(
  wall: Pick<WallSettings, "priceMarquee" | "priceMedium" | "priceSmall">,
  tier: WallTier,
): number | null {
  const dollars =
    tier === "marquee" ? wall.priceMarquee : tier === "medium" ? wall.priceMedium : wall.priceSmall;
  if (typeof dollars !== "number" || !Number.isFinite(dollars)) return null;
  const cents = Math.round(dollars * 100);
  return cents >= MIN_CHARGE_CENTS ? cents : null;
}

/**
 * A square's BASE price in FAPS — wallTierCents's twin for the FAPS-mode board. Same
 * contract (rounded, refused when garbage) with ONE deliberate difference: no processor
 * floor. MIN_CHARGE_CENTS exists because a card cannot be charged under it; nobody is
 * charging a card here. The floor is 1, because a 0-FAP placement would collide with the
 * free-claim sentinel that wallTakeoverCents reads out of `price_paid_cents === 0`.
 */
export function wallTierFaps(
  faps: Pick<SystemSettings["faps"], "wallPriceMarquee" | "wallPriceMedium" | "wallPriceSmall">,
  tier: WallTier,
): number | null {
  const n =
    tier === "marquee" ? faps.wallPriceMarquee : tier === "medium" ? faps.wallPriceMedium : faps.wallPriceSmall;
  if (typeof n !== "number" || !Number.isFinite(n)) return null;
  const whole = Math.round(n);
  return whole >= 1 ? whole : null;
}

/**
 * What taking an OWNED square costs right now, computed on read — the whole reason the
 * wall needs no decay clock. TWO LEGS, both `decayDays` long: buy at P and the takeover
 * price starts at `multiplier × P`, falls linearly to P over the first leg (the fresh
 * purchase is expensive to snipe), then keeps falling from P to `baseCents` over the
 * second (a stale square drifts back toward the open-market price). Never below the
 * floor at any point.
 *
 * The floor is read LIVE from settings, not snapshotted at sale: repricing is an operator
 * act (the SystemSettings.gold argument), so raising a tier's base can put parts of a
 * fresh sale's curve under the new floor — then the answer is simply the floor, which is
 * also where every garbage input (bad multiplier, zero decay) lands: the floor is the
 * one number that is always defensible.
 */
export function wallTakeoverCents(args: {
  pricePaidCents: number;
  placedAt: number;
  now: number;
  baseCents: number;
  multiplier: number;
  decayDays: number;
}): number {
  const { pricePaidCents, placedAt, now, baseCents, multiplier, decayDays } = args;
  // A free claim records $0 paid but decays exactly like a floor-price sale — without
  // this its whole curve sits under the floor, making every freebie instantly free to
  // the NEXT claimant and the board $0 forever. ONLY the freebie: a paid price under a
  // later-RAISED floor keeps its real curve and is clamped from below, the operator
  // reprice rule pinned in tools/checks/wall.mts.
  const paid = pricePaidCents === 0 ? baseCents : Math.round(pricePaidCents);
  const start = Math.round(paid * multiplier);
  const legMs = decayDays * 86_400_000;
  if (!Number.isFinite(start) || !Number.isFinite(paid) || !Number.isFinite(legMs) || legMs <= 0) {
    return baseCents;
  }
  const t = Math.max(now - placedAt, 0);
  const price =
    t < legMs
      ? // Leg 1: multiplier × paid → paid.
        start - ((start - paid) * t) / legMs
      : t < 2 * legMs
        ? // Leg 2: paid → base.
          paid - ((paid - baseCents) * (t - legMs)) / legMs
        : baseCents;
  return Math.max(baseCents, Math.round(price));
}

/**
 * Materialise the wall as a View, from whatever the caller knows about each square.
 *
 * Pure and placement-shaped rather than Db-shaped so the checks can pin it without a
 * database: the server passes a closure over the placements ledger. A square with no
 * state, a revoked placement, or zero still-servable clips all land on `random` — the
 * board is never blank, and "this square's content died" degrades to "this square is
 * open" with no special case.
 *
 * Same stability contract as builtinView: region ids from the wall's own board, timestamps
 * frozen at 0. `name` carries the holder's username (or "Available") so the existing
 * tile-label vocabulary renders ownership with no new field. `soundOn: true` per the
 * viewFromTemplate argument — nobody authors this wall tile by tile, and the screen's
 * master mute still keeps the room quiet until asked.
 *
 * `wallId` defaults to the Community Wall so every caller that predates the second wall —
 * and every check that pins the board — reads unchanged.
 */
export function wallView(
  stateFor: (regionId: string) => { holder?: string; clips?: MediaItem[] } | null | undefined,
  wallId: WallId = "main",
): View {
  const def = WALL_DEFS[wallId];
  return {
    id: def.viewId,
    name: def.name,
    icon: def.icon,
    aspect: { w: 16, h: 9 },
    /*
     * ADAPTIVE, unlike a bought layout normally would be. A 16:9 board of eleven squares
     * on a portrait phone is eleven slivers — the wall's worst surface, and the one most
     * people meet it on. The transpose is RENDER-ONLY (DisplayApp.shownRegions) and swaps
     * geometry alone: a square keeps its id, its holder, its price and its rank, so the
     * marquee is still the biggest thing on the glass. Nobody's purchase is reshaped,
     * only re-hung.
     */
    adaptiveLayout: true,
    regions: REGIONS_BY_WALL[wallId].map(({ regionId, rect }) => {
      const state = stateFor(regionId);
      const clips = state?.clips ?? [];
      return {
        id: regionId,
        ...rect,
        name: state?.holder ?? "Available",
        source: clips.length
          ? ({ kind: "clips", clips } satisfies ClipsSourceConfig)
          : ({ kind: "random" } satisfies RandomSourceConfig),
        fit: "cover" as const,
        soundOn: true,
      };
    }),
    createdAt: 0,
    updatedAt: 0,
  };
}

// ---------------------------------------------------------------------------
// Infinity Walls — the stub view, the preset pool, and the page dealer.
// ---------------------------------------------------------------------------

/**
 * The view behind `infinity:main`: a name and an icon for the pill, and NO regions. The
 * pages are dealt on the client (buildInfinityPage) from the random feed, and a server
 * that returned a first page here would be pretending one layout was the view when the
 * whole point is that there is always another. Zero regions is also what keeps every
 * region-driven feature on the screen — Play 1's pick, the keyboard skip, the hover
 * chrome — honestly idle rather than acting on a page that has already scrolled away.
 */
export function infinityView(infinityId: InfinityId = "random"): View {
  const def = INFINITY_DEFS[infinityId];
  return {
    id: def.viewId,
    name: def.name,
    icon: def.icon,
    aspect: { w: 16, h: 9 },
    regions: [],
    createdAt: 0,
    updatedAt: 0,
  };
}

/**
 * The layouts a page can be dealt: every preset that tiles the page without overlap in
 * two to six regions. `phone` marks the two-to-four ones — a portrait phone gets only
 * those, because six regions on a phone are six thumbnails, and a page here is meant to
 * be WATCHED, one loop per region. Hand-listed rather than derived so the pool is
 * curated (pip-corners overlaps and is out; grids past 3×2 are a wall, not a page); the
 * infinity-walls check pins every entry's rect count so the list cannot rot under a
 * preset edit. Which of these a given SCREEN is dealt is INFINITY_RECT_ASPECT's call.
 */
export const INFINITY_PRESETS: ReadonlyArray<{ id: LayoutPresetId; phone: boolean }> = [
  { id: "2-vertical", phone: true },
  { id: "2-horizontal", phone: true },
  { id: "3-vertical", phone: true },
  { id: "3-horizontal", phone: true },
  { id: "1-left-2-right", phone: true },
  { id: "2-left-1-right", phone: true },
  { id: "2-top-1-bottom", phone: true },
  { id: "grid-2x2", phone: true },
  { id: "4-vertical", phone: true },
  { id: "3-top-1-bottom", phone: true },
  { id: "1-top-3-bottom", phone: true },
  { id: "big-little-big", phone: true },
  { id: "filmstrip", phone: false },
  { id: "panorama", phone: false },
  { id: "hero-left-rail", phone: false },
  { id: "twin-towers", phone: false },
  { id: "l-shape", phone: false },
  // grid-3x2 (two columns, three rows) is out: its cells are 2.4:1 on a 16:10 window
  // and 2.7:1 on 16:9 — strips, not tiles — and it slipped under the aspect bound on
  // any window a little narrower than 16:9. Judged on the glass 2026-09-18.
  { id: "grid-2x3", phone: false },
];

/**
 * How stretched a dealt region may render, as width over height in pixels. THE SCREEN
 * PICKS THE LAYOUT: three stacked rows on a 16:9 monitor are three strips at 5:1 that no
 * clip fills, and the same preset transposed on a phone is three slivers at 1:5 — so a
 * preset is only dealt where every one of its rects, hung for that screen, stays inside
 * these bounds. A monitor therefore gets columns (3-vertical is 0.59, a portrait clip's
 * home) and never 3-horizontal; a phone gets rows and never 2-horizontal. Asymmetric on
 * purpose: tall regions have a whole catalog of phone-shot clips to fill them, wide strips
 * have almost none. Measured against the pool in tools/checks/infinity-walls.mts.
 */
export const INFINITY_RECT_ASPECT = { widest: 2.5, tallest: 0.35 } as const;

/** The presets that render within INFINITY_RECT_ASPECT on a page of this aspect, hung
 *  for its orientation. Exported for the check; the dealer applies it. */
export function infinityPresetsFor(
  portrait: boolean,
  pageAspect: number,
): ReadonlyArray<{ id: LayoutPresetId; phone: boolean }> {
  return INFINITY_PRESETS.filter((p) => {
    if (portrait && !p.phone) return false;
    return presetRects(p.id).every((r) => {
      const hung = portrait ? { w: r.h, h: r.w } : r;
      const aspect = (hung.w * pageAspect) / hung.h;
      return aspect <= INFINITY_RECT_ASPECT.widest && aspect >= INFINITY_RECT_ASPECT.tallest;
    });
  });
}

/**
 * MIRRORING — the variations that keep a scroll's layouts from feeling like a deck of
 * nineteen cards.
 *
 * Two same-sized tiles side by side can play ONE clip, the right one flipped, and read
 * as a single symmetrical image (the twin-towers quick setup does this by hand). On a
 * dealt page that is a variation of the layout, not a layout: 4 columns can be two
 * mirrored pairs, or just the outer two, or the inner two, or none; 3 columns can mirror
 * the outer two; a grid's rows can each pair up. The pairs are FOUND, not listed:
 * any two rects of equal size on the same row, hung for this screen, so a preset that
 * is columns on a monitor and rows on a phone offers pairs only where the pairs exist.
 *
 * Portrait-ish only (MIRROR_MAX_ASPECT): a mirrored pair of tall tiles is a body and its
 * reflection; a mirrored pair of wide strips is a smear. Wide tiles keep their own clips.
 */
export const MIRROR_MAX_ASPECT = 1.3;

/** How often a page that COULD mirror does not. Half, so mirroring is a thing that
 *  happens rather than the way every wall looks. */
export const MIRROR_PLAIN_SHARE = 0.5;

/** The pairs of slots that may mirror on this page: equal rects on one row, portrait-ish
 *  once hung. Ordered, so the rng's pick is the same in the check as on the glass. */
export function infinityMirrorPairs(
  hung: readonly Rect[],
  pageAspect: number,
): Array<[number, number]> {
  const eq = (a: number, b: number) => Math.abs(a - b) < 1e-6;
  const out: Array<[number, number]> = [];
  for (let i = 0; i < hung.length; i++) {
    const a = hung[i]!;
    if ((a.w * pageAspect) / a.h > MIRROR_MAX_ASPECT) continue;
    for (let j = i + 1; j < hung.length; j++) {
      const b = hung[j]!;
      if (eq(a.y, b.y) && eq(a.w, b.w) && eq(a.h, b.h)) out.push(a.x < b.x ? [i, j] : [j, i]);
    }
  }
  return out;
}

/** Every way to mirror this page: each non-empty set of DISJOINT pairs (a slot leads or
 *  follows once). For four columns that is nine variations; for two, one. */
export function infinityMirrorVariants(pairs: ReadonlyArray<[number, number]>): Array<Array<[number, number]>> {
  const out: Array<Array<[number, number]>> = [];
  const walk = (from: number, taken: Set<number>, chosen: Array<[number, number]>) => {
    if (chosen.length) out.push([...chosen]);
    for (let k = from; k < pairs.length; k++) {
      const [a, b] = pairs[k]!;
      if (taken.has(a) || taken.has(b)) continue;
      taken.add(a); taken.add(b); chosen.push(pairs[k]!);
      walk(k + 1, taken, chosen);
      chosen.pop(); taken.delete(a); taken.delete(b);
    }
  };
  walk(0, new Set(), []);
  return out;
}

/** A page's region id: the view, the page number, the slot — unique for the life of a
 *  scroll, and readable in a fap ledger or an analytics event. */
export const infinityRegionId = (viewId: string, pageNo: number, slot: number): string =>
  `${viewId}:p${pageNo}:r${slot}`;

/**
 * Deal one page: a random preset from the pool, its rects hung for this screen's
 * orientation, and a clip for each rect chosen by SHAPE.
 *
 * "The regions are responsive to the video's real size" is this assignment: rects are
 * filled largest first, and each takes the pooled clip whose orientation fits it best
 * (aspectPenalty — a portrait clip in a tall rect costs nothing, in a wide one costs two),
 * so a tall clip lands in a tall region and `cover` crops as little as the deal allows.
 * The layout is dealt BEFORE the clips are looked at on purpose: choosing a layout to fit
 * the clips would make every page the same two grids, and the surprise of the layout is
 * the feature.
 *
 * Pure. `rng` is injected (Math.random in the browser, a fixed sequence in the check) and
 * `pool` is read, never mutated — the clips used come back in `used` so the caller can
 * retire them. A pool smaller than the smallest preset deals a page of whatever it has.
 * `mirror` (default on) lets the page take a mirror variation — see infinityMirrorPairs;
 * a mirrored follower carries `syncWith`/`syncMode: "flip"` to its leader and uses none
 * of the pool.
 *
 * `pageAspect` is the page's width over its height in pixels — the rect's shape depends
 * on it (a half-width rect on a 9:16 phone is tall; on a 16:9 desktop it is nearly
 * square), and on a portrait screen the rects are transposed first, the same one
 * reflection adaptive layout uses on the walls.
 */
export function buildInfinityPage(
  pageNo: number,
  pool: readonly MediaItem[],
  opts: {
    portrait: boolean;
    pageAspect: number;
    rng: () => number;
    viewId?: string;
    mirror?: boolean;
    /** Deal THIS layout rather than a random one — a Build a Slut round is always 2×2. */
    preset?: LayoutPresetId;
  },
): { regions: Region[]; used: MediaItem[] } {
  const fits = (p: { id: LayoutPresetId }) => presetRects(p.id).length <= pool.length;
  // The screen's shape picks the layouts (see INFINITY_RECT_ASPECT); a screen so odd
  // that nothing passes still deals from the orientation's pool rather than nothing.
  let candidates = infinityPresetsFor(opts.portrait, opts.pageAspect).filter(fits);
  if (candidates.length === 0)
    candidates = INFINITY_PRESETS.filter((p) => (!opts.portrait || p.phone) && fits(p));
  if (opts.preset) candidates = fits({ id: opts.preset }) ? [{ id: opts.preset, phone: true }] : [];
  if (candidates.length === 0 || pool.length === 0) return { regions: [], used: [] };
  const preset = candidates[Math.floor(opts.rng() * candidates.length) % candidates.length]!;
  const rects = presetRects(preset.id);
  const hung = opts.portrait ? rects.map((r) => ({ x: r.y, y: r.x, w: r.h, h: r.w })) : rects;
  // The mirror variation, if any (see infinityMirrorPairs): one rng draw decides plain
  // or mirrored, a second picks which variation. A follower takes no clip of its own.
  const followerOf = new Map<number, number>();
  if (opts.mirror !== false) {
    const variants = infinityMirrorVariants(infinityMirrorPairs(hung, opts.pageAspect));
    if (variants.length > 0 && opts.rng() >= MIRROR_PLAIN_SHARE) {
      const variant = variants[Math.floor(opts.rng() * variants.length) % variants.length]!;
      for (const [leader, follower] of variant) followerOf.set(follower, leader);
    }
  }
  // Largest first: the marquee gets first pick of the clips that fit it.
  const order = hung
    .map((rect, slot) => ({ rect, slot }))
    .filter(({ slot }) => !followerOf.has(slot))
    .sort((a, b) => b.rect.w * b.rect.h - a.rect.w * a.rect.h);
  const remaining = [...pool];
  const used: MediaItem[] = [];
  const dealt = new Map<number, MediaItem>();
  for (const { rect, slot } of order) {
    if (remaining.length === 0) break;
    const shape = aspectTag(rect.w * opts.pageAspect, rect.h);
    let best = 0;
    for (let i = 1; i < remaining.length; i++) {
      if (aspectPenalty(remaining[i]!, shape) < aspectPenalty(remaining[best]!, shape)) best = i;
    }
    const clip = remaining.splice(best, 1)[0]!;
    dealt.set(slot, clip);
    used.push(clip);
  }
  const idOf = (slot: number) => infinityRegionId(opts.viewId ?? INFINITY_VIEW_ID, pageNo, slot);
  const regions: Region[] = hung.map((rect, slot) => {
    const leader = followerOf.get(slot);
    return {
      id: idOf(slot),
      ...rect,
      // A follower carries its leader's clip too: SyncedRegion ignores it, but anything
      // that reads a region's source (the fap ledger, a screenshot's caption) reads the
      // clip actually on the glass.
      source: { kind: "clip", clip: dealt.get(leader ?? slot) ?? null } satisfies ClipSourceConfig,
      fit: "cover",
      soundOn: true,
      ...(leader !== undefined ? { syncWith: idOf(leader), syncMode: "flip" as const } : {}),
    };
  });
  return { regions, used };
}

/**
 * The next `n` clips from a feed page that this scroll has not shown yet — video only (a
 * region here LOOPS, and a still does not), keyed by clipKey so a clip the random feed
 * dealt twice across pages shows once. `seen` is the caller's memory and is written here:
 * every clip returned is marked, so two calls can never hand out the same one.
 */
export function takeDistinct(
  items: readonly MediaItem[],
  seen: Set<string>,
  n: number,
): MediaItem[] {
  const out: MediaItem[] = [];
  for (const item of items) {
    if (out.length >= n) break;
    if (item.kind !== "video") continue;
    const key = clipKey(item);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

/** Cents as money: ALWAYS two decimals, so `$3.00` and `$2.40` sit in one column and a
 *  price never reads as a count. One formatter, so a square's badge, the buy dialog and
 *  the admin ledger cannot quote the same cents three ways. */
export function centsLabel(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// Faps — the platform currency.
//
// Naming, because the word was already taken twice: a *fap* is the act (the crown),
// *your faps* is the collection it lands in, and Faps — capitalised, and in the bar
// just the crown beside a number — is the currency. The three formatters below are the ONLY spellings of an
// amount, for centsLabel's reason: a pill, a badge, a ledger row and an admin table
// must not quote one number four ways.
// ---------------------------------------------------------------------------

/** The peg: one FAP is one cent on the Community Wall. See SystemSettings.faps. */
export const FAPS_PER_DOLLAR = 100;

/** A count, grouped: `1,240`. The pill and the hero — the unit is the crown beside it. */
export function formatFaps(n: number): string {
  return Math.round(n).toLocaleString("en-US");
}

/** A price or amount with its unit: `200 Faps`, `1 Fap`. Badges, buttons, sentences. */
export function fapsLabel(n: number): string {
  const whole = Math.round(n);
  return `${formatFaps(whole)} ${Math.abs(whole) === 1 ? "Fap" : "Faps"}`;
}

/** A ledger delta with its sign: `+1`, `−200` (a true minus, the way the rest of the
 *  copy sets its dashes). */
export function signedFaps(n: number): string {
  const whole = Math.round(n);
  return whole < 0 ? `−${formatFaps(-whole)}` : `+${formatFaps(whole)}`;
}

/** Why a FAPS row exists. A closed vocabulary: the server never ships prose about a
 *  row, the client maps each kind to its sentence. `admin` rows carry a note instead. */
export type FapsKind =
  | "welcome"
  | "fap"
  | "watch"
  | "chest"
  | "stipend"
  | "code"
  | "wall"
  /** The displaced-holder cut: a share of the winning price, paid to whoever just lost the
   *  square (the wall's bidding sink recycled into a faucet). Its ref carries the region so
   *  the wallet can offer a take-it-back — see `regionId` below. */
  | "unseated"
  /** The daily quest bundle: every assigned quest finished the same UTC day, paid once
   *  under `quest:<day>` — see QuestId and FapsWalletResponse.quests. */
  | "quest"
  /** A step of vault space bought (2026-09-09): one row per step under `vault:<uid>:<n>`,
   *  n counted from these very rows, so a retried purchase pays once and a later change
   *  to the step size cannot shift the numbering. See POST /vault/expand. */
  | "vault"
  | "admin";

/**
 * A daily quest's kind — what it counts, not its target (the target is a setting, see
 * SystemSettings.faps). Two are booleans in practice (did it happen today at all); three
 * carry a count toward a settings-driven target. The pool a day's quests are drawn from
 * — see pickDailyQuests in server/src/faps.ts, which is pure and lives there rather than
 * here because it needs no wire shape, only this closed vocabulary of what a quest IS.
 */
export type QuestId =
  | "fap_clips"
  | "scroll_pages"
  | "edge"
  | "wall_bid"
  | "create_view"
  // The second five (2026-09-09): each drives a feature that is easy to miss rather
  // than one you'd do anyway — the chest already nags for itself, but a collection, a
  // code, a session, a defend are all one tap from somewhere nobody thought to look.
  | "open_chest"
  | "create_collection"
  | "start_session"
  | "watch_clips"
  // Rotating: today's ONE target (a wall, or a Quick Setup) is picked the same seeded
  // way the day's quests are — see pickWatchTarget in server/src/faps.ts. One id in
  // the pool covers every surface rather than a QuestId per Quick Setup.
  | "watch_view"
  | "pair_device"
  // Goonette of the Hill (2026-09-19): rounds played — a pick per round, counted on the
  // server as the pick lands, so it cannot be bumped from a tab.
  | "battle_rounds"
  // Goon Rooms (2026-09-19): minutes spent in a room today, measured by the server from
  // the room socket's join and leave — never reported by a tab.
  | "room_minutes";

/** The pool every day's quests are drawn from, in the fixed order pickDailyQuests
 *  indexes into — order matters for the seeded pick, so this is the one place it lives. */
export const QUEST_IDS: ReadonlyArray<QuestId> = [
  "fap_clips",
  "scroll_pages",
  "edge",
  "wall_bid",
  "create_view",
  "open_chest",
  "create_collection",
  "start_session",
  "watch_clips",
  "watch_view",
  "pair_device",
  "battle_rounds",
  "room_minutes",
];

/** One ledger row as the owner sees it. No `ref` on the wire: refs hold internal clip
 *  ids and session ids, which are ours (see tools/checks/clip-id-privacy.mts). */
export interface FapsLedgerEntry {
  id: number;
  kind: FapsKind;
  /** Signed: earns positive, spends negative. */
  delta: number;
  note: string | null;
  createdAt: number;
  /** The wall square this row is about, for the rows that ARE about a square (`unseated`).
   *  Surfaced from the ref, never the internal placement id — a region id is public
   *  geometry (the board renders it), so it is safe on the wire where a clip id is not.
   *  Absent on every other kind. It is what lets the take-it-back card name a square to
   *  reopen without a second fetch. */
  regionId?: string;
}

/** One of today's assigned quests, as the dialog renders it. `count`/`target` are
 *  clip/page/edge counts for the three counting quests, and 0/1 for the two
 *  did-it-happen ones — `completed` is what actually gates the bundle, `count`/`target`
 *  is only the progress bar's arithmetic. */
export interface QuestProgress {
  id: QuestId;
  count: number;
  target: number;
  completed: boolean;
}

/** GET /api/faps/wallet — everything the pill and the dialog need in one read. */
export interface FapsWalletResponse {
  /** False = the switch is off; every other field is a quiet zero and the client
   *  renders nothing. */
  enabled: boolean;
  balance: number;
  /** Today's take against each faucet, UTC day. `fap.earned`/`fap.cap` are CLIP counts
   *  now (once per distinct clip, capped at N clips/day), not a Faps amount — see
   *  FapsService.earnFap. `watch` is no longer a per-minute rate against a cap; it is a
   *  single daily threshold, crossed or not. */
  today: {
    fap: { earned: number; cap: number };
    watch: { minutes: number; thresholdMinutes: number; earnedToday: boolean };
  };
  chest: {
    available: boolean;
    /** When the next chest opens — a SERVER instant (UTC midnight). */
    nextAt: number;
  };
  /** Today's 2–3 assigned quests (see QuestId, pickDailyQuests) and what completing all
   *  of them pays — doubled already for a Premium reader, so the dialog quotes one
   *  number, not two it would have to reconcile itself. */
  quests: {
    items: QuestProgress[];
    bundleReward: number;
    allComplete: boolean;
    /** Today's ONE rotating watch target (pickWatchTarget) — present exactly when
     *  `watch_view` is among today's assigned quests. The client compares this against
     *  whatever view is currently on screen before it bothers reporting a minute. */
    watchTarget?: { viewId: string; name: string };
  };
  recent: FapsLedgerEntry[];
  /** THIS read is the one that granted the welcome — the one moment the client may greet.
   *  Never repeated: the `welcome` ref dedupes every later read, so the flag is absent. */
  welcomed?: true;
}

/** POST /api/faps/chest/deal — today's chest, dealt: nine tiles to pick from. Tiles are
 *  bare rung paths (GOONCHA's rule — no clip identity on the wire); the prizes stay on
 *  the server until the pick. One-shot: a deal is consumed by its pick or its expiry. */
export interface FapsChestDeal {
  dealId: string;
  tiles: string[];
  expiresAt: number;
}

/** POST /api/faps/chest/pick — the reveal. `amount` is the prize under the picked
 *  tile; `prizes` is every tile's, so the grid can show what the other eight hid. */
export interface FapsChestResponse {
  amount: number;
  index: number;
  prizes: number[];
  balance: number;
}

/** POST /api/faps/redeem — a Patreon-shop pack, pasted in. */
export interface FapsRedeemResponse {
  amount: number;
  balance: number;
}

/** POST /api/faps/click — the crown's reply. `fapsEarned` is 1 when the tap paid (the
 *  switch on, the day's cap not yet reached), else 0; the crown burst is immediate,
 *  the "+1 Fap" float waits for this. `fapsBalance` rides along so the pill ticks without a refetch.
 *  `fapsAmount` is what the tap actually paid (a member's crown pays more than 1, and the
 *  day meter must move by that). `capped` says WHY a tap paid nothing: the day's take is
 *  full — the one moment a Basic account has a reason to hear about Premium's cap. */
export interface FapClickResponse {
  ok: true;
  fapsEarned: 0 | 1;
  fapsAmount?: number;
  fapsBalance?: number;
  capped?: true;
}

// --- admin ---

/** A batch of redeem codes, as the admin panel lists it. Batches are a GROUP BY over
 *  the codes, not their own table — one fewer thing to keep in step. */
export interface AdminFapsCodeBatch {
  batchId: string;
  label: string | null;
  amount: number;
  count: number;
  redeemed: number;
  revoked: number;
  createdAt: number;
}

/** The mint's reply — the ONLY time the plaintext codes exist. The panel shows them
 *  once; the database keeps hashes. */
export interface AdminFapsMintResponse {
  batchId: string;
  codes: string[];
}

export interface AdminFapsLedgerRow extends FapsLedgerEntry {
  userId: string;
  username: string;
  /** The admin may see refs — they are the audit trail. */
  ref: string | null;
}

/**
 * What a buyer may bring to a square, and the shape of it.
 *
 * WEB-SAFE ONLY, and the allowlist is the whole policy: a phone's .mov or an HEVC mp4
 * plays for almost nobody, and this box has two cores — normalising a stranger's upload
 * would put a minutes-long encode in front of the thing whose actual job is serving
 * video. So refuse what a browser cannot play, name the formats that work, and never
 * transcode.
 *
 * The map is MIME → extension because both ends need it: the client to build an
 * `accept` attribute, the server to name the file it writes.
 */
export const WALL_UPLOAD_TYPES: Readonly<Record<string, string>> = {
  "video/mp4": ".mp4",
  "video/webm": ".webm",
  "image/jpeg": ".jpg",
  "image/png": ".png",
  "image/webp": ".webp",
};

/** A square is a loop, not a broadcast slot. Sixty seconds is the catalog's own ceiling
 *  (MAX_CLIP_SECONDS), so an upload cannot outstay anything already on the wall. Since
 *  2026-09-12 this is checked at CHECKOUT, when a vault item is picked for a square — the
 *  vault itself takes longer files (settings.vault.maxSeconds). */
export const WALL_UPLOAD_MAX_SECONDS = 60;
/** An image is served RAW — the player never asks for a rung on a still — so its pixels
 *  ship in full to every screen showing the wall. Cap the long edge. (The wall's own byte
 *  cap and per-account count went with its intake, 2026-09-12: the vault's byte quota is
 *  what bounds the disk now.) */
export const WALL_UPLOAD_MAX_DIMENSION = 1920;

/** Where an upload is in its review. `pending` renders nowhere — that one word is the
 *  whole moderation gate (see wallUploadClips). */
/** Every uploaded clip's internal key starts with this. The wall splits a placement's
 *  keys on it to decide which gate a clip must pass — see ViewResolver.wallClips. */
export const UPLOAD_CLIP_PREFIX = "upload:";

/**
 * The reserved id of every account's "Your Uploads" collection — the vault, as a
 * collection. Modelled on the Faps collection exactly: a VIRTUAL playlist computed from
 * the vault_items table, no playlist rows, so it cannot be renamed, deleted or made
 * public (those routes answer 400 for it, as for "faps"), and the picker trades it for
 * the owner-scoped `uploads:<uid>` at tap, which the resolver answers ONLY for the
 * owner's own screen (see PlaylistAdapter).
 */
export const VAULT_COLLECTION_ID = "uploads";

/**
 * The reserved ids of the two VIRTUAL collections — Faps and Your Uploads. Neither has
 * playlist rows; the public resolver answers them only under the owner-scoped form
 * `<id>:<uid>`, and the bare id deliberately resolves to nothing (faps-play.mts). Every
 * surface that turns a picked collection into a playlist source must trade through
 * `ownerScopedPlaylistId` — the region feed picker did not, and "Faps" chosen on the
 * glass played an empty region for as long as the picker existed (2026-09-10).
 */
export const RESERVED_COLLECTION_IDS: ReadonlySet<string> = new Set(["faps", VAULT_COLLECTION_ID]);

/** A picked collection's id as a playlist source: reserved ids become `<id>:<uid>`; with no
 *  signed-in user there is no owner to scope to and the pick is null (the server would
 *  answer nothing anyway). Ordinary collections pass through untouched. */
export function ownerScopedPlaylistId(id: string, userId: string | null | undefined): string | null {
  if (!RESERVED_COLLECTION_IDS.has(id)) return id;
  return userId ? `${id}:${userId}` : null;
}

/** The inverse, for matching a region's live source back to the picker's chip: strips the
 *  `:<uid>` suffix off a reserved id and leaves everything else alone. */
export function basePlaylistId(playlistId: string): string {
  const i = playlistId.indexOf(":");
  if (i < 0) return playlistId;
  const head = playlistId.slice(0, i);
  return RESERVED_COLLECTION_IDS.has(head) ? head : playlistId;
}

/** GET /api/vault — the caller's vault, as the panel draws it. All bytes are bytes. */
export interface VaultStatus {
  enabled: boolean;
  usedBytes: number;
  /** The tier's starting size, and what has been bought on top of it. */
  baseBytes: number;
  extraBytes: number;
  /** The tier's ceiling: base + extra can never exceed it. */
  maxBytes: number;
  stepBytes: number;
  stepPriceFaps: number;
  maxFileBytes: number;
  maxSeconds: number;
  items: number;
}

export type WallUploadStatus = "pending" | "live" | "rejected";

/** A vault item's standing on the wall: `none` = never placed on a square. Rejected
 *  items are not listed at all — there is nothing a buyer can do with one. */
export type WallPlaceStatus = WallUploadStatus | "none";

/**
 * One of the buyer's VAULT items, as the wall's Upload tab lists it (2026-09-12: the
 * vault is the wall's only intake; GET /wall/uploads/mine reads vault_items).
 */
export interface WallUploadItem {
  /** The clip's wire token — `clipKey(item)`, i.e. `upload:<token>` — and what checkout's
   *  `uploads` array carries back. The general resolver still refuses this form (see
   *  clip-ids.ts); the wall admits it only through an owner-scoped decode. */
  id: string;
  status: WallPlaceStatus;
  /** What to render. Carries no `previewUrl`: generating one is a real encode, and a
   *  stranger's hover must not be able to queue transcodes. */
  item: MediaItem;
  /** When it entered the vault. */
  createdAt: number;
}

export interface WallUploadsResponse {
  uploads: WallUploadItem[];
}

/** One row of the moderator's queue — a `wall_uploads` review row, minted when a vault
 *  item was first placed on a square. Its `id` is the REVIEW row's, which is what approve
 *  and reject are addressed by; not the clip token the picker uses. */
export interface AdminWallUpload {
  id: string;
  status: WallUploadStatus;
  item: MediaItem;
  /** When the review row was minted (the first placement), not when the file arrived. */
  createdAt: number;
  username: string;
  /** The square this is riding on, if a placement carries it — null when the buyer
   *  started a checkout and never paid. */
  regionId: string | null;
  /** The Feed post carrying it, if that is where it was posted (2026-09-13). An upload
   *  may wait on the Feed instead of a square; the queue row says which. */
  feedPostId: string | null;
  reviewedAt: number | null;
  reviewedReason: string | null;
}

export interface AdminWallUploadsResponse {
  uploads: AdminWallUpload[];
}

export type WallSquareStatus = "open" | "reserved" | "taken";

/** One square's public state — GET /api/wall/state. Geometry is NOT here: the client
 *  reads WALL_REGIONS directly, so state stays a small poll. */
export interface WallSquareState {
  regionId: string;
  tier: WallTier;
  /** `taken` wins over `reserved` — the 423 at checkout is what actually guards the
   *  race; this field only shapes the badge. */
  status: WallSquareStatus;
  /** Present when taken. `gold` marks a live GoonLords Gold holder — the badge wears
   *  the same GOLD chip every other surface puts on a gold name. */
  holder?: { username: string; gold?: boolean };
  /** What taking this square costs right now: decayed takeover when taken, base when open. */
  minBidCents: number;
  /** Present (true) only while wall.freeClaims is on and the take-price sits at the
   *  tier floor — the client's ONLY "free" signal. Deliberately NOT minBidCents === 0:
   *  a $0.00 with no boolean stays the loud misconfig alarm. */
  freeClaim?: true;
  clipCount: number;
  placedAt?: number;
}

export interface WallStateResponse {
  /** Which board this is — the response names it so a client polling two walls cannot
   *  paint one's prices on the other. */
  wallId: WallId;
  /** Claiming is a GoonLords Premium privilege here. Watching never is. */
  premiumOnly: boolean;
  /** Selling is possible: the admin switch AND a way to pay — a processor able to mint
   *  checkouts, or FAPS. */
  enabled: boolean;
  /** What `minBidCents` is denominated in. Under FAPS the NUMBER is the same (1 FAP =
   *  1¢); only the label and the checkout leg change. */
  currency: "faps" | "cash";
  squares: WallSquareState[];
}

export type WallCheckoutResponse =
  /** The paid path: go to PayzMoon. */
  | { checkoutUrl: string; transactionId: string }
  /** The free path and the FAPS path: the placement is already live — no processor,
   *  nothing pending. `fapsBalance` is present when FAPS paid for it. */
  | { claimed: true; regionId: string; fapsBalance?: number };

/** POST /api/wall/defend — a holder re-stamped their own square (Faps only). `minBidCents`
 *  is the fresh takeover price (multiplier × what defending cost), so the dialog and board
 *  update without a re-poll; `fapsBalance` is the balance after the debit. */
export interface WallDefendResponse {
  defended: true;
  regionId: string;
  minBidCents: number;
  fapsBalance: number;
}

/** One ledger row as the admin panel lists it. */
export interface AdminWallPlacement {
  id: string;
  regionId: string;
  tier: string;
  username: string;
  clipCount: number;
  pricePaidCents: number;
  /** What `pricePaidCents` was paid in. Derived: a placement with no processor
   *  transaction and a price was bought with Faps. */
  currency: "faps" | "cash";
  createdAt: number;
  revokedAt: number | null;
  revokeReason: string | null;
  /** live = the board shows it; displaced = outbid; revoked = pulled (admin or refund). */
  status: "live" | "displaced" | "revoked";
}

export interface AdminWallResponse {
  placements: AdminWallPlacement[];
}

// ---------------------------------------------------------------------------
// The Feed (2026-09-13) — posts, comments, the daily boards.
//
// Every post carries exactly ONE thing to watch — a clip, or one of the poster's public
// collections; text is optional and a post with neither cannot exist. The clip is addressed on the wire the way every clip is (a masked
// MediaItem); the post's stored clip key is internal and NEVER appears on any DTO here,
// the wall_placements.clip_keys rule.
// ---------------------------------------------------------------------------

export const FEED_CAPTION_MAX = 280;
export const FEED_COMMENT_MAX = 500;
export const FEED_PAGE_SIZE = 20;

// ---------------------------------------------------------------------------
// GOON ROOMS (2026-09-19) — a shared screen one person drives.
//
//   POST   /api/rooms {name, public, viewId?}      -> RoomResponse      (user; 5/h)
//   GET    /api/rooms                              -> RoomsListResponse (public open rooms; the house rooms; `mine` signed in)
//   GET    /api/rooms/:id                          -> RoomResponse      (public; the id IS the link)
//   POST   /api/rooms/:id/close                    -> { ok }            (owner)
//   PUT    /api/rooms/:id/view {viewId|null}       -> RoomResponse      (owner)
//   POST   /api/rooms/:id/bans {username}          -> { ok }            (owner)
//   DELETE /api/rooms/:id/bans/:username           -> { ok }            (owner)
//
// The people, the chat and the moderation ride the room SOCKET (see WsMessage's
// `role: "room"` block); the room's VIEW rides the display socket like any view's.
// ---------------------------------------------------------------------------

export const ROOM_NAME_MAX = 40;
/** Hard, every tier — a mesh of cameras and a wall of tiles per member is the ceiling's
 *  reason, not a paywall's. Enforced at the socket join. */
export const MAX_ROOM_MEMBERS = 20;
/**
 * THE HOUSE ROOMS (issue #18): always open, hosted by nobody, so /rooms is never empty.
 * Each plays a quick setup, dealt by the server (server/src/house-rooms.ts). The id is the
 * address (`/room/<id>`), and it is not a UUID, so it can never name a row in `rooms`.
 */
export const HOUSE_ROOMS: ReadonlyArray<{ id: string; name: string; templateId: string }> = [
  { id: "house-wall", name: "The House Wall", templateId: "around-the-world" },
  { id: "house-big-screen", name: "The Big Screen", templateId: "russian-roulette" },
];
/** Their door. MAX_ROOM_MEMBERS' reason is a mesh of cameras, and a house room has none;
 *  a room that exists so nobody is turned away should not fill up at twenty. */
export const HOUSE_ROOM_MEMBERS = 200;
/** The name a house room goes by where a member room shows its host. */
export const HOUSE_ROOM_HOST = "Goon Lords";
export const ROOM_CHAT_MAX = 500;
/** The ring the hub keeps per room; a late joiner gets the tail of it. */
export const ROOM_CHAT_HISTORY = 200;
/** Cameras on at once, per room — a peer-to-peer mesh costs every sender (n−1) uploads. */
export const ROOM_CAM_CAP = 6;
export const ROOM_IMAGE_MAX_BYTES = 8 * 1024 * 1024;
export const ROOM_IMAGE_MAX_PX = 720;
export const ROOM_GIF_MAX_SEC = 10;
export const ROOM_IMAGE_TYPES: ReadonlySet<string> = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);
/** The owner's socket may be gone this long (a reload, a flaky link) before the room ends. */
export const ROOM_OWNER_GRACE_MS = 30_000;

export type RoomRole = "owner" | "member";
export type RoomStatus = "open" | "closed";
export type RoomModAction = "kick" | "ban" | "cam_off";
export type RoomRefusal = "signin" | "banned" | "no_room" | "full" | "not_member" | "not_owner" | "rate" | "too_long" | "cam_cap" | "bad_image" | "chat_off";

/** A room as the list shows it. The owner is a FeedAuthor: username-keyed, no id. */
export interface RoomSummary {
  id: string;
  name: string;
  public: boolean;
  owner: FeedAuthor;
  createdAt: number;
  /** Live: the sockets in it right now. */
  members: number;
  /** A house room: nobody hosts it, and the three below are what its card shows. */
  house?: true;
  /** The poster of what is on its first tile now. */
  thumbUrl?: string;
  /** The name of what it plays. */
  showingName?: string;
}
export interface Room extends RoomSummary {
  /** What the host is SHOWING — any resolvable id, a scroll included. Raw for the owner
   *  (`mine`); `memberShowing` for anyone else. Null while nothing is. */
  viewId: string | null;
  mine?: true;
}
export interface RoomsListResponse {
  /** Member-hosted rooms. */
  rooms: RoomSummary[];
  /** The house rooms, separate so `rooms` keeps meaning what its analytics have always counted. */
  house: RoomSummary[];
  /** The caller's own OPEN room, public or private, so the page can offer "back to it". */
  mine?: Room;
}
export interface RoomResponse {
  room: Room;
}
export interface RoomCreateBody {
  name: string;
  public: boolean;
  viewId?: string;
}

/** One person in a room. `pid` is the SOCKET's handle (WebRTC signals to a tab, not a
 *  person; two tabs are two tiles), room-scoped and meaningless outside it; `username`
 *  is the person — the roster, the profile link and moderation key on it. */
export interface RoomMember {
  pid: string;
  username: string;
  avatarUrl?: string;
  gold?: boolean;
  owner?: true;
  cam: boolean;
}
/** What one of the host's regions is playing, as the server relays it. */
export interface RoomRegionState {
  item: MediaItem;
  /** The host's resolved url — a member picks its OWN rung off `item.url` for its tile
   *  size and only falls back to this. */
  src: string;
  /** Server clock: when the host's tile revealed it. */
  startedAt: number;
  /** Monotonic per room; a member keys its crossfade slots on it. */
  seq: number;
}
/** How many of the host's scroll pages the hub keeps for a late joiner. */
export const ROOM_STAGE_PAGES = 8;
/** One Infinite Scroll page the host dealt, as the hub relays it — InfinityStage's own
 *  Page. Regions are BARE and clip-sourced: a member's tiles are driven off `room_playing`
 *  and nothing in here is loaded directly. */
export interface RoomPage {
  no: number;
  regions: Region[];
  portrait: boolean;
  themed: boolean;
  color: string | null;
  round?: { stage: BuildStageId; word: string; picks: BuildPick[] };
  battle?: { round: number; championSlot: number | null; streak: number };
}
/** The host's scroll while the showing is one: the last ROOM_STAGE_PAGES pages, ascending
 *  by `no`, and the `no` of the page on the host's glass (a number, not an index — the
 *  hub keeps a ring and an index would not survive it). */
export interface RoomStageState {
  kind: "infinity";
  pages: RoomPage[];
  active: number;
}
/** One line of what somebody did in a room. `count` grows as one person repeats one
 *  act inside ROOM_ACTIVITY_COALESCE_MS (the same `id` comes again, updated); `target` is
 *  a view's name or a username; `minutes` a session's length. */
export type RoomActivityKind = "session_start" | "session_end" | "edge" | "fap" | "repoint" | "pause" | "resume" | "kick" | "ban";
export interface RoomActivity {
  id: string;
  at: number;
  actor: FeedAuthor;
  kind: RoomActivityKind;
  count?: number;
  target?: string;
  minutes?: number;
}
export const ROOM_ACTIVITY_HISTORY = 40;
/** A repeat of one act by one person inside this window bumps the line's count. */
export const ROOM_ACTIVITY_COALESCE_MS = 90_000;
export interface RoomChatImage {
  key: string;
  w: number;
  h: number;
  kind: "image" | "gif";
}
export interface RoomChatMessage {
  id: string;
  at: number;
  author: FeedAuthor;
  body?: string;
  image?: RoomChatImage;
}
/** Plain shapes, not the DOM's: shared compiles for node too. */
export interface RoomRtcSdp {
  type: "offer" | "answer";
  sdp: string;
}
export interface RoomRtcIce {
  candidate: string;
  sdpMid: string | null;
  sdpMLineIndex: number | null;
}
export interface RoomImageResponse {
  image: RoomChatImage;
}
export interface RoomImageUrlResponse {
  url: string;
}

/** Who wrote it. `gold` marks a live GoonLords Gold holder — the same pair
 *  WallSquareState.holder carries, so the name wears the same chip everywhere. */
export interface FeedAuthor {
  username: string;
  gold?: boolean;
  /** Their profile picture, when they set one; absent = the initials disc. */
  avatarUrl?: string;
}

/** A collection shared on the Feed — the poster's OWN, and PUBLIC (the same rule
 *  /browse lives by; a post whose collection goes private disappears with it). */
export interface FeedCollection {
  id: string;
  name: string;
  clipCount: number;
  /** Up to four servable clips, for the card's cover grid. */
  covers: MediaItem[];
}

export interface FeedPost {
  id: string;
  author: FeedAuthor;
  /** What to render — a served MediaItem, masked like every other. An upload carries
   *  no previewUrl (the wall's rule: a stranger's hover must not queue transcodes).
   *  Exactly one of `item` and `collection` is present. */
  item?: MediaItem;
  /** The other kind of post (2026-09-13): one of your public collections. */
  collection?: FeedCollection;
  caption: string;
  createdAt: number;
  /** The clip's crown count — global, the same number the crown pill shows anywhere. */
  crowns: number;
  /** Live comments, replies included. */
  comments: number;
  /** The caller wrote it (delete is offered). */
  mine?: true;
  /** The clip is the author's own upload, still in the wall's review queue: only the
   *  author sees the post, with a notice, until a moderator clears it. */
  pending?: true;
}

export interface FeedPageResponse {
  posts: FeedPost[];
  /** Pass back as `?before=` for the next page; absent when this was the last. */
  nextBefore?: number;
}

export interface FeedPostResponse {
  post: FeedPost;
}

export interface FeedComment {
  id: string;
  postId: string;
  /** The top-level comment this replies to; null for a top-level comment. Replies go
   *  one level deep — a reply to a reply is refused at the door. */
  parentId: string | null;
  author: FeedAuthor;
  body: string;
  createdAt: number;
  mine?: true;
}

export interface FeedCommentsResponse {
  comments: FeedComment[];
}

export interface FeedCommentResponse {
  comment: FeedComment;
}

/** POST /api/feed/posts/:id/crown — the crown's reply, plus the clip's fresh count so
 *  the pill reconciles without a refetch. */
export interface FeedCrownResponse extends FapClickResponse {
  crowns: number;
}

export type FeedBoard = "gooning" | "faps" | "crowns";

export interface FeedLeaderboardRow {
  username: string;
  gold?: boolean;
  avatarUrl?: string;
  /** Minutes gooned, Faps earned, or crowns given — the board says which. */
  value: number;
  /** 1-based, today. */
  rank: number;
  /** Rank change since yesterday: positive = climbed, negative = fell, 0 = held, null =
   *  was not on yesterday's board at all ("new"). */
  delta: number | null;
}

/** GET /api/feed/leaderboards — today's top five on each board, public profiles only. */
export interface FeedLeaderboardsResponse {
  /** "2026-09-13" — the UTC day these numbers are for. */
  day: string;
  gooning: FeedLeaderboardRow[];
  faps: FeedLeaderboardRow[];
  crowns: FeedLeaderboardRow[];
}

/** One row of the admin's feed list — the post plus who it belongs to, for removal
 *  and for banning the account behind it. */
export interface AdminFeedPost extends FeedPost {
  userId: string;
  /** Soft-deleted rows are listed too, so a moderator can see what was pulled. */
  deletedAt: number | null;
  deletedBy: "author" | "admin" | null;
}

export interface AdminFeedPostsResponse {
  posts: AdminFeedPost[];
}

export const DEFAULT_SETTINGS: SystemSettings = {
  ageGate: { enabled: true, minAge: 18 },
  goonFuel: { enabled: true },
  ads: { enabled: false, zones: {} },
  // The axes the public site already filters on: People, Sex, Race (labelshop's
  // `ethnicity`), Nudity. Body position is deliberately absent until its model is finished.
  labelshop: { importAxes: ["people", "sex", "ethnicity", "nudity"] },
  // OFF until prod has a verified live solve: a broken grid on LOGIN is a lockout.
  gooncha: { enabled: false },
  display: { phoneDprCap: 1.5, phoneRungCeiling: 720, decodeBudget: true },
  // Both switches OFF: selling and the caps both wait for their launch flips, so the
  // whole block can ship dark. The prices are the real ones — goldTierCents turns them
  // into the amount the payment intent charges (see the SystemSettings.gold docstring),
  // so editing a number here IS repricing, not copy-editing.
  gold: {
    enabled: false,
    capsEnabled: false,
    price7d: 5,
    price30d: 10,
    price90d: 25,
    price365d: 80,
    paymentUrl: "",
    wallMinutesFree: 960,
    degradedRegions: 4,
  },
  // Selling OFF: the board renders and fills with random clips from day one, the buy
  // button waits for the launch flip. Prices are real, per the gold block's argument.
  // The small tier sits EXACTLY on MIN_CHARGE_CENTS — the impulse-buy door, priced as
  // low as the processor will actually mint; wallTierCents refuses anything under it.
  wall: {
    enabled: false,
    uploadsEnabled: false,
    // No review by default: uploads publish on arrival; the queue is for after-the-fact.
    reviewUploads: false,
    // Free claims ship dark too — flipping this on is the seeding experiment.
    freeClaims: false,
    priceMarquee: 2,
    priceMedium: 1.5,
    priceSmall: 1,
    takeoverMultiplier: 5,
    decayDays: 7,
    // Bidding sinks ship dark: no cut, defend off, until an operator turns them on. 40
    // is the proposed cut — enough to soften a loss without making holding free.
    displacedCutPct: 0,
    defendEnabled: false,
  },
  // FAPS ships dark. The rate card below is the launch proposal: a casual free day
  // (a few faps, a chest, some watching) lands around 60–100, a small square is 100,
  // and the $10 Patreon tier's stipend is 5,000 — so a buyer gets there instantly and
  // a free user in a day or two. The wall ladder is the dollar ladder × 100 on day one
  // so a legacy $2.00 placement decays toward the same floor a 200-FAPS one does.
  faps: {
    enabled: false,
    welcomeGrant: 100,
    fapReward: 1,
    // A CLIP-COUNT cap now (2026-09-09), not a Faps amount: 100 distinct clips a day,
    // 200 for Premium — the numbers the feature was specced with.
    fapDailyCap: 100,
    fapRewardPremium: 2,
    fapDailyCapPremium: 200,
    // One flat award for the day's first 60 cumulative minutes of playback, no session
    // required (2026-09-09 — replaces the old per-minute-of-ended-session rate).
    watchThresholdMinutes: 60,
    watchReward: 30,
    // Premium earns more, faster: double the watch-time take, and a chest that pays
    // double. The crown's Premium knobs (above) set the precedent.
    watchMultiplierPremium: 2,
    chestPrizeSmall: 50,
    chestPrizeLarge: 100,
    chestPrizeBig: 200,
    chestLargePct: 25,
    chestBigPct: 1,
    chestMultiplierPremium: 2,
    stipendPerEntitledCent: 5,
    wallPriceMarquee: 200,
    wallPriceMedium: 150,
    wallPriceSmall: 100,
    shopUrl: "",
    // 2026-09-09: 2–3 random daily tasks, one bundle reward for finishing all of them,
    // doubled for Premium.
    questCount: 3,
    questBundleReward: 100,
    questMultiplierPremium: 2,
    questTargetFapClips: 5,
    questTargetScrollPages: 20,
    // Bounded low on purpose (2026-09-09): most sittings never edge N times, and a
    // quest nobody can realistically finish is a bad one — the same lesson defend_square
    // and redeem_code taught the same day.
    questTargetEdges: 2,
    questTargetWatchClips: 5,
    questTargetWatchViewMinutes: 5,
    questTargetBattleRounds: 5,
    questTargetRoomMinutes: 5,
  },
  // The members' wall: the same numbers to start (the operator prices it apart in the
  // admin panel), and dark for the same reason — a switch, not a deploy, opens the sale.
  premiumWall: {
    enabled: false,
    freeClaims: false,
    priceMarquee: 2,
    priceMedium: 1.5,
    priceSmall: 1,
    takeoverMultiplier: 5,
    decayDays: 7,
    displacedCutPct: 0,
    defendEnabled: false,
  },
  // Small on purpose, and dear: the owner's numbers, 2026-09-09 ("free is 256 MB or
  // something, and it should be more expensive"). Worst case per account is 1 GB Basic,
  // 4 GB Premium; the global budget is what stands between every account maxing out and
  // a full disk.
  vault: {
    enabled: false,
    baseBytesFree: 256 * 1024 * 1024,
    baseBytesPremium: 1024 * 1024 * 1024,
    maxBytesFree: 1024 * 1024 * 1024,
    maxBytesPremium: 4 * 1024 * 1024 * 1024,
    stepBytes: 256 * 1024 * 1024,
    stepPriceFaps: 1000,
    maxFileBytes: 50 * 1024 * 1024,
    maxSeconds: 600,
    diskBudgetBytes: 50 * 1024 * 1024 * 1024,
  },
  // Dark, like every surface that publishes what a stranger typed. One flip opens it.
  feed: { enabled: false },
  // House chat ON (2026-11-03, the owner's call): a house room has no host to kick or ban,
  // so the admin switch stays for closing it again.
  rooms: { enabled: false, houseRooms: true, houseChat: true },
};



/**
 * How a media response was satisfied. The distinction that matters is
 * `rendition` vs `renditionAsMaster`: both answer a `/r/<height>` request, but only
 * the first ships a file sized for the tile. The second means the ladder has a rung
 * in name only — the master was already at or below that height, so the tile pays
 * master bytes (and master frame rate) for a small picture.
 */
export type MediaServeOutcome =
  /** GET /api/media/:key — the master, asked for by name. */
  | "master"
  /** GET /api/media/:key/r/:h — a real downscaled sidecar. */
  | "rendition"
  /** GET /api/media/:key/r/:h — the master, because it is already <= :h tall. */
  | "renditionAsMaster"
  /** GET /api/media/:key/r/:h — 302 to the master while the rung encodes. */
  | "renditionCold"
  /** GET /api/media/:key/preview — the short hover loop. */
  | "preview";

/** Counters describing how media is actually leaving the server right now. */
export interface MediaServeStats {
  startedAt: number;
  /** Per-outcome request count and bytes delivered (partial transfers counted as sent). */
  outcomes: Partial<Record<MediaServeOutcome, { requests: number; bytes: number }>>;
  /** Responses streaming at this instant, and the high-water mark. Compare against the
   *  browser's 6-connections-per-origin cap on HTTP/1.1. */
  inFlight: number;
  peakInFlight: number;
  /** Over a rolling window of recent responses: how many asked for a variant the
   *  client had already been sent. Non-zero means something is downloading twice. */
  recent: { requests: number; distinct: number; repeats: number };
}

/** Read-only server diagnostics shown in the admin panel. */
export interface AdminServerInfo {
  version: string;
  nodeVersion: string;
  platform: string;
  /** Whole seconds the process has been up. */
  uptimeSec: number;
  startedAt: number;
  /** Absolute path of the JSON data file. */
  dataFile: string;
  /** True when Reddit OAuth creds are present in the environment. */
  /** Local media cache footprint. */
  mediaCache: { files: number; bytes: number };
  /** How media is being served right now — the ladder's real hit rate, concurrency,
   *  and whether anything is being downloaded twice. */
  mediaServe: MediaServeStats;
  /** Encode slots busy/queued, by lane. Interactive work is something a tile is
   *  waiting on; background is ingest and backfill. */
  transcode: Record<"interactive" | "background", { active: number; waiting: number }>;
}

export interface AdminLoginRequest {
  password: string;
}
export interface AdminLoginResponse {
  token: string;
}
export interface AdminSettingsResponse {
  settings: SystemSettings;
  server: AdminServerInfo;
}

/** One account as the admin Users section lists it. */
export interface AdminUserSummary {
  id: ID;
  username: string;
  createdAt: number;
  goldUntil?: number;
}
export interface AdminUsersResponse {
  users: AdminUserSummary[];
}
/** One row of the gold_grants ledger — an extension (days > 0) or a revoke (days = 0). */
export interface AdminGoldGrant {
  id: number;
  days: number;
  source: string;
  note: string | null;
  createdAt: number;
}
/** One labelshop axis as the admin panel lists it: what it writes, and how much is waiting. */
export interface AdminLabelAxis {
  /** labelshop axis key — the value that goes in LabelshopSettings.importAxes. */
  key: string;
  title: string;
  /** The catalog namespace it writes (`actor_count`, `race`, …). */
  namespace: string;
  /** True when that namespace is a declared catalog facet (renders a sidebar group). */
  facet: boolean;
  perActor: boolean;
  /** Approved labels in labelshop on this axis. */
  approved: number;
  enabled: boolean;
}
export interface AdminLabelAxesResponse {
  axes: AdminLabelAxis[];
}
/** What one labelshop import run did (or, for a dry run, would do). Null when the box has
 *  no labelshop database to read. */
export interface AdminLabelImportSummary {
  scanned: number;
  matched: number;
  added: number;
  changed: number;
  promoted: number;
  unchanged: number;
  byAxis: Record<string, { added: number; changed: number; promoted: number; unchanged: number }>;
  missingClip: number;
  skipped: { axisOff: number; cantTell: number; noClipId: number; outOfVocabulary: number; castNotOne: number };
  conflicts: Array<{ clipId: string; file: string; axis: string; values: string[]; chose: string }>;
  perActorCleared: Array<{ clipId: string; tag: string; source: string }>;
  axes: string[];
  dryRun: boolean;
}
export interface AdminLabelImportResponse {
  result: AdminLabelImportSummary | null;
}

/** Deep-merge a partial settings patch onto a base, tolerating missing sections. */
export function mergeSettings(
  base: SystemSettings,
  patch: Partial<SystemSettings> | undefined,
): SystemSettings {
  return {
    ageGate: { ...base.ageGate, ...(patch?.ageGate ?? {}) },
    goonFuel: { ...base.goonFuel, ...(patch?.goonFuel ?? {}) },
    labelshop: {
      ...(base.labelshop ?? DEFAULT_SETTINGS.labelshop),
      ...(patch?.labelshop ?? {}),
      // Replaces wholesale: a merge could never turn an axis OFF.
      importAxes:
        patch?.labelshop?.importAxes ??
        base.labelshop?.importAxes ??
        DEFAULT_SETTINGS.labelshop.importAxes,
    },
    gooncha: {
      ...(base.gooncha ?? DEFAULT_SETTINGS.gooncha),
      ...(patch?.gooncha ?? {}),
    },
    // Defaults first (the wall's argument): a settings file written before this block
    // existed reads as the shipping values, not as undefined.
    display: {
      ...DEFAULT_SETTINGS.display,
      ...(base.display ?? {}),
      ...(patch?.display ?? {}),
    },
    gold: {
      ...(base.gold ?? DEFAULT_SETTINGS.gold),
      ...(patch?.gold ?? {}),
    },
    wall: {
      // Defaults FIRST, so a key added after the settings file was written arrives at its
      // default rather than as undefined. The other blocks fall back to defaults only
      // when the whole section is missing, which was fine while sections never grew;
      // `uploadsEnabled` is the first key to be added to a section already on disk in
      // production, and a switch that reads `undefined` is a switch with no stated
      // position.
      ...DEFAULT_SETTINGS.wall,
      ...(base.wall ?? {}),
      ...(patch?.wall ?? {}),
    },
    // Defaults first, the wall's argument: this whole section is younger than every
    // settings file on disk, and a rate card with holes in it is a faucet that pays
    // NaN. (This merge is hand-written per block — a section missing from here is
    // `undefined` at runtime while the type says otherwise.)
    faps: {
      ...DEFAULT_SETTINGS.faps,
      ...(base.faps ?? {}),
      ...(patch?.faps ?? {}),
    },
    // Same discipline: this whole block is absent from every settings file written before
    // the Premium Wall existed, so defaults go first or the panel reads `undefined`.
    premiumWall: {
      ...DEFAULT_SETTINGS.premiumWall,
      ...(base.premiumWall ?? {}),
      ...(patch?.premiumWall ?? {}),
    },
    // Defaults first, the faps argument: younger than every settings file on disk, and
    // a quota with a hole in it is a vault that admits NaN bytes.
    vault: {
      ...DEFAULT_SETTINGS.vault,
      ...(base.vault ?? {}),
      ...(patch?.vault ?? {}),
    },
    // Defaults first, the same argument: no settings file on disk has heard of the Feed.
    feed: {
      ...DEFAULT_SETTINGS.feed,
      ...(base.feed ?? {}),
      ...(patch?.feed ?? {}),
    },
    rooms: {
      ...DEFAULT_SETTINGS.rooms,
      ...(base.rooms ?? {}),
      ...(patch?.rooms ?? {}),
    },
    ads: {
      ...(base.ads ?? DEFAULT_SETTINGS.ads),
      ...(patch?.ads ?? {}),
      // Replaces wholesale rather than merging: a merge would make a CLEARED zone id
      // un-clearable, so a zone pulled from the admin panel would keep serving. (This
      // used to say "like subredditRules above" — those went to the producer with the
      // rest of ingestion, and a comment pointing at a vanished neighbour is worse than
      // one that states the rule itself.)
      zones: patch?.ads?.zones ?? base.ads?.zones ?? {},
    },
  };
}

// ---------------------------------------------------------------------------
// User accounts + clip catalog + manual classification  (v2 foundation)
//
// Accounts are OPTIONAL: anonymous visitors use the whole app but can't save.
// Signed-in users get a personal bearer token and can classify clips into a
// catalog (keep/reject + category tags), which future playlists/stats build on.
//
//   POST /api/auth/register {username,password} -> AuthResponse
//   POST /api/auth/login    {username,password} -> AuthResponse
//   POST /api/auth/logout                        -> { ok: true }        (auth)
//   GET  /api/auth/me                            -> { user }            (auth)
//   POST /api/clips/classify {clip,verdict,categories} -> ClassificationResponse (auth)
//   GET  /api/clips/classification?clipId=...    -> ClassificationResponse (auth)
//   GET  /api/clips?verdict=keep&category=...     -> CatalogResponse     (auth)
//   GET  /api/clips/categories                    -> { categories }      (auth)
// ---------------------------------------------------------------------------

export interface User {
  id: ID;
  username: string;
  createdAt: number;
  /**
   * GoonLords Gold paid-through timestamp — present (and only meaningful) on YOUR OWN
   * user via /auth/me and the auth responses. `goldUntil > now` is the entire
   * entitlement check; a renewal payment and an admin grant both extend it, and a lapse
   * is just this timestamp passing.
   */
  goldUntil?: number;
  /**
   * Whether /u/<username> serves this account's stats to anyone who opens it. Like
   * `goldUntil`, present (and only meaningful) on YOUR OWN user via /auth/me and the
   * auth responses — it is the state of the owner's own switch, never a fact about
   * someone else. The public read of it is `PublicProfile.isPublic`.
   */
  profilePublic?: boolean;
  /** The FAPS balance, your own user only — the cached column the ledger maintains.
   *  The wallet read is the authority; this is the number the first paint can use. */
  fapsBalance?: number;
  /** The profile picture (2026-09-13), a served image url; absent = the initials disc.
   *  Present on every User the wire carries — it is public by construction. */
  avatarUrl?: string;
}

export interface AuthResponse {
  token: string;
  user: User;
  /** Present (and true) ONLY when the signed-in account is a QA test account
   *  (users.is_test, which only a keyed signup through server/src/test-bot.ts sets), on
   *  every session the server mints. The client tags the person and the signup/login event
   *  `is_test_bot` so PostHog's test-account filter can drop them. Never the key itself. */
  testBot?: true;
}
export interface RegisterRequest {
  username: string;
  password: string;
  /** The second box on the form. A typo guard, not a security control — see
   *  passwordError, which is where both sides check it. */
  passwordConfirm: string;
  /** Stored for account recovery and NOTHING else. Nothing in this app sends mail. */
  email: string;
  /** 'YYYY-MM-DD'. Refused under settings.ageGate.minAge — see dobError, which is
   *  emphatic that this is a door policy and not a verification. */
  dob: string;
  gender: Gender;
  /** Exactly SECURITY_ANSWER_COUNT, all three question ids distinct. */
  security: SecurityAnswer[];
  /** GOONCHA pass token, required while settings.gooncha.enabled. One-shot. */
  gooncha?: string;
}
export interface LoginRequest {
  username: string;
  password: string;
  /** GOONCHA pass token, required while settings.gooncha.enabled. One-shot. */
  gooncha?: string;
}

/**
 * GOONCHA — the robot check that labels the library.
 *
 *   POST /api/gooncha/new              -> GoonchaChallenge | GoonchaPass (degraded)
 *   POST /api/gooncha/solve {challengeId, picks:[0..8]} -> GoonchaPass | 403 ApiError
 *
 * `tiles` are NINE media rung paths in grid order; a pick is an INDEX. The wire
 * deliberately carries no clip identity — the server keeps index→clip in memory,
 * because the whole point of the grade is that the client cannot know which tiles
 * are the graded ones.
 */
export interface GoonchaChallenge {
  challengeId: string;
  /** The prompt's display name — "Select all tiles with <prompt>". */
  prompt: string;
  /** Nine playable tile URLs, grid order. Short clips, lowest rung, meant to loop. */
  tiles: string[];
  expiresAt: number;
}
export interface GoonchaPass {
  ok: true;
  /** One-shot token that register/login consumes while GOONCHA is enabled. */
  token: string;
  expiresAt: number;
}

export const USERNAME_MIN = 3;
export const USERNAME_MAX = 24;
/**
 * The password floor — raised 6 -> 8 on 2026-09-20, and safe to raise because
 * `UserAuth.login` never reads it. Only `register` checks a length, so every account
 * already holding a six- or seven-character password goes on signing in forever; the
 * floor binds what is NEWLY typed and strands nobody. Anything that starts checking
 * this on the way IN is a lockout, so say why before you add one.
 */
export const PASSWORD_MIN = 8;

/** Shared username rule (client shows the hint; server enforces). */
export function usernameError(name: string): string | null {
  if (name.length < USERNAME_MIN || name.length > USERNAME_MAX) {
    return `Username must be ${USERNAME_MIN}–${USERNAME_MAX} characters.`;
  }
  if (!/^[a-zA-Z0-9_]+$/.test(name)) {
    return "Username may only contain letters, numbers, and underscores.";
  }
  return null;
}


/*
 * ---------------------------------------------------------------------------
 * The sign-up profile (2026-09-20): an email, a date of birth, a gender, and three
 * security questions.
 *
 * Accounts were a username and a password and nothing else, which meant an account
 * that lost its password was gone. These four facts are what a recovery could one day
 * stand on. Required of NEW accounts only: every account that predates this carries
 * them blank, is never prompted and is never gated, and fills them in at /settings if
 * it feels like it. Blank is a legitimate, permanent state — write code that reads
 * these as "not answered", never as "not yet answered".
 *
 * Everything here is pure and shared so the form and the door enforce one rule and
 * refuse in one sentence — the reason usernameError lives up there.
 * ---------------------------------------------------------------------------
 */

/** An address is at most this long (RFC 5321's practical ceiling). */
export const EMAIL_MAX = 254;

/**
 * Does this look like an email? Deliberately shallow — the only honest test of an
 * address is sending to it, and we never send. It rejects typos, not liars.
 *
 * Hoisted out of the Gold checkout (routes/gold.ts), which had this exact rule inline
 * for the processor's receipt field. One definition, two voices for refusing it.
 */
export function isEmailShaped(email: string): boolean {
  return email.length >= 3 && email.length <= EMAIL_MAX && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/** What gets stored: trimmed and lowercased. The UNIQUE index is NOCASE as well, but
 *  that is belt-and-braces — SQLite's NOCASE folds ASCII only, this folds properly. */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** The shared email rule (the form shows it; the server enforces it). */
export function emailError(email: string): string | null {
  if (!email.trim()) return "An email is required.";
  if (!isEmailShaped(normalizeEmail(email))) return "That doesn't look like an email address.";
  return null;
}

/**
 * The password rule, beside usernameError for the same reason.
 *
 * `confirm` is the second box on the sign-up form. It is a TYPO GUARD, not a security
 * control — the server checks it only because it is free to, and nothing is weaker if
 * a caller omits it.
 */
export function passwordError(password: string, confirm?: string): string | null {
  if (password.length < PASSWORD_MIN) return `Password must be at least ${PASSWORD_MIN} characters.`;
  if (confirm !== undefined && password !== confirm) return "Those passwords don't match.";
  return null;
}

/** The five, as stored — lowercase slugs on the wire, never the label. */
export const GENDERS = ["man", "woman", "trans", "non-binary", "prefer-not-to-say"] as const;
export type Gender = (typeof GENDERS)[number];

/** What a human reads. Separate from the slug so a rewording is not a migration. */
export const GENDER_LABELS: Readonly<Record<Gender, string>> = {
  man: "Man",
  woman: "Woman",
  trans: "Trans",
  "non-binary": "Non-binary",
  "prefer-not-to-say": "Prefer not to say",
};

export function isGender(v: unknown): v is Gender {
  return typeof v === "string" && (GENDERS as readonly string[]).includes(v);
}

/**
 * Whole years old on `at`, in UTC, or null when `dob` is not a real calendar date.
 *
 * The round-trip is the whole function. `new Date("2026-02-31T00:00:00Z")` is NOT
 * Invalid Date — it silently rolls to March 3rd, and "2025-02-29" rolls to March 1st
 * — so a regex plus a Date parse accepts February 31st and calls it verified. Compare
 * the parsed date back to the string it came from and the rollovers fall out.
 *
 * UTC throughout, and the string is never re-parsed as a local date: `new Date(dob)`
 * with a bare date is UTC midnight, which is the day BEFORE in every western timezone,
 * and that is the classic way a birthday arrives a day late.
 */
export function ageOn(dob: string, at: number): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) return null;
  const born = new Date(`${dob}T00:00:00Z`);
  if (Number.isNaN(born.getTime()) || born.toISOString().slice(0, 10) !== dob) return null;
  const now = new Date(at);
  let age = now.getUTCFullYear() - born.getUTCFullYear();
  const monthDiff = now.getUTCMonth() - born.getUTCMonth();
  // Not had this year's birthday yet — the day-before-your-birthday edge case.
  if (monthDiff < 0 || (monthDiff === 0 && now.getUTCDate() < born.getUTCDate())) age -= 1;
  return age;
}

/**
 * The 18+ refusal at the sign-up door.
 *
 * THIS IS NOT THE AGE GATE AND IT IS NOT VERIFICATION. `/age/verify` deliberately
 * STOPPED taking a date of birth, because doing arithmetic on a typed-in year "looked
 * like verification and was not" (routes/age.ts says it at length). Nothing here
 * disagrees. That gate is an anonymous attestation that mints a cookie; this is a
 * profile field on a named account — a fact the account states about itself, stored,
 * and a reason to refuse a signup. The refusal is a door policy, not a claim that
 * anybody checked anything, which is why the copy says "you need to be 18" and never
 * "we couldn't verify your age".
 *
 * `minAge` is passed in rather than hardcoded: settings.ageGate.minAge already exists
 * and is admin-configurable, and two numbers meaning "how old you must be" is one
 * number too many.
 */
export function dobError(dob: string, now: number, minAge: number): string | null {
  if (!dob.trim()) return "Your date of birth is required.";
  const age = ageOn(dob, now);
  if (age === null) return "That isn't a real date.";
  if (age < 0) return "That date is in the future.";
  if (age > 120) return "That date is too far back.";
  if (age < minAge) return `You need to be ${minAge} or older to make an account.`;
  return null;
}

/** One question from the fixed pool. */
export interface SecurityQuestion {
  id: string;
  /** As asked. The site's voice — direct, lowercase, no house style. */
  prompt: string;
}

/**
 * The pool, frozen in code rather than in settings.json — the GOONCHA_PROMPTS
 * precedent, and for a harder reason than that one has: `id` is the key every stored
 * answer hangs off. A prompt may be REWORDED freely; an id that changes, or is reused
 * for a different question, silently orphans or misattributes every answer already
 * written under it. Add to the end, never renumber, never recycle.
 *
 * Ordered stable-answer-first on purpose. Half of these are survey questions whose
 * true answer drifts month to month ("how many screens", "what time of day"), and a
 * recovery flow that demands what was true at signup will fail honest people. When
 * recovery is built it should take the earlier questions, or 2 of 3 rather than 3 of 3.
 */
export const SECURITY_QUESTIONS: readonly SecurityQuestion[] = [
  { id: "start-age", prompt: "How old were you when you started gooning?" },
  { id: "first-site", prompt: "What site did you find your first porn on?" },
  { id: "irl-name", prompt: "First name of someone you know in real life that you fap to?" },
  { id: "pornstar", prompt: "Which pornstar makes you leak the most?" },
  { id: "porn-type", prompt: "What kind of porn do you cum the hardest to?" },
  { id: "hand-or-toy", prompt: "Right hand, left hand, or a toy?" },
  { id: "first-crush", prompt: "Who was the first person you ever jerked off thinking about?" },
  { id: "screens", prompt: "How many screens are going when you goon?" },
  { id: "goon-hour", prompt: "What time of day do you goon the most?" },
  { id: "longest-session", prompt: "How many hours was your longest session?" },
];

/**
 * Questions whose answers are kept for RECOVERY ONLY and never tallied into the
 * operator's survey.
 *
 * `irl-name` collects a fact about somebody who is not a user, never consented, and
 * cannot ask for it back. It earns its place as a recovery question — it is memorable
 * and it does not drift — but aggregating it would be collecting on third parties for
 * statistics, which is a different act. The survey read is what honours this.
 */
export const SECURITY_QUESTIONS_NO_SURVEY: readonly string[] = ["irl-name", "first-crush"];

export function isSecurityQuestionId(id: unknown): id is string {
  return typeof id === "string" && SECURITY_QUESTIONS.some((q) => q.id === id);
}

/** How many a new account picks. */
export const SECURITY_ANSWER_COUNT = 3;
/**
 * ONE character, and it has to be.
 *
 * This was 2 until the pool met it: "how many screens are going when you goon?" and
 * "how many hours was your longest session?" both have honest single-digit answers, and
 * a floor of 2 refused the true answer to a question we asked. A one-character answer
 * is weak for recovery — but the fix for that is a better question, not a rule that
 * tells somebody their own answer is wrong.
 */
export const SECURITY_ANSWER_MIN = 1;
export const SECURITY_ANSWER_MAX = 120;

/**
 * What gets hashed AND what gets stored: trimmed, inner whitespace collapsed,
 * lowercased. "  Riley   REID " and "riley reid" are the same answer, because six
 * months later nobody remembers their own capitalisation.
 *
 * The hash is taken over THIS, never over the raw input — hash the raw string and
 * recovery would demand the original spacing and casing, which is the whole thing
 * normalizing was for.
 */
export function normalizeSecurityAnswer(answer: string): string {
  return answer.trim().replace(/\s+/g, " ").toLowerCase();
}

export function securityAnswerError(answer: string): string | null {
  const normalized = normalizeSecurityAnswer(answer);
  if (normalized.length < SECURITY_ANSWER_MIN) return "Answer all three questions.";
  if (normalized.length > SECURITY_ANSWER_MAX) {
    return `Keep answers under ${SECURITY_ANSWER_MAX} characters.`;
  }
  return null;
}

/** One picked question and what was typed under it. */
export interface SecurityAnswer {
  questionId: string;
  answer: string;
}

/**
 * The account's own four facts — OWN ACCOUNT ONLY, and its own type for that reason.
 *
 * Deliberately NOT four more optionals on `User`. `User` is what Db.mapUser returns,
 * and a User reaches strangers: a room's activity line, a feed author, an admin list.
 * goldUntil and profilePublic ride there because leaking either is nothing; an email
 * and a date of birth are not in that class, and the cheapest way to guarantee they
 * never arrive somewhere public is for them to live somewhere mapUser cannot reach.
 */
export interface AccountProfile {
  email: string | null;
  /** 'YYYY-MM-DD', or null when never set — which is exactly what unlocks editing. */
  dob: string | null;
  /** `dob !== null`. Sent so the form reads the rule rather than re-deriving it. */
  dobLocked: boolean;
  gender: Gender | null;
  /**
   * Which questions were answered, in the order they were picked. IDS ONLY — the
   * answers never leave the server on any door but the admin survey, in either
   * direction. The Settings form starts its three answer boxes empty for this reason
   * and says so; there is nothing to prefill it with.
   */
  securityQuestionIds: string[];
}

export interface AccountProfileResponse {
  profile: AccountProfile;
}

/** PATCH /api/auth/profile — every field optional; a body with none of them is a 400. */
export interface AccountProfilePatch {
  email?: string;
  dob?: string;
  gender?: Gender;
  /** All three at once or not at all — never a single slot. */
  security?: SecurityAnswer[];
}

/**
 * Password recovery (2026-10-25): the three security questions, ALL THREE right.
 *
 * Not two of three. Several questions in the pool have a handful of honest answers
 * ("right hand, left hand, or a toy?", "how many screens") — two of those together are
 * ~15 guesses, which a daily attempt cap only slows to a few days. Three of three is the
 * operator's call (2026-10-25), and RECOVERY_MAX_FAILURES per RECOVERY_WINDOW_MS per
 * account is the rate, counted in the database so a restart does not reset it.
 */
export const RECOVERY_MAX_FAILURES = 5;
export const RECOVERY_WINDOW_MS = 24 * 60 * 60 * 1000;

/** POST /api/auth/recover/start — a username and the robot check. */
export interface RecoverStartRequest {
  username: string;
  gooncha?: string;
}
/** The account's three question ids, in the order they were picked, and the one-shot
 *  ticket the finish step must carry. The client maps ids to prompts itself. */
export interface RecoverStartResponse {
  ticket: string;
  questionIds: string[];
}
/** POST /api/auth/recover/finish — the three answers in question order, and the new password. */
export interface RecoverFinishRequest {
  ticket: string;
  answers: string[];
  password: string;
  passwordConfirm: string;
}
/** POST /api/auth/password — signed in, knows the current password. */
export interface ChangePasswordRequest {
  current: string;
  password: string;
  passwordConfirm: string;
}
/** POST /api/auth/delete-account — signed in, and the password again. Irreversible. */
export interface DeleteAccountRequest {
  password: string;
}

/** Keep or reject — the manual quality gate. */
export type ClipVerdict = "keep" | "reject";

/** One user's classification of one clip. */
export interface Classification {
  clipId: string;
  verdict: ClipVerdict;
  categories: string[];
  updatedAt: number;
}

export interface ClassifyRequest {
  clip: MediaItem;
  verdict: ClipVerdict;
  categories?: string[];
}
export interface ClassificationResponse {
  classification: Classification | null;
}

/** A catalogued clip plus the requesting user's classification of it. */
export interface CatalogItem {
  clip: MediaItem;
  verdict: ClipVerdict;
  categories: string[];
  updatedAt: number;
}
export interface CatalogResponse {
  items: CatalogItem[];
}

/** Stable catalog id for a media item (source-scoped so ids never collide). */
export function clipKey(item: Pick<MediaItem, "id" | "sourceKind">): string {
  return `${item.sourceKind}:${item.id}`;
}

// ---------------------------------------------------------------------------
// Playlists — curated clip lists, usable as a region source.
//
//   GET    /api/playlists                          -> { playlists }        (auth)
//   POST   /api/playlists {name}                   -> Playlist             (auth)
//   GET    /api/playlists/:id                      -> PlaylistDetail       (auth, owner)
//   PATCH  /api/playlists/:id UpdatePlaylistRequest -> Playlist            (auth, owner)
//   DELETE /api/playlists/:id                      -> { ok: true }         (auth, owner)
//   POST   /api/playlists/:id/items {clip}         -> { ok: true }         (auth, owner)
//   DELETE /api/playlists/:id/items/:clipId        -> { ok: true }         (auth, owner)
//   POST   /api/playlists/:id/bulk BulkCollectionRequest -> BulkCollectionResponse (auth, owner)
//   POST   /api/playlists/bulk/undo {token}        -> { ok: true }         (auth, the same user)
//
// A playlist a user marks `public` is discoverable by everyone (see Collections).
// ---------------------------------------------------------------------------

export interface Playlist {
  id: ID;
  ownerId: ID;
  name: string;
  description?: string;
  /** The owner published it to the public discovery surface. */
  public: boolean;
  /** Admin-curated from the admin panel — badged "Editor's Choice". */
  editorsChoice: boolean;
  /** clipKey of the clip chosen as the Browse thumbnail (unset = first clip). */
  thumbnailClipId?: string;
  /** Resolved poster of the thumbnail clip, for cards. Server-populated (fail-closed to
   *  local media); absent on the detail responses that don't need it. */
  thumbnailPosterUrl?: string;
  clipCount: number;
  createdAt: number;
  updatedAt: number;
}

export interface PlaylistDetail extends Playlist {
  items: MediaItem[];
}

export interface CreatePlaylistRequest {
  name: string;
}
export interface UpdatePlaylistRequest {
  name?: string;
  description?: string;
  public?: boolean;
  /** clipKey to use as the Browse thumbnail; null clears back to the first clip. */
  thumbnailClipId?: string | null;
}
export interface PlaylistsResponse {
  playlists: Playlist[];
}

/**
 * Bulk select on a collection page (issue #12): several clips, one request, one
 * transaction. `clips` are WIRE ids — `clipKey(item)` of what the page was handed, the
 * same string the single remove sends. Select all sends every id the page holds: a
 * collection already arrives whole, so there is nothing a server-side "all" flag would
 * know that the page does not.
 *
 *   - add    — copy into `to`; the source is untouched.
 *   - move   — into `to` and out of the source, together or not at all. Out of Faps
 *              means unfapping.
 *   - remove — out of the source only.
 *
 * `to` is an existing collection the caller owns, or `{newName}` to create one in the
 * same transaction. Faps and Your Uploads are never a destination.
 */
export type BulkCollectionAction = "add" | "move" | "remove";
export const BULK_COLLECTION_MAX = 5000;
export interface BulkCollectionRequest {
  action: BulkCollectionAction;
  clips: string[];
  to?: { id: ID } | { newName: string };
}
export interface BulkCollectionResponse {
  /** Clips that actually changed: moved, added or removed. */
  applied: number;
  /** Wire ids that were not applied, by reason. `already` = already in the destination
   *  (for a move those still leave the source — the clip ends where it was asked to). */
  skipped: { already: string[]; invalid: string[]; banned: string[]; upload: string[] };
  /** The destination after the change (add / move), counts current. */
  destination?: Playlist;
  /** Hand back to POST /playlists/bulk/undo within BULK_UNDO_TTL_MS to restore exactly
   *  what this call changed, original order included. */
  undoToken: string;
}
export const BULK_UNDO_TTL_MS = 2 * 60_000;

// ---------------------------------------------------------------------------
// Collections — the PUBLIC face of playlists (discovery page + source picker).
// A "collection" is just a playlist with public=true; Editor's Choice ones are
// admin-curated. These endpoints need no auth so displays can resolve them.
//
//   GET  /api/collections          -> PublicPlaylistsResponse   (public)
//   GET  /api/collections/:id      -> PublicPlaylistDetail       (public)
//
// Admin curation (auth, admin bearer):
//   GET    /api/admin/collections                 -> { collections: Playlist[] }
//   POST   /api/admin/collections {name,...}       -> Playlist
//   GET    /api/admin/collections/:id              -> PlaylistDetail
//   PATCH  /api/admin/collections/:id UpdatePlaylistRequest -> Playlist
//   DELETE /api/admin/collections/:id              -> { ok: true }
//   POST   /api/admin/collections/:id/items {clip} -> { ok: true }  (caches clip)
//   DELETE /api/admin/collections/:id/items/:clipId -> { ok: true }
//   GET    /api/admin/clips?q=...                   -> { items: MediaItem[] } (global catalog)
// ---------------------------------------------------------------------------

/** A publicly-listed playlist as shown on the discovery page + public picker. */
export interface PublicPlaylist {
  id: ID;
  name: string;
  description?: string;
  editorsChoice: boolean;
  /** Display name of the owner, or null for Editor's Choice / system-owned. */
  ownerName: string | null;
  /** The curator holds GoonLords Gold right now — drives the GOLD chip on their byline.
   *  Always false for Editor's Choice rows, whose owner is the site itself. */
  ownerGold?: boolean;
  clipCount: number;
  /** Poster of the thumbnail clip (the chosen one, else the first clip). */
  thumbnailPosterUrl?: string;
  updatedAt: number;
  createdAt: number;
}
/** GET /api/u/:username — the public profile page (what the share-card QR opens).
 *
 *  Identity, Gold, join date and published collections always ride here. Stats ride
 *  here too, but ONLY when the owner has made their profile public: consent is one
 *  all-or-nothing switch on the account (`users.profile_public`), not a per-field
 *  policy and not a per-card act. A private profile serves `stats: null` to everyone
 *  but the owner, who still gets their own page so the switch doesn't look broken.
 *
 *  This replaced an earlier rule that kept stats off this shape entirely. Per-user
 *  activity is still the most sensitive data the app holds — the switch is what earns
 *  the right to publish it, so anything reading `stats` must respect `isPublic`. */
export interface PublicProfile {
  username: string;
  /** Holds GoonLords Gold right now — drives the page's GOLD chip. */
  gold: boolean;
  /** Their profile picture, when set. Public whatever the stats switch says — it is
   *  the face beside their name on every surface already. */
  avatarUrl?: string;
  joinedAt: number;
  /** Their published, non-empty collections. Never Editor's Choice (site-owned). */
  collections: PublicPlaylist[];
  /** The owner's switch. False means a visitor gets the refusal state, not a 404 —
   *  the lord exists, their page is simply shut. */
  isPublic: boolean;
  /** The same numbers `/profile` renders, present when `isPublic` — or when the
   *  caller IS the owner, whose own page is never withheld from them. */
  stats: GoonStats | null;
}
export interface PublicProfileResponse {
  profile: PublicProfile;
}
export interface PublicPlaylistsResponse {
  collections: PublicPlaylist[];
}
export interface PublicPlaylistDetail extends PublicPlaylist {
  items: MediaItem[];
}
export interface AdminCollectionsResponse {
  collections: Playlist[];
}
export interface CreateCollectionRequest {
  name: string;
  description?: string;
}
export interface AdminClipsResponse {
  items: MediaItem[];
}

// ---------------------------------------------------------------------------
// User-facing catalog browse + per-clip tag editing.
//
//   GET  /api/catalog?q=&tag=&race=&sex=&… -> CatalogBrowseResponse   (public)
//   GET  /api/clips/tags?clip=<clipKey>    -> ClipTagsResponse        (public)
//   POST /api/clips/tags  {clip,tags}      -> ClipTagsResponse        (auth)
//
// Tags are GLOBAL per clip (shared across users) — the same model the admin panel
// uses. Any signed-in user may edit them from the catalog/library.
// ---------------------------------------------------------------------------

/** Facet selections on a catalog query: at most one value per facet, and every
 *  selection NARROWS the result (they're ANDed). Values are BARE — `{race: "latina"}`
 *  means the keyword `race:latina`. */
export type CatalogFacets = Partial<Record<TagFacet, string>>;

/** Read facet selections out of a query string, keeping only declared values.
 *
 *  An unrecognised value is DROPPED, not rejected. These land in URLs that people
 *  bookmark and share, and a vocabulary can change under a saved link — widening the
 *  result set is a better failure than a 400 on a link that used to work. */
export function parseCatalogFacets(query: Record<string, unknown>): CatalogFacets {
  const out: CatalogFacets = {};
  for (const facet of TAG_FACET_NAMES) {
    const raw = query[facet];
    const value = typeof raw === "string" ? raw.trim().toLowerCase() : "";
    if (value && isFacetTag(`${facet}:${value}`)) out[facet] = value;
  }
  return out;
}

/** The keyword slugs a facet selection resolves to, in declaration order. */
export function facetTagsOf(facets: CatalogFacets): string[] {
  return TAG_FACET_NAMES.flatMap((f) => (facets[f] ? [`${f}:${facets[f]}`] : []));
}

/**
 * The order facet groups are presented in, and what each group is called.
 *
 * ONE list, shared by every surface that groups keywords — the browse sidebar and the
 * view editor's region picker. They used to keep private copies, and the copies were not
 * the same: the editor's held four facets to the sidebar's then-seven, so a facet added to the
 * catalog appeared on one surface and silently not the other. Adding `clip_length` had to
 * be done twice, which is the tell.
 *
 * Ordered what's IN the clip first (who is in it, then what it looks like), then how the
 * file was made. A viewer picking a wall reads down that list in roughly that priority.
 *
 * The LABEL is here rather than derived because these are group headings, not keywords:
 * "tits" heads a group called "Breasts", and `actor_count` heads "People". Individual
 * VALUES still resolve through tagDisplayName, so an admin rename still works.
 */
export const FACET_ORDER: readonly TagFacet[] = [
  // The performer, by name, first — before how many people, what race: the first thing a
  // viewer asks of a clip. Open, populated by the creator-subreddit rules.
  "pornstar",
  "actor_count",
  "race",
  "sex",
  "tits",
  // What the body is doing / how covered it is. Both come from vision heads that gate on
  // their own confidence, so these groups are sparser than race/sex — they sit after the
  // always-present facets rather than leading with holes.
  "nudity",
  "body_position",
  "focus",
  // The groups (2026-09-12): what is happening, then what she looks like / wears, then
  // where and how it was shot. Multi-valued, so their counts overlap.
  "act",
  "position",
  "hair",
  "body",
  "attire",
  "hairstyle",
  "eyes",
  "nails",
  "toy",
  "setting",
  "camera_angle",
  "aesthetic",
  "genre",
  "pairing",
  "cock",
  "orientation",
  "clip_length",
  // A property of the file, like the two below it — but asked for more often ("I want
  // sound on the wall" is a viewing decision, not an encoding one), so it leads them.
  "audio",
  "quality",
  "aspect",
];

const FACET_LABELS: Record<TagFacet, string> = {
  pornstar: "Pornstar",
  actor_count: "People",
  race: "Race",
  sex: "Sex",
  tits: "Breasts",
  nudity: "Nudity",
  body_position: "Body position",
  focus: "Focus",
  act: "Act",
  position: "Position",
  hair: "Hair",
  body: "Body",
  attire: "Attire",
  hairstyle: "Hair Style",
  eyes: "Eyes",
  nails: "Nails",
  aesthetic: "Aesthetic",
  genre: "Genre",
  pairing: "Pairing",
  cock: "Cock",
  toy: "Toys",
  setting: "Setting",
  camera_angle: "Camera",
  orientation: "Orientation",
  clip_length: "Clip Length",
  audio: "Audio",
  quality: "Quality",
  aspect: "Aspect",
};

/** A facet group's heading. Exhaustive over TagFacet, so declaring a new facet without
 *  naming it here fails the build rather than rendering a group headed `clip_length`. */
export function facetLabel(facet: TagFacet): string {
  return FACET_LABELS[facet];
}

/**
 * Explanatory text for a facet VALUE, or null when its name already says it.
 *
 * Only clip length needs one: its buckets are relative words ("Medium") that mean nothing
 * without the seconds behind them. Shared so the sidebar and the editor cannot describe
 * the same bucket differently.
 */
export function facetValueHint(facet: TagFacet, value: string): string | null {
  return facet === "clip_length" ? clipLengthRangeLabel(value) : null;
}

/** As facetValueHint, but for a full slug (`clip_length:short`) — what the editor holds. */
export function tagHint(tag: string): string | null {
  const facet = facetOf(tag);
  return facet ? facetValueHint(facet, stripTagNamespace(tag)) : null;
}

/**
 * The label for a keyword SLUG, facet-aware.
 *
 * `tagDisplayName` derives a name from the slug's own words, which is right for a keyword
 * and wrong for a facet value that needs its axis to make sense: `actor_count:2` derives
 * "2", and a chip reading "2" tells you nothing without the group heading above it. This
 * routes facet slugs through facetValueLabel and leaves everything else alone.
 *
 * Use this anywhere a slug is rendered outside its facet group — the region editor's
 * chips, a region's summary line — where the heading is not there to complete the phrase.
 */
export function tagLabel(tag: string): string {
  const facet = facetOf(tag);
  return facet ? facetValueLabel(facet, stripTagNamespace(tag)) : tagDisplayName(tag);
}

/** A facet value's label. Just the keyword's display name — TAG_NAMESPACE_SUFFIX
 *  already decides which values can't stand on their own ("Medium Tits") and which
 *  can ("White"), so a facet row needs no separate rule. */
export function facetValueLabel(facet: TagFacet, value: string): string {
  // actor_count is stored as a bare number, which reads as nothing on its own — a chip
  // saying "2" under a heading saying "People" makes you assemble the sentence yourself.
  // The top bucket ("4") means "4 or more" and says so. Display only; the stored tag
  // stays actor_count:4.
  if (facet === "actor_count") {
    return value === "1" ? "1 Person" : `${value === "4" ? "4+" : value} People`;
  }
  return tagDisplayName(`${facet}:${value}`);
}

/**
 * How many clips each facet value would yield — the numbers beside the sidebar rows.
 *
 * Counted per facet with that facet's OWN selection lifted, but every other filter
 * applied. Picking "Latina" shouldn't make the other races read 0; it should show what
 * you'd get by switching to them. Every other facet still narrows, so the numbers
 * answer "what happens if I click this" rather than "how big is this keyword".
 */
export interface CatalogFacetCountsResponse {
  counts: Record<string, Record<string, number>>;
}

/** The visibility-safe shared catalog (private uploads + banned clips excluded). */
export interface CatalogBrowseResponse {
  items: MediaItem[];
  /** Total number of clips matching the query (across all pages), for pagination. */
  total: number;
}
/** Sort order for the public catalog browse. */
export type CatalogSort = "latest" | "faps" | "edges" | "views";
/** The current global tag set for a clip, each carrying its display name and the
 *  source that applied it (so the UI can badge AI-assigned keywords). */
export interface ClipTagsResponse {
  tags: ClipTag[];
}
export interface SetClipTagsRequest {
  clip: MediaItem;
  /** Replaces the clip's global tag set (trimmed/deduped/capped server-side). */
  tags: string[];
}

/** A collection a clip belongs to, for the detail panel. */
export interface ClipDetailCollection {
  id: ID;
  name: string;
  /** Admin-curated Editor's Choice collection. */
  editorsChoice: boolean;
  /** Owned by the requesting user (their own collection). */
  owned: boolean;
  /** Publicly visible (linkable on /browse). Editor's Choice is always public. */
  isPublic: boolean;
}

/**
 * Aggregate stats for one clip, shown in the detail/preview panel.
 *   GET /api/clips/details?clip=<clipKey> -> ClipDetailsResponse   (public)
 */
export interface ClipDetails {
  /** Total faps ("crowns") across all users. */
  crowns: number;
  /** Lifetime views: one per PLAYTHROUGH, across every player surface (screen regions,
   *  Goon Fuel, the Browse clip player), session or no session. Reported by the client. */
  views: number;
  /**
   * Lifetime cold fetches of this clip's media from the server — the reconciliation
   * signal for `views`, and admin-only in the UI.
   *
   * `views` is reported by the browser on a route that asks nobody who they are, so it is
   * trust-the-client by construction. This one the client cannot touch: it is counted
   * where the bytes leave.
   *
   * IT IS NOT A BOUND IN EITHER DIRECTION, and reading it as one is the mistake to avoid.
   * Media is served `immutable`, so a looping clip replays from the browser cache and
   * fetches STAY PUT while views climb; and the player warms the next clip before showing
   * it, so a skipped warm is a fetch with no view. What it is good for is orders of
   * magnitude: 60 views against 40 fetches is a wall doing its job, 50,000 against 12 is a
   * client lying.
   */
  fetches: number;
  /** Lifetime edges: an edge in a goon session credits every clip on screen in
   *  the last ~5s. */
  edges: number;
  /** Public / Editor's Choice collections that contain this clip. */
  collections: ClipDetailCollection[];
  /** Uploader username for a user upload; null for scraped catalog clips. */
  uploader: string | null;
  /** The uploader holds GoonLords Gold right now — the GOLD chip beside their handle. */
  uploaderGold?: boolean;
  /*
   * `sourceKind` was here — the clip's origin platform, sent verbatim, and the panel printed
   * it as an "Origin: RedGifs" row.
   *
   * It is gone rather than mapped to the generic wire value, because nothing renders it any
   * more and a field nobody reads is a field that gets read again later by accident. This
   * response is the one the CLIP DETAILS panel asks for, so it was the second, quieter half
   * of the same leak as the id: `/api/faps?clip=redgifs:…` named the source in the request,
   * and this named it in the reply. Fixing only the first would have moved the leak, not
   * closed it. See server/src/clip-ids.ts.
   */
  /** When the clip entered the catalog (epoch ms); null if not persisted yet. */
  createdAt: number | null;
  width?: number;
  height?: number;
  durationSec?: number;
}

export interface ClipDetailsResponse {
  details: ClipDetails;
}

// ---------------------------------------------------------------------------
// User tag SUGGESTIONS + tag catalog.
//
// Signed-in users can only SUGGEST tags for a clip; a suggestion sits pending
// until an admin accepts it (which applies it to the clip's global tags) or
// rejects it. The public tag catalog exposes already-approved tags + usage
// counts for autocomplete.
//
//   POST /api/clips/tags/suggest {clip,tag}  -> { ok: true }        (auth)
//   GET  /api/catalog/tags?q=&limit=          -> CatalogTagsResponse  (public)
//   GET    /api/admin/tag-suggestions                 -> AdminTagSuggestionsResponse (auth)
//   POST   /api/admin/tag-suggestions/:id/accept      -> { ok: true } (auth)
//   POST   /api/admin/tag-suggestions/:id/reject      -> { ok: true } (auth)
// ---------------------------------------------------------------------------

/** A user's pending tag suggestion for a clip (awaiting admin review). */
export interface TagSuggestion {
  id: number;
  tag: string;
  clip: MediaItem;
  /** Username of the suggester, or null if system/absent. */
  suggestedBy: string | null;
  createdAt: number;
}
export interface AdminTagSuggestionsResponse {
  suggestions: TagSuggestion[];
}
/** Approved catalog tags with their usage counts (feeds tag autocomplete). The slug
 *  stays the identity — it's what /browse filters on — while `displayName` is what
 *  every surface renders. */
export interface CatalogTagsResponse {
  tags: { tag: string; count: number; displayName: string }[];
}
export interface SuggestTagRequest {
  clip: MediaItem;
  tag: string;
}

// Content-first curation: which Editor's Choice collections each clip belongs to.
// Collections act like keywords/labels applied across many clips.
//   POST /api/admin/clips/collections {clipIds} -> ClipCollectionsResponse (auth)
export interface ClipCollectionsRequest {
  /** clipKey()s to look up. */
  clipIds: string[];
}
export interface ClipCollectionsResponse {
  /** clipKey -> collection ids that contain it. */
  memberships: Record<string, string[]>;
}

// GET /api/admin/tags -> AdminTagsResponse (auth)
// All distinct freeform clip tags (for tag autocomplete), most-used first.
export interface AdminTagsResponse {
  tags: string[];
}

// A global tag with the number of clips carrying it (admin tag manager).
export interface AdminTagCount {
  tag: string;
  count: number;
  /** Whether this tag is hidden from public surfaces (browse sidebar + clip tag lists).
   *  Admin surfaces always show hidden tags so they stay manageable. */
  hidden: boolean;
  /** The admin-set display name, or null when the derived fallback is in use.
   *  Null vs "" matters here: the manager shows the placeholder for null. */
  displayName: string | null;
  /** What the UI would render right now (override, else derived) — saves every
   *  admin surface from re-deriving it. */
  resolvedName: string;
}

// GET  /api/admin/tag-counts         -> AdminTagCountsResponse (auth)
// POST /api/admin/tags/delete { tag } -> AdminTagCountsResponse (auth) — remove a tag everywhere
// POST /api/admin/tags/merge  { from, into } -> AdminTagCountsResponse (auth) — fold `from` into `into`
// POST /api/admin/tags/visibility { tag, hidden } -> AdminTagCountsResponse (auth) — hide/reveal a tag
// POST /api/admin/tags/display-name { tag, displayName } -> AdminTagCountsResponse (auth)
//      — set (or clear, with null/"") a keyword's display-name override
export interface AdminTagCountsResponse {
  tags: AdminTagCount[];
}

// ---------------------------------------------------------------------------
// Mislabelled-keyword reports. A signed-in user who sees an AI-assigned keyword
// that looks wrong reports it from the keyword bubble; an admin then resolves it.
//
//   POST /api/clips/tags/report  ReportTagRequest -> {ok:true}          (auth)
//   GET  /api/admin/tag-reports                   -> AdminTagReportsResponse (admin)
//   POST /api/admin/tag-reports/resolve ResolveTagReportRequest
//                                                 -> AdminTagReportsResponse (admin)
//
// Resolving is one of:
//   keep    the model was right — promote the association ai -> admin. The badge
//           goes away and it can't be reported again.
//   remove  the model was wrong — delete the clip↔keyword association.
// Either way every open report for that clip+keyword closes.
// ---------------------------------------------------------------------------

export const TAG_REPORT_RESOLUTIONS = ["keep", "remove"] as const;
export type TagReportResolution = (typeof TAG_REPORT_RESOLUTIONS)[number];

export interface ReportTagRequest {
  clip: MediaItem;
  tag: string;
  /** Optional free-text note (what the user thinks it should be). Capped server-side. */
  note?: string;
}

/** One reported clip↔keyword pair, with everything the review screen renders. */
export interface AdminTagReport {
  clipId: ID;
  tag: string;
  displayName: string;
  /** How many distinct reports are open against this pair. */
  reports: number;
  /** Newest report first — the notes users left, empty ones dropped. */
  notes: string[];
  /** Epoch ms of the most recent report. */
  reportedAt: number;
  /** The clip itself, so the reviewer can watch it before deciding. Null if the
   *  clip has since been deleted — the row is still shown so it can be cleared. */
  clip: MediaItem | null;
  /** The association's current source. Always "ai" for an open report, but carried
   *  so the UI can detect one already resolved in another tab. */
  source: TagSource;
}

export interface AdminTagReportsResponse {
  reports: AdminTagReport[];
}

export interface ResolveTagReportRequest {
  clipId: ID;
  tag: string;
  resolution: TagReportResolution;
}

// Per-clip admin metadata: freeform tags + banned state.
//   GET  /api/admin/clips/meta?clipId=...          -> ClipMetaResponse   (auth)
//   POST /api/admin/clips/meta SetClipMetaRequest  -> ClipMetaResponse   (auth)
export interface ClipMetaResponse {
  tags: string[];
  /** Banned = excluded from every source/feed and dropped from all collections. */
  banned: boolean;
}
export interface SetClipMetaRequest {
  /** The clip to attach metadata to (upserted into the catalog if new). */
  clip: MediaItem;
  /** Omit to leave unchanged; an array replaces the tag set. */
  tags?: string[];
  /** Omit to leave unchanged; true bans the clip platform-wide, false unbans. */
  banned?: boolean;
}

// ---------------------------------------------------------------------------
// Gooning stats — watch events aggregated into lifetime totals + an activity
// time-series. Reported per signed-in user.
//
//   POST /api/stats/watch {clip, seconds}  -> { ok: true }   (auth)
//   GET  /api/stats                         -> GoonStats      (auth)
// ---------------------------------------------------------------------------

/** One day's worth of watch activity (UTC date). */
export interface StatsActivityPoint {
  date: string; // YYYY-MM-DD
  seconds: number;
  clips: number;
}

/** One "you beat X% of gooners" ranking for a single metric. */
export interface Percentile {
  /** Percentage of active users this user ranks strictly above (0–100). */
  pct: number;
  /** This user's raw value for the metric. */
  value: number;
  /** 1-based rank among active users (1 = top). */
  rank: number;
}

/** Gamified "addiction level" — the user's standing vs. everyone else. */
export interface AddictionStats {
  /** Composite 0–100 score (a weighted blend of the percentiles below). */
  score: number;
  /** Tier name for the score (e.g. "Goon Addict"). */
  level: string;
  /** 0-based tier index and total tier count (for a progress track). */
  levelIndex: number;
  levelCount: number;
  /** Overall rank + active-user population. */
  rank: number;
  totalUsers: number;
  /** Per-metric "goon more than X% of users" rankings. */
  time: Percentile;
  clips: Percentile;
  faps: Percentile;
  taps: Percentile;
  edges: Percentile;
  /** Current Faps balance, ranked against every other account — "wealth," not activity. */
  wealth: Percentile;
}

/** Per-user goon-session stats, derived by grouping watch events into sittings. */
export interface SessionStats {
  /** Number of distinct sessions (watch bursts). */
  count: number;
  /** Longest / shortest / average session length, in seconds. */
  longestSec: number;
  shortestSec: number;
  avgSec: number;
}

/** A clip surfaced on the profile, tagged with the metric that earned its spot. */
export interface StatHighlightClip {
  clip: MediaItem;
  /** Total watch seconds (most-watched). */
  seconds?: number;
  /** Number of watch events (most-watched). */
  plays?: number;
  /** Fap tap count (most-fapped). */
  clicks?: number;
  /** Edge count (most-edged). */
  edges?: number;
  /** Times the loop button was pressed on this clip (most-looped). */
  loops?: number;
}

export interface GoonStats {
  /** Total seconds spent watching ("time gooning"). */
  totalSeconds: number;
  /** Number of clips watched (watch events). */
  clipsWatched: number;
  /** Number of clips the user has fapped (liked). */
  faps: number;
  /** Total fap taps across all clips (the spammable tap tally). */
  totalTaps: number;
  /** Total edges the user has logged (across all their sessions). */
  edges: number;
  /**
   * Times this user pressed the loop button to pin a clip on a screen.
   *
   * Counts the press, not the replays it causes: the button is a toggle, so only turning
   * it ON is an act. A loop pressed on a signed-out screen is not counted at all — the
   * same rule the fap crown follows, for the same reason (there is no account to credit).
   */
  loops: number;
  keptCount: number;
  rejectedCount: number;
  /** Number of collections the user owns. */
  collectionsCount: number;
  favoriteCategory: string | null;
  categoryBreakdown: Array<{ category: string; seconds: number }>;
  /** The clip the user has spent the most time watching (null if no watch data). */
  mostWatched: StatHighlightClip | null;
  /** The clip the user has fapped the most (by tap count; null if none). */
  mostFapped: StatHighlightClip | null;
  /** The clip the user has edged to the most (null if none logged yet). */
  mostEdged: StatHighlightClip | null;
  /** The clip the user has pinned on a screen the most (null if none logged yet). */
  mostLooped: StatHighlightClip | null;
  /** The user's collection with the most watch-time invested (fallback: most clips). */
  favoriteCollection: { id: string; name: string; clipCount: number } | null;
  /** The user's top tags, by time spent on clips carrying them. */
  topKeywords: Array<{ tag: string; count: number }>;
  /** Gamified standing vs. the rest of the platform. */
  addiction: AddictionStats;
  /** Goon-session stats (longest / shortest / average sitting). */
  sessions: SessionStats;
  /** Longest run of consecutive active days, and the current ongoing streak. */
  longestStreak: number;
  currentStreak: number;
  /** Most watch-seconds in a single day. */
  bestDaySeconds: number;
  /** Recent days (oldest → newest) for the activity graph. */
  activity: StatsActivityPoint[];
}

// ---------------------------------------------------------------------------
// Landing stats — the four vanity figures on `/`.
//
//   GET /api/stats/landing -> LandingStats   (public, cached)
//
// Each figure is a BASELINE plus a real measurement. The baselines are a head start on
// numbers that are honestly small early on; the measurement is what makes them move.
//
// They live here, in the shared contract, for one reason: the server adds them to its
// aggregate and the client falls back to them when the aggregate cannot be fetched. Two
// copies of "4300" would drift, and the drift would show as the front page quietly
// reporting a different figure on a bad day than on a good one.
//
// Applied SERVER-side. If the client added them, the real measurement would be on the
// wire, and anyone could recover it by subtracting a published constant.
// ---------------------------------------------------------------------------

/** The head start each landing figure carries, before anything real is counted. */
export const LANDING_BASELINE = {
  /** Edge presses. */
  edges: 4_300,
  /** Distinct identities that have ever used the app. */
  gooners: 1_500,
  /** Goon sessions carried to a finish. */
  gallons: 2_100,
  /** Hours of playback. */
  hours: 53_000,
} as const;

/** The four figures the landing's stats band renders, baselines already included. */
export interface LandingStats {
  /** Edge presses recorded across every session. */
  edges: number;
  /** Distinct identities — accounts plus signed-out browsers that did something. */
  gooners: number;
  /** Goon sessions that reached an end. */
  gallons: number;
  /** Hours of playback: clip views multiplied by each clip's length. */
  hours: number;
}

export interface WatchRequest {
  clip: MediaItem;
  seconds: number;
}

// ---------------------------------------------------------------------------
// The shop (2026-11-02). Physical goods, shipped within the US by hand; the catalog is a
// JSON file on the server (server/src/shop-config.ts), so these are wire shapes only.
// ---------------------------------------------------------------------------

export interface ShopProductSummary {
  slug: string;
  name: string;
  tagline: string;
  priceCents: number;
  /** Served behind the age cookie, unsigned: /api/shop/images/<file>. */
  images: string[];
  soldOut: boolean;
  /** A ShopCategory slug, or "" when the product has none. */
  category: string;
  /** What the same things cost bought separately, for a "Save $X" badge; 0 for none. */
  compareAtCents: number;
}

export interface ShopCategory {
  slug: string;
  name: string;
  /** One line under the section heading. May be empty. */
  blurb: string;
}

export interface ShopProduct extends ShopProductSummary {
  description: string;
  /** Owner's note about paying, from data/shop.json. Empty when there is none. */
  cartNotice: string;
  /** The smallest cart a card can pay for; 0 when there is no such limit. */
  cardMinimumCents: number;
}

/** `open: false` is how the menu knows to hide the row; it is not an error. */
export interface ShopCatalogResponse {
  open: boolean;
  /** Sections for the landing page, in order. Empty means one plain grid. */
  categories: ShopCategory[];
  /** The smallest cart a card can pay for; 0 when there is no such limit. */
  cardMinimumCents: number;
  products: ShopProductSummary[];
}

export interface ShopShipTo {
  name: string;
  line1: string;
  line2: string;
  city: string;
  /** Two-letter USPS code. */
  state: string;
  zip: string;
}

export type ShopOrderStatus = "paid" | "shipped" | "refunded";

/** One line of an order, priced when it was bought: the catalog can change afterwards. */
export interface ShopOrderItem {
  slug: string;
  name: string;
  priceCents: number;
  quantity: number;
}

export interface ShopOrder {
  id: string;
  /** The item's slug, or "cart" when the order has more than one line. */
  productSlug: string;
  /** Every item in one line of text, for lists and alerts. `items` is the real record. */
  productName: string;
  /** What was paid for the whole order. */
  priceCents: number;
  items: ShopOrderItem[];
  status: ShopOrderStatus;
  tracking: string | null;
  createdAt: number;
  shippedAt: number | null;
  refundedAt: number | null;
}

export interface ShopOrderDetail extends ShopOrder {
  /** Null once deleted, 30 days after shipping or refund. */
  shipTo: ShopShipTo | null;
}

export interface ShopCartLine {
  slug: string;
  quantity: number;
}

export const SHOP_CART_MAX_QUANTITY = 20;
export const SHOP_CART_MAX_LINES = 50;

export interface ShopCheckoutRequest {
  /** What to buy. Only slugs and quantities: the server reads every price from the catalog. */
  items?: ShopCartLine[];
  /** One unit of one product: what a tab loaded before the cart shipped still sends. */
  slug?: string;
  /** A signed-out browser's anonymous analytics id, so its paid order joins its funnel. */
  analyticsId?: string;
  email: string;
  shipTo: ShopShipTo;
}

export interface ShopCheckoutResponse {
  checkoutUrl: string;
  transactionId: string;
  /** The only way back to this order without an account. Handed over once: the server keeps
   *  a hash of it and could not show it again. */
  orderKey: string;
}

/** Sent with order reads: how a buyer without an account proves the order is theirs. */
export const SHOP_ORDER_KEY_HEADER = "x-order-key";

/** POST /api/shop/orders/find — a guest's way back with what they have in hand. */
export interface ShopFindOrderRequest {
  order: string;
  email: string;
}

/** The owner's wording, shown on every order still waiting to ship and before paying. */
export const SHOP_SHIPPING_NOTICE = "Made to order, ships in 2-3 weeks.";

/** What the parcel looks like. Only what is true: nothing here about the card statement. */
export const SHOP_DISCREET_NOTICE = "Ships in plain packaging, with no GoonLords branding on the outside.";

/** USPS codes: the states, DC, the territories and the military mail regions. */
export const US_STATE_CODES: readonly string[] = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL", "GA", "HI", "ID", "IL", "IN",
  "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH",
  "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT",
  "VT", "VA", "WA", "WV", "WI", "WY", "AS", "GU", "MP", "PR", "VI", "AA", "AE", "AP",
];

/**
 * A US address, or the sentence saying what is wrong with it. Shared so the form and the
 * server cannot disagree. REFUSES what is too long rather than trimming it to fit: a
 * silently shortened address is a parcel that goes missing.
 */
export function parseShopShipTo(
  input: unknown,
): { ok: true; shipTo: ShopShipTo } | { ok: false; error: string } {
  const o = (input ?? {}) as Record<string, unknown>;
  const field = (k: string) => (typeof o[k] === "string" ? (o[k] as string).trim() : "");
  const shipTo: ShopShipTo = {
    name: field("name"),
    line1: field("line1"),
    line2: field("line2"),
    city: field("city"),
    state: field("state").toUpperCase(),
    zip: field("zip"),
  };
  if (!shipTo.name) return { ok: false, error: "Enter the name for the parcel" };
  if (!shipTo.line1) return { ok: false, error: "Enter a street address" };
  if (!shipTo.city) return { ok: false, error: "Enter a city" };
  if (shipTo.name.length > 120 || shipTo.city.length > 100) {
    return { ok: false, error: "That name or city is too long" };
  }
  if (shipTo.line1.length > 200 || shipTo.line2.length > 200) {
    return { ok: false, error: "That street address is too long" };
  }
  if (!US_STATE_CODES.includes(shipTo.state)) return { ok: false, error: "Choose a US state" };
  if (!/^\d{5}(?:-\d{4})?$/.test(shipTo.zip)) {
    return { ok: false, error: "Enter a ZIP code like 12345 or 12345-6789" };
  }
  return { ok: true, shipTo };
}
